/**
 * Small reusable UI components: Pill, GreenBtn, Card, GoogleIcon, ScrollLock, OverflowMenu,
 * ViewportLayer, ToastProvider (useToast), ConfirmModal, and the modal shell
 * trio ModalBackdrop / ModalHeader / ModalScrollBody + ModalActionBar.
 */
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useContext, createContext, useId, useMemo, useSyncExternalStore, startTransition } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router";
import { ORGANIZER_CONTACT_LIMITS } from "@sendly/shared";
import { F, DEFAULT_ACCENT } from "../constants";
import { groupTone, blendHex, getTheme } from "../utils";
import { useAppTheme } from "../contexts/ThemeContext";
import { useEscapeKey, EscapeDepthContext } from "../hooks/useEscapeKey";
import { useExpanderState } from "../hooks/useExpanderState";
import { ScrollFadeOverlay, useScrollFadeBottom } from "./ScrollFade";
import { statusBarTintStyle, isAppleTouchDevice, statusBarInset } from "../utils/iosStatusBar";

/* ==========================================================================
 * Open-modal registry — who is dimming the screen right now, and by how much.
 *
 * Only `StatusBarTintBar` reads it: the bar has to know the dim of the top-most
 * backdrop so it can paint the colour that dim makes of the header. A counter
 * would not do — `dim` is a prop, and a picker sheet uses a lighter one than a
 * dialog — so the stack keeps the values and the last one wins.
 *
 * Registered by EVERY backdrop, `lockScroll={false}` ones included: what
 * matters here is the dimming, not the scroll lock.
 * ========================================================================= */
let _modalDims = [];
const _modalDimListeners = new Set();
const _subscribeModalDim = (fn) => { _modalDimListeners.add(fn); return () => _modalDimListeners.delete(fn); };
const _topModalDim = () => (_modalDims.length ? _modalDims[_modalDims.length - 1] : 0);
function _pushModalDim(dim) {
  _modalDims = [..._modalDims, dim];
  _modalDimListeners.forEach((fn) => fn());
}
function _popModalDim(dim) {
  const i = _modalDims.lastIndexOf(dim);
  if (i >= 0) _modalDims = [..._modalDims.slice(0, i), ..._modalDims.slice(i + 1)];
  _modalDimListeners.forEach((fn) => fn());
}

/**
 * StatusBarTintBar — owns the colour iOS paints behind the status bar, and the
 * colour Safari tints its chrome with. Renders nothing off iOS/iPadOS.
 *
 * Mounted by the app shells that have a header, NOT globally: a TV dashboard, a
 * competition screen or the embedded widget has its own look at the top edge,
 * and a strip of header colour there would be a visible bar rather than an
 * invisible one. See `statusBarTintStyle` for what the bar has to be, and for
 * the three measurements behind it.
 *
 * `color` is whatever the mounting surface actually paints behind the bar, and
 * it is not cosmetic: the gym selector's top strip is `th.bg`, not `th.headerBg`,
 * and while 9/255 apart sounds invisible, on the dark theme's near-black those
 * two read as two different colours. Default is `th.headerBg` — the gym page
 * and the profile both have a header there.
 *
 * @param {string} [props.color] - the colour behind the bar on this surface
 */
export function StatusBarTintBar({ color }) {
  const { th } = useAppTheme();
  const dim = useSyncExternalStore(_subscribeModalDim, _topModalDim, _topModalDim);
  const style = statusBarTintStyle(color || th.headerBg, dim, isAppleTouchDevice());
  if (!style) return null;
  // The `key` is the mechanism, not bookkeeping: iOS re-reads the strip's
  // colour when a qualifying element is INSERTED, and ignores one that merely
  // changes its background — which is exactly why the dim used to stick after a
  // modal closed (an element left, nothing arrived). Keying makes React throw
  // the old node away and mount a fresh one, so each change is an insertion.
  //
  // Keyed on the COLOUR rather than on the dim, because the dim is not the only
  // thing that moves it: switching the theme in the profile repainted the bar
  // from white to near-black and the strip stayed white until the next
  // navigation.
  return ReactDOM.createPortal(
    <div key={`tint-${style.background}`} aria-hidden="true" style={style} />,
    document.body,
  );
}

/** Prevents body scroll while mounted. Handles nested modals via counter.
 *
 *  Implementation note: just setting `body.overflow = "hidden"` was
 *  causing iOS Safari to scroll the body to the top the first time a
 *  modal opened on a page (subsequent opens were fine — the bug only
 *  fires when the body overflow transitions from a value that allows
 *  scroll into a value that doesn't, *and* iOS Safari's URL bar happens
 *  to be in a transitional state). The robust pattern is to also pin
 *  the body's scroll position with `position: fixed; top: -scrollY`,
 *  then restore it on unlock. This keeps the visual scroll position
 *  identical across the lock boundary on every browser.
 */
let _lockCount = 0;
let _saved = { overflow: "", position: "", top: "", left: "", right: "", width: "", scrollY: 0 };
export function ScrollLock() {
  // useLayoutEffect (not useEffect) runs synchronously after DOM
  // mutations but BEFORE the browser paints. That matters here: a
  // typical modal autofocuses an input the moment it mounts, and
  // browsers scroll the body to bring focus into view. With useEffect
  // the lock is applied AFTER paint, so the focus-induced scroll has
  // already moved the page. useLayoutEffect locks first, then focus
  // can't shift body scroll because body is `position: fixed`.
  useLayoutEffect(() => {
    if (_lockCount === 0) {
      _saved.overflow = document.body.style.overflow;
      _saved.position = document.body.style.position;
      _saved.top = document.body.style.top;
      _saved.left = document.body.style.left;
      _saved.right = document.body.style.right;
      _saved.width = document.body.style.width;
      _saved.scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${_saved.scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    }
    _lockCount++;
    return () => {
      _lockCount--;
      if (_lockCount === 0) {
        document.body.style.overflow = _saved.overflow;
        document.body.style.position = _saved.position;
        document.body.style.top = _saved.top;
        document.body.style.left = _saved.left;
        document.body.style.right = _saved.right;
        document.body.style.width = _saved.width;
        // Restore scroll without smooth-scroll easing, otherwise the
        // page visibly slides back into place after the modal closes.
        window.scrollTo(0, _saved.scrollY);
      }
    };
  }, []);
  return null;
}
import { makeB, sortedEntries, initials, confirmWordMatches } from "../utils";
import { tDiffLabel, tHoldLabel, useTranslation } from "../i18n";
import FeeLadder from "./FeeLadder";
import { callableErrorText } from "../utils/callableError";

/**
 * ColumnChart — small bucket-bar chart used by the admin and
 * platform-admin stats pages. Each bucket is a column with the count
 * above it (only when > 0) and a label below. Bars normalise to the
 * tallest bucket. Empty buckets render as a thin neutral line so the
 * shape of the time axis is still visible.
 *
 * Passing `negativeData` switches it to the **diverging** layout shared by
 * the monthly AND daily member/user-change charts: positive bars above a
 * centred baseline, negative below, each column carrying its own +N / −N
 * count and a label, plus an optional legend. The monthly and daily charts
 * are the SAME control — only `data` (labels + counts) and the legend
 * strings differ.
 *
 * Props:
 *   data           — [{ label: string, count: number, title?: string }]
 *                    positive series. In diverging mode `title` overrides the
 *                    per-column hover text (e.g. full date vs. day number).
 *   color          — positive-bar fill (typically the gym's accent)
 *   negativeData   — optional [{ count }] aligned 1:1 with `data` by index.
 *                    Present (any array) → diverging dual mode. Both series
 *                    share one max so a +5 and a −5 render equal-length, but
 *                    each side reserves only as much vertical room as its own
 *                    tallest bar needs (no big gap when one series ≫ other).
 *   negativeColor  — bar fill for the negative series (default #dc2626)
 *   upLabel        — optional legend label for the positive series
 *   downLabel      — optional legend label for the negative series
 *   theme          — getTheme() output (uses textMuted, borderLight)
 *   height         — total chart height in px (default 80; min 150 in
 *                    diverging mode)
 *   labelSize      — x-axis label fontSize in px (default 8; min 11 diverging)
 */
export function ColumnChart({
  data, color, negativeData, negativeColor = "#dc2626",
  upLabel, downLabel, height = 80, labelSize = 8,
  anchorLabelEnd = false, minLabelPx = 16,
}) {
  const { th: theme } = useAppTheme();
  // `negativeData` present (any array) → diverging dual mode; otherwise the
  // original single-series bars. The SAME diverging control powers both the
  // monthly AND the daily member/user-change charts — only `data` (labels +
  // counts) and the legend strings differ between callers.
  const hasNegative = Array.isArray(negativeData);

  // Responsive tick-label thinning (opt-in via `anchorLabelEnd`). When the
  // columns get too narrow for every label to fit (e.g. 30 day-numbers on a
  // phone), show only every Nth label while ALWAYS keeping the rightmost
  // column's label visible — the "today" bucket. The thinning therefore
  // counts BACKWARDS from the last column (today → −step → −2·step → …), so
  // the most recent day is the fixed anchor. ALL bars still render; only the
  // tick labels are dropped. `labelStep` is derived from the measured row
  // width vs. `minLabelPx` (a label's footprint incl. breathing room) and
  // escalates 1 → 2 → 3 → … as space tightens.
  const labelRowRef = useRef(null);
  const [labelStep, setLabelStep] = useState(1);
  useLayoutEffect(() => {
    if (!anchorLabelEnd) { setLabelStep(1); return; }
    const el = labelRowRef.current;
    const n = data.length;
    if (!el || n <= 1) { setLabelStep(1); return; }
    const measure = () => {
      const slot = el.clientWidth / n;
      setLabelStep(slot >= minLabelPx ? 1 : Math.max(2, Math.ceil(minLabelPx / Math.max(slot, 1))));
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => { if (ro) ro.disconnect(); };
  }, [anchorLabelEnd, minLabelPx, data.length]);
  // i is hidden only when thinning is active AND it isn't on the
  // last-anchored stride. Last column (i === data.length-1) is always shown.
  const showLabelAt = (i) =>
    !anchorLabelEnd || labelStep <= 1 || ((data.length - 1 - i) % labelStep === 0);

  if (!hasNegative) {
    // ── Single-series mode (original behaviour) ────────────────────────
    const max = Math.max(1, ...data.map(m => m.count || 0));
    // Column layout = [count text][gap][bar][gap][label]. The column is
    // a flex-column nested inside an `alignItems: "flex-end"` row, so
    // when the column is taller than `height` the overflow goes UPWARD
    // — directly into whatever section title sits above the chart.
    // We budget for the worst case (label wraps to 2 lines on a narrow
    // mobile column like "máj. '26") so the tallest bar stays inside.
    //   countRoom = count text minHeight + bottom gap = 13
    //   labelRoom = labelSize * 2 (2-line wrap) + 4 (gap+line spacing)
    const COUNT_ROOM = 13;
    const labelRoom = labelSize * 2 + 4;
    const barRoom = Math.max(20, height - COUNT_ROOM - labelRoom);
    // labelArea pins the label slot to the 2-line worst case so 1-line
    // labels ("máj. '26") and 2-line labels ("szept. '26") reserve the
    // same vertical space. Without this, the per-column intrinsic height
    // differs by ~10 px and the parent's `align-items: flex-end` (which
    // aligns column BOTTOMS) drags the bar baseline up by that same
    // amount on short-label columns — so the chart's 0-line stops being
    // a single horizontal line. lineHeight 1.1 + 2 px slack to absorb
    // font-metric rounding across browsers.
    const labelArea = Math.ceil(labelSize * 2 * 1.1) + 2;
    return (
      // `minWidth: 0` keeps the chart from overflowing on narrow viewports
      // (iPhone 13 mini: ~345 px → ~29 px per column).
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height, minWidth: 0 }}>
        {data.map((m, i) => {
          const count = m.count || 0;
          const h = Math.max(2, (count / max) * barRoom);
          return (
            <div key={i} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color, minHeight: 11, lineHeight: 1 }}>
                {count > 0 ? count : ""}
              </span>
              <div style={{
                width: "100%", height: h, borderRadius: 4,
                background: count > 0 ? color : theme.borderLight,
                transition: "height 0.3s ease",
              }} />
              <span style={{
                fontSize: labelSize, color: theme.textMuted, textAlign: "center",
                lineHeight: 1.1, maxWidth: "100%",
                display: "block", minHeight: labelArea,
              }}>{m.label}</span>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Diverging dual mode (positive up, negative down) ────────────────
  // Each column: +N count hugging the bar end above the baseline, −N below.
  // Uniform 11 px counts + labels (deliberately read, not glanced at; the
  // same sizing whether the axis is 12 months or 30 days). A per-data
  // `title` overrides the hover text (the daily chart passes the full date
  // while the visible label is just the day number).
  const countSize = 11;
  const countMinH = 14;
  const dualLabelSize = Math.max(labelSize, 11);
  const dualHeight = Math.max(height, 150);
  // Reserve 2 label lines so 1-line ("7") and 2-line ("szept. '26") labels
  // keep the baseline flat across columns. LABEL_GAP is a little breathing
  // room between the bars/counts and the labels.
  const labelArea = Math.ceil(dualLabelSize * 2 * 1.1) + 2;
  const LABEL_GAP = 6;
  // Up and down bars share ONE scale (global max across both series) so a +5
  // and a −5 render equal-length, BUT each side's reserved vertical room is
  // sized to that side's OWN tallest bar — so when one series ≫ the other
  // (e.g. joins ≫ leaves) the short bars don't hang above a big empty gap
  // before the labels. `fullHalf` is the budget a side gets when it owns the
  // global max.
  const maxUp = Math.max(0, ...data.map(m => m.count || 0));
  const maxDown = Math.max(0, ...negativeData.map(m => m?.count || 0));
  const max = Math.max(1, maxUp, maxDown);
  const fullHalf = Math.max(18, (dualHeight - countMinH * 2 - 1 - labelArea - LABEL_GAP) / 2);
  // A non-zero value ALWAYS renders a visible bar: clamp to MIN_BAR px so a
  // tiny count beside a much larger one (e.g. −2 next to +71) can't round
  // away to nothing. Corner radius drops to 1 px on short bars so the 3 px
  // rounding doesn't visually swallow the whole sliver.
  const MIN_BAR = 3;
  const barRadius = (h) => (h >= 6 ? 3 : 1);
  const upRoom = maxUp > 0 ? Math.max(MIN_BAR, (maxUp / max) * fullHalf) : 0;
  const downRoom = maxDown > 0 ? Math.max(MIN_BAR, (maxDown / max) * fullHalf) : 0;

  return (
    <div>
      <div ref={labelRowRef} style={{ display: "flex", gap: 2, minWidth: 0 }}>
        {data.map((m, i) => {
          const upCount = m.count || 0;
          const downCount = negativeData[i]?.count || 0;
          const upH = upCount > 0 ? Math.max(MIN_BAR, (upCount / max) * fullHalf) : 0;
          const downH = downCount > 0 ? Math.max(MIN_BAR, (downCount / max) * fullHalf) : 0;
          return (
            <div key={i} title={`${m.title ?? m.label}: +${upCount} / −${downCount}`} style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "center" }}>
              {/* Up half — bar pinned to the baseline (bottom), +N count
                  hugging directly above the bar end. */}
              <div style={{ height: upRoom + countMinH, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
                <span style={{ fontSize: countSize, fontWeight: 700, color, minHeight: countMinH, lineHeight: 1 }}>
                  {upCount > 0 ? `+${upCount}` : ""}
                </span>
                <div style={{ width: "100%", height: upH, borderRadius: `${barRadius(upH)}px ${barRadius(upH)}px 0 0`, background: upCount > 0 ? color : "transparent", transition: "height 0.3s ease" }} />
              </div>
              {/* Baseline — always visible so the time axis is legible */}
              <div style={{ width: "100%", height: 1, background: theme.borderLight }} />
              {/* Down half — bar hangs from the baseline (top), −N count
                  hugging directly below the bar end. */}
              <div style={{ height: downRoom + countMinH, width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start", gap: 2 }}>
                <div style={{ width: "100%", height: downH, borderRadius: `0 0 ${barRadius(downH)}px ${barRadius(downH)}px`, background: downCount > 0 ? negativeColor : "transparent", transition: "height 0.3s ease" }} />
                <span style={{ fontSize: countSize, fontWeight: 700, color: negativeColor, minHeight: countMinH, lineHeight: 1 }}>
                  {downCount > 0 ? `−${downCount}` : ""}
                </span>
              </div>
              <span style={{ fontSize: dualLabelSize, color: theme.textMuted, textAlign: "center", lineHeight: 1.1, maxWidth: "100%", display: "block", minHeight: labelArea, marginTop: LABEL_GAP }}>{showLabelAt(i) ? m.label : ""}</span>
            </div>
          );
        })}
      </div>
      {(upLabel || downLabel) && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
          {upLabel && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: 4, background: color }} />
              <span style={{ fontSize: 12, color: theme.textMuted }}>{upLabel}</span>
            </div>
          )}
          {downLabel && (
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 10, height: 10, borderRadius: 4, background: negativeColor }} />
              <span style={{ fontSize: 12, color: theme.textMuted }}>{downLabel}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Toggleable pill button – simple toggle style (accent when active, neutral when inactive). */
export function Pill({ active, onClick, children, role, "aria-selected": ariaSelected, "aria-pressed": ariaPressed, title, ariaLabel, dashed = false }) {
  const { accent, th: theme } = useAppTheme();
  const acc = accent || DEFAULT_ACCENT;
  const bg = active ? acc : (theme?.card || "#fff");
  const fg = active ? "#fff" : (theme?.text || "#333");
  // `dashed` marks a value (the spray consensus) without selecting it, so it
  // only shows while the pill is inactive — the active fill says more.
  const markOnly = dashed && !active;
  // A toggle pill says whether it is on: `aria-pressed` follows `active` unless
  // the caller states it, or gave the pill another role (a `tab` carries
  // `aria-selected`, and `aria-pressed` on a tab is wrong). A pill with no
  // `active` is an action chip, not a toggle, so it gets none.
  const pressedState = ariaPressed !== undefined ? ariaPressed
    : (role === undefined && typeof active === "boolean" ? active : undefined);
  const bd = active || markOnly ? acc : (theme?.border || "#e0e0e0");
  return (
    <button type="button" onClick={onClick} role={role} aria-selected={ariaSelected} aria-pressed={pressedState}
      title={title} aria-label={ariaLabel} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      // Bumped vertical padding 6→7 — small nudge that gets us to ~33px tap
      // target without making pills look chunky on desktop.
      padding: "7px 14px", borderRadius: 999, fontSize: 13,
      fontWeight: active ? 800 : 600,
      border: "1.5px " + (markOnly ? "dashed" : "solid") + " " + bd,
      background: bg, color: fg, cursor: "pointer", transition: "all 0.15s",
      // Active state gets a subtle accent-tinted shadow so it lifts off the
      // page even on dark backgrounds where the accent might otherwise sit
      // close in value to the theme card color.
      boxShadow: active ? `0 2px 8px ${acc}66` : "none",
      fontFamily: F,
    }}>{children}</button>
  );
}

/**
 * A price with a line discount: the struck-through list price (muted), then
 * the price actually charged. Used wherever an add-on carries its own
 * discount (platform gym editor + gym subscription breakdowns, the add-ons
 * card). Renders inline spans only, so the caller keeps its own row layout.
 * `format` turns a number into text (the caller owns the currency); `suffix`
 * is appended to the charged price (e.g. "/ quarter").
 */
export function DiscountedPrice({ listPrice, price, format, suffix }) {
  const { th: theme } = useAppTheme();
  return (
    <>
      <span style={{ fontWeight: 500, color: theme.textMuted, textDecoration: "line-through", marginRight: 6 }}>
        {format(listPrice)}
      </span>
      <span>{suffix ? `${format(price)} ${suffix}` : format(price)}</span>
    </>
  );
}

/** The green "−50%" mark next to a discounted add-on's name or price. */
export function DiscountTag({ children }) {
  return <span style={{ color: "#16a34a", marginLeft: 6 }}>{children}</span>;
}

/**
 * Lifecycle status of a competition, an event or a series.
 *
 * Three admin sections wrote this badge byte for byte and differed only in
 * their colour maps, which between them describe **three tones, not five
 * statuses**: not started yet is grey, running is green, over is red. Events
 * add `published` and `cancelled`, which are the same two ideas under other
 * names, so they share the tones rather than getting their own.
 *
 * The label is passed in already translated: the i18n prefix differs per
 * section (`comp.status.` / `events.status.` / `series.status.`), and keeping
 * the key at the call site is what lets a grep find it.
 */
const STATUS_TONES = {
  draft: { bg: "#e5e7eb", fg: "#6b7280" },
  active: { bg: "#d1fae5", fg: "#059669" },
  published: { bg: "#d1fae5", fg: "#059669" },
  closed: { bg: "#fee2e2", fg: "#dc2626" },
  cancelled: { bg: "#fee2e2", fg: "#dc2626" },
};

export function StatusBadge({ status, label }) {
  // Grey for an unrecognised status. Two of the three copies already did this;
  // the series one fell back to green, which is the one tone that claims the
  // thing is live — the worst guess to make about a status we cannot read.
  const c = STATUS_TONES[status] || STATUS_TONES.draft;
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 999,
      fontSize: 12, fontWeight: 700, background: c.bg, color: c.fg, fontFamily: F,
    }}>
      {label}
    </span>
  );
}

/**
 * The kind-of-entry chip in front of an audit log row.
 *
 * There is no `bg` prop: all nineteen call sites across the three log viewers
 * passed `color + "22"`, eighteen of them as a second hand-written hex literal
 * (`bg="#dc262622" color="#dc2626"`). That is a derivation, not a parameter.
 */
export function TypePill({ label, color, title }) {
  return (
    <span title={title} style={{
      fontSize: 12, fontWeight: 800, padding: "2px 8px", borderRadius: 999,
      background: color + "22", color, letterSpacing: 0.5,
      whiteSpace: "nowrap", flexShrink: 0, fontFamily: F,
    }}>{label}</span>
  );
}

/* ── Form fields ──────────────────────────────────────────────────────────
 *
 * `TextInput` / `Textarea` / `Select` / `Field` are the only way to render a
 * form field, and `inputPrimitives.test.js` scans the source to keep that
 * true — the same shape of gate `modalScrollAffordance.test.js` uses for
 * backdrops.
 *
 * Before this there were 18 local `inputStyle` objects plus two shared
 * builders spread over 52 files, and the drift was not a matter of taste:
 * eleven builders rounded the corner at 10, eight at 8 and one at 12, the
 * border was 1px in nineteen of them and 1.5px in the one feeding the ten
 * competition editors, and the 152 fields styled inline added 6 and 12 to the
 * radius set. The same field looked like a different control depending on
 * which screen you reached it from.
 *
 * ONE radius for every field (`INPUT_RADIUS`), and `size` moves the padding
 * and the type size only: a small field is the same control, closer in. The
 * two-radius `size="sm"` idea was considered and dropped — today's 11×10 /
 * 8×8 split is variance, not intent, so encoding it would preserve the very
 * thing this removes.
 *
 * They spread `...rest` onto the native element, so they drop into the
 * uncontrolled `defaultValue` + `onBlur` form the admin editors use as
 * readily as into `value` + `onChange`.
 *
 * NOTE none of them sets `outline: "none"`. Ten of the old copies did, which
 * left keyboard users with no focus ring on roughly half the app's fields.
 * Browsers only paint the default ring for `:focus-visible`, so dropping the
 * override costs mouse and touch users nothing and gives the ring back.
 */

/** The one border radius every form field uses, whatever its size. */
export const INPUT_RADIUS = 10;

/** Padding + type size per size. The radius is deliberately NOT in here.
 *
 *  `lg` exists for one reason and it is not emphasis: below 16px, iOS Safari
 *  zooms the page when a field takes focus. The login form and the generic
 *  input dialog both hit that, and both had written the 16 out by hand. */
export const INPUT_SIZES = {
  lg: { padding: "12px 14px", fontSize: 16 },
  md: { padding: "10px 12px", fontSize: 14 },
  sm: { padding: "8px 10px", fontSize: 13 },
  xs: { padding: "6px 8px", fontSize: 12 },
};

/** The app-wide error ink: an invalid field border, an error placeholder.
 *  The same literal `STEP_ERR` carries in the registration steps. */
export const DANGER_INK = "#dc2626";

/**
 * The amber palette — a semantic colour, not a surface, so it is the same in
 * both themes. `WarnNote` is the notice; the paused-competition button, the
 * "warn" settings tone and the amber status chips are controls and labels that
 * share the palette without being a note. Sixteen files used to spell the hex
 * themselves; `designSystemGates.test.js` (R7) now fails on any spelling
 * outside this file.
 *
 *   bg      the fill              (#fef3c7)
 *   border  the hairline          (#fcd34d)
 *   ink     text on the fill      (#92400e — the note's body)
 *   accent  the stronger label ink / chip foreground (#d97706)
 */
export const AMBER = {
  bg: "#fef3c7",
  border: "#fcd34d",
  ink: "#92400e",
  accent: "#d97706",
};

/** Accepts a resolved theme object OR a `"light"` / `"dark"` mode string.
 *  Both spellings are live at the call sites, and `getTheme(object)` silently
 *  returns the LIGHT theme — a white field on the dark theme, invisible to an
 *  author working in light mode. */
function resolveTheme(theme) {
  return theme && typeof theme === "object" ? theme : getTheme(theme);
}

/**
 * The field style as a plain object, for the places that need the style
 * rather than the element: `DateTimeField` paints this box under a
 * transparent native input, and a few grids derive a per-column variant.
 * Prefer the components — this is the escape hatch, not the front door.
 */
export function inputStyleFor(theme, { size = "md", invalid = false } = {}) {
  const th = resolveTheme(theme);
  const sz = INPUT_SIZES[size] || INPUT_SIZES.md;
  return {
    width: "100%", boxSizing: "border-box",
    padding: sz.padding, fontSize: sz.fontSize,
    borderRadius: INPUT_RADIUS,
    border: "1px solid " + (invalid ? DANGER_INK : th.border),
    background: th.inputBg || th.bg, color: th.text,
    fontFamily: F,
  };
}

/**
 * The id a `Field`'s label points at (`htmlFor`), handed down to the control
 * inside it: `TextInput`, `Textarea` and `Select` take it as their `id` when
 * they have none of their own. So a field wrapped in `Field` is named by its
 * label without every call site minting an id — before this, a `Field` with no
 * `htmlFor` (the event editor's name, status, visibility, every
 * `LabeledInput`) drew a label that named nothing. A `Field` labels ONE
 * control: two primitives inside it would share the id.
 */
const FieldControlContext = createContext(null);

/** The control's own id, else the enclosing `Field`'s. */
function useFieldControlId(ownId) {
  const fromField = useContext(FieldControlContext);
  return ownId ?? fromField ?? undefined;
}

/** Text-shaped `<input>` — text, number, email, password, search, date-less.
 *  Not for checkbox / radio / color / file / range: those have no field box,
 *  and the scan leaves them alone for the same reason. */
export function TextInput({ size, invalid, style, inputRef, id, ...rest }) {
  const { th: theme, th: thProp } = useAppTheme();
  return (
    <input
      ref={inputRef}
      id={useFieldControlId(id)}
      style={{ ...inputStyleFor(theme || thProp, { size, invalid }), ...style }}
      {...rest}
    />
  );
}

/** Multi-line field. `resize: "vertical"` is the default because a textarea
 *  that can be dragged wider escapes every grid it sits in. */
export function Textarea({ size, invalid, style, rows = 3, inputRef, id, ...rest }) {
  const { th: theme, th: thProp } = useAppTheme();
  return (
    <textarea
      ref={inputRef}
      id={useFieldControlId(id)}
      rows={rows}
      style={{ ...inputStyleFor(theme || thProp, { size, invalid }), resize: "vertical", ...style }}
      {...rest}
    />
  );
}

/** Native `<select>`. The browser chevron is kept on purpose — an
 *  `appearance: none` dropdown needs its own arrow, its own open state and its
 *  own mobile behaviour, and that is `Segmented`'s job, not a field's. */
export function Select({ size, invalid, style, children, inputRef, id, ...rest }) {
  const { th: theme, th: thProp } = useAppTheme();
  return (
    <select
      ref={inputRef}
      id={useFieldControlId(id)}
      style={{ ...inputStyleFor(theme || thProp, { size, invalid }), ...style }}
      {...rest}
    >{children}</select>
  );
}

/* ── Empty states ─────────────────────────────────────────────────────────
 *
 * `EmptyState` is the centred, muted line that stands in for content that is
 * not there — "no competitions yet", "nothing matched", "still loading".
 *
 * It already existed, locally, in `PublicWidget` — which is the first step of
 * the copy pattern this roadmap keeps finding, not the last. Around it, 40-odd
 * screens wrote the same three declarations by hand and landed on padding 12,
 * 14, 16, 20, 24, 28, 32, 40 and 60 with fontSize 12, 13, 14 and 18: a spread
 * that is not a decision anyone made, because nobody ever saw two of them at
 * once.
 *
 * `size` is the four meanings behind those numbers, not the numbers:
 *
 *   sm  inside a list section within a card   (the editor tabs)
 *   md  a panel or a modal body               (the default)
 *   lg  a whole tab or page area
 *   tv       a panel on a TV screen, read from across the room
 *   tvSlide  a whole TV slide with nothing to show — one step larger, to
 *            match the type the slide would have carried
 *
 * The message is a PROP, never translated in here: `ui.js` stays i18n-free,
 * and the public widget renders inside a fixed-language subtree where the
 * ambient `t` would be the wrong language.
 */

/** Padding + type size per size — the four meanings above. */
export const EMPTY_SIZES = {
  sm: { padding: 16, fontSize: 13 },
  md: { padding: 24, fontSize: 13 },
  lg: { padding: 40, fontSize: 14 },
  tv: { padding: 60, fontSize: 18 },
  tvSlide: { padding: 60, fontSize: 24 },
};

export function EmptyState({
  children, size = "md", tone = "muted", boxed = false,
 style,
}) {
  const { th: theme, th: thProp } = useAppTheme();
  const th = resolveTheme(theme || thProp);
  const sz = EMPTY_SIZES[size] || EMPTY_SIZES.md;
  return (
    <div style={{
      textAlign: "center",
      padding: sz.padding,
      fontSize: sz.fontSize,
      color: tone === "error" ? DANGER_INK : th.textMuted,
      // `boxed` is the dashed drop-zone look the event editor's tabs use: an
      // empty list you are expected to ADD to, rather than one that simply has
      // nothing in it yet.
      ...(boxed ? { borderRadius: 10, background: th.bg, border: "1px dashed " + th.border } : null),
      ...style,
    }}>{children}</div>
  );
}

/**
 * StatSkeleton — the first-load placeholder of a card or chart whose data has
 * not arrived yet (docs/spec/01f-tab-loading.md, "The per-card first-load
 * gate"). It stands where the content will stand, at the content's height, so
 * nothing below moves when the card renders — and it replaces the fake `0` /
 * "no data" a card used to show while its read was still in flight.
 *
 * `label` is the accessible name (`t('common.loading')` at the call site —
 * this file stays i18n-free). The pulse stops under reduced motion.
 */
export function StatSkeleton({ height, label, style }) {
  const { th: theme } = useAppTheme();
  const th = resolveTheme(theme);
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      className="sendly-skeleton"
      style={{ height, borderRadius: 12, background: th.bg, ...style }}
    >
      <style>{`@keyframes sendly-skeleton-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
        .sendly-skeleton { animation: sendly-skeleton-pulse 1.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .sendly-skeleton { animation: none; } }`}</style>
    </div>
  );
}

/**
 * One number of a statistics panel: the value over its label (the Statistics tab's
 * personal and activity tiles, the global profile's summary cards). The tile is a
 * `group` named by its label, so "Összes pont: 69" is one unit to a screen reader —
 * and to a test, which finds the tile by its label and reads its value.
 *
 * `value` is drawn in the big accent style; `valueNode` replaces it when the value
 * is not a plain number (a difficulty name with its grade range). `labelExtra` sits
 * after the label, outside the name (an ⓘ button); `children` follow below (a
 * percentage, a weekly change). `compact` is the smaller tile of a box inside a
 * card (the Spraywall boxes of the Statistics tab and the global profile): no card
 * background of its own, a 20 px value. `style` / `valueStyle` / `labelStyle` keep
 * a surface's own look.
 */
export function StatTile({ label, value, valueNode, labelExtra, children, compact = false, style, valueStyle, labelStyle }) {
  const { th, accent } = useAppTheme();
  const labelId = useId();
  return (
    <div role="group" aria-labelledby={labelId} style={{
      textAlign: "center", padding: compact ? "10px 4px" : "12px 4px", background: compact ? "transparent" : th.card,
      borderRadius: 12, border: "1px solid " + th.borderLight, ...(compact ? { minWidth: 0 } : null), ...style,
    }}>
      {valueNode ?? <div style={{ fontSize: compact ? 20 : 22, fontWeight: 900, color: accent, ...valueStyle }}>{value}</div>}
      <div style={{ fontSize: 12, color: th.textMuted, marginTop: valueNode ? 2 : 0, ...labelStyle }}>
        <span id={labelId}>{label}</span>
        {labelExtra}
      </div>
      {children}
    </div>
  );
}

/**
 * The heading that folds a list's past part away — „Korábbi versenyek (3)" on the
 * competition list, „Korábbi események (2)" on the event list, both closed by
 * default. A button (`pressable`) that says whether it is open
 * (`aria-expanded`); the turning ▶ is decoration. The two lists wrote it by hand
 * each, the event one as a bare clickable div.
 */
export function PastSectionToggle({ open, onToggle, label, count }) {
  const { th } = useAppTheme();
  return (
    <div
      {...pressable(onToggle)}
      aria-expanded={open}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        marginTop: 20, marginBottom: 10,
        cursor: "pointer", userSelect: "none",
      }}
    >
      <span aria-hidden="true" style={{
        fontSize: 12, color: th.textMuted,
        transition: "transform 0.2s", display: "inline-block",
        transform: open ? "rotate(90deg)" : "rotate(0deg)",
      }}>
        &#9654;
      </span>
      <span style={{ fontSize: 14, fontWeight: 700, color: th.textMuted }}>
        {label} ({count})
      </span>
    </div>
  );
}

/**
 * A collapsible groupbox: a card whose header folds its body away, the state kept
 * per `persistKey` (and `scope`, e.g. the gym) by `useExpanderState`. The box is a
 * `section` named by its title; the header is a button (`pressable`) that says
 * whether it is open (`aria-expanded`). The Statistics tab's groupboxes and the
 * platform admin's statistics groups.
 */
export function CollapsibleGroupBox({ title, persistKey, scope = null, defaultOpen = true, padding = 12, style, children }) {
  const { th } = useAppTheme();
  const [open, toggle] = useExpanderState(persistKey, defaultOpen, scope);
  const titleId = useId();
  return (
    <section aria-labelledby={titleId} style={{
      padding, borderRadius: 14, background: th.card, border: "1px solid " + th.border, ...style,
    }}>
      <div {...pressable(toggle)} aria-expanded={open}
        style={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none", marginBottom: open ? 12 : 0 }}>
        <span aria-hidden="true" style={{
          fontSize: 12, marginRight: 8, transition: "transform 0.2s", display: "inline-block",
          transform: open ? "rotate(90deg)" : "rotate(0deg)", color: th.textMuted,
        }}>&#9654;</span>
        <span id={titleId} style={{ fontSize: 14, fontWeight: 800, color: th.text }}>{title}</span>
      </div>
      {open && children}
    </section>
  );
}

/**
 * Label + control + optional hint — the `<div><label/><input/></div>` eight
 * files wrote with a local `labelStyle` const each.
 *
 * `span` is a grid-column span for the dense registration grids; `required`
 * appends the red asterisk rather than leaving each call site to place it.
 *
 * The label always points at a control: `htmlFor` when the caller names one,
 * else an id of its own, which the `TextInput` / `Textarea` / `Select` inside
 * takes (`FieldControlContext`). One control per `Field`.
 */
export function Field({
  label, hint, htmlFor, required = false, span,
 children, style, labelStyle,
}) {
  const { th: theme, th: thProp } = useAppTheme();
  const th = resolveTheme(theme || thProp);
  const ownId = useId();
  const controlId = htmlFor || ownId;
  return (
    <FieldControlContext.Provider value={controlId}>
    <div style={{ ...(span ? { gridColumn: `span ${span}` } : null), ...style }}>
      {label != null && (
        <label htmlFor={controlId} style={{
          fontSize: 12, fontWeight: 600, color: th.textMuted,
          marginBottom: 4, display: "block", fontFamily: F, ...labelStyle,
        }}>
          {label}{required && <span style={{ color: DANGER_INK }}> *</span>}
        </label>
      )}
      {children}
      {hint != null && (
        <div style={{ fontSize: 12, color: th.textMuted, marginTop: 6 }}>{hint}</div>
      )}
    </div>
    </FieldControlContext.Provider>
  );
}

/**
 * Labelled text/number input for the event registration grids — now a thin
 * `Field` + `TextInput` pair.
 *
 * `invalid` reddens the border without moving anything, because these sit in a
 * dense grid where a validation message would reflow every row below. The two
 * off-scale values it inherited from its copies (label 11px, gap 2px) are gone
 * with the hand-rolled markup that carried them.
 */
export function LabeledInput({ label, value, onChange, type = "text", span = 1, min, max, invalid = false }) {
  return (
    <Field label={label} span={span}>
      <TextInput
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        min={min}
        max={max}
        size="sm"
        invalid={invalid}
      />
    </Field>
  );
}

/**
 * Small red count badge — the notification chip used on the header profile
 * pill and the ⚙️ Settings menu item. Renders nothing when `count < 1`.
 * Multiple pending signals (friend requests + new-feature discovery) are summed
 * by the caller into a single number, so two overlapping badges read as one
 * "2" instead of stacking. Positioning (absolute corner vs. inline) is passed
 * via `style` so it matches each call site.
 */
export function CountBadge({ count, style }) {
  if (!count || count < 1) return null;
  return (
    <div style={{
      minWidth: 18, height: 18, borderRadius: 999, background: "#dc2626", color: "#fff",
      fontSize: 10, fontWeight: 700, display: "inline-flex", alignItems: "center",
      justifyContent: "center", padding: "0 4px", ...style,
    }}>{count}</div>
  );
}

/**
 * TabBar — the app’s top-level tab strip (icon over label). The bar OWNS the
 * 3px accent line that used to be the header’s bottom border, and runs it
 * along its own BOTTOM edge, where it also serves as the boundary between the
 * tabs and the page. At the active tab the line breaks, turns UP both sides
 * with rounded corners and runs over the top of that tab, so the selection
 * reads as a bubble lifted out of the line. That bubble is FILLED with the
 * page background (`th.bg`) while the rest of the bar keeps the header colour
 * (`th.headerBg`), which is what makes the selected tab read as joined to the
 * content below rather than as a slot in the bar. Shared by the in-gym shell
 * (App.js) and the global profile page so both read as the same navigation
 * control — which is why neither header may draw its own accent border any
 * more (it would put a second line back under the header).
 *
 * The notch is ONE SVG path spanning the whole tablist: line stubs that run
 * thousands of px past both ends, two bends, two walls and the line over the
 * tab. A single continuous stroke, so there is no seam where a curve meets a
 * straight and the anti-aliasing is the renderer’s, not three CSS borders
 * trying to line up on sub-pixels. Because the stubs cover any viewport, the
 * SVG IS the line while a tab is selected; the plain full-width line renders
 * only when nothing is selected or before the first measurement. (An earlier version drew a base line and covered it above the
 * active tab; the cover’s edge let a faint hairline through whenever it sat
 * on a fractional pixel mid-move.)
 *
 * Motion: the two walls are animated INDEPENDENTLY, in pixel coordinates,
 * by a rAF loop that rewrites the path — the shape flows rather than
 * sliding as a rigid block. On a tab change the wall FACING the new tab
 * leads: it travels first while the far wall stays put, so the notch
 * stretches open across the gap; when the leading wall lands, the trailing
 * one follows and closes it up. The two walls move differently on purpose:
 * the leading one runs on a plain S-curve (`tabNotchEaseS`) and lands
 * exactly, because the second wall's departure is cued off its arrival; the
 * trailing one is a spring (`tabNotchEaseSpring`) that swings past its mark
 * and settles, and it swings further the further it has to travel — the
 * damping comes from its own pixel distance. A change that is NOT a tab change
 * (a resize, or the labels folding) moves both walls together instead —
 * staggering a symmetric narrowing just makes it wobble.
 *
 * `labelOpen` (0…1) is the first stage of the shells' scroll collapse
 * (`useHeaderCollapse`): the labels fold away under the reader's finger and
 * the bar gets shorter, and only then does the whole block slide out of view.
 * The notch narrows with them, from the label's width to the icon's, and the
 * buttons' vertical padding grows back — a half-folded bar is a resting state,
 * and without that it would come to rest under the 44px a finger needs.
 *
 * On a device that cannot keep up, the move turns itself off: a run that
 * draws under TAB_MOTION_MIN_FPS is a strike, TAB_MOTION_STRIKES consecutive
 * strikes and the notch simply lands from then on. A two-phase move delivered
 * in four visible steps is worse than no move at all, and it is the phone
 * that decides, not a guess from a user-agent string.
 *
 * Why the click updates local state first: the parent’s `onSelect` swaps in
 * the tab’s content, and that render can be heavy (the map, a lazy chunk).
 * With one setState the indicator only starts moving once that render has
 * committed, which reads as a stutter. So the bar commits its own selection
 * immediately and hands the parent’s update to `startTransition`, letting
 * the motion begin while the content renders. The local choice is cleared as
 * soon as `active` changes, so a parent that decides differently still wins.
 *
 * Every tab renders BOTH `.tab-label-full` and `.tab-label-short` spans;
 * the CSS in index.html swaps them below 480px. That’s why
 * `labelShort` must always be supplied (fall back to `label`).
 *
 * @param {Array<{key: string, label: string, labelShort?: string, icon: ReactNode, badge?: boolean}>} props.tabs
 * @param {string} props.active      - key of the selected tab
 * @param {function} props.onSelect  - (key) => void
 * @param {string} props.accent
 * @param {object} props.th          - theme object
 * @param {string} [props.ariaLabel]
 * @param {number} [props.maxWidth]  - inner row max width (default 960)
 */
const TAB_LINE = 3; // accent stroke — same weight the header border had
const TAB_NOTCH_GAP = 16; // air between the label and each wall when the notch fits the label
const TAB_NOTCH_R = 12; // corner radius of the notch, the same at every width
const TAB_STUB = 4000; // how far the top-line stubs run past the bar (covers any viewport)
const TAB_PHASE_MS = 210; // one wall's travel; on a tab change the two run back to back

/**
 * Notch inset + corner radius for a column. The notch hugs the tab’s
 * CONTENT (icon + label) plus `TAB_NOTCH_GAP` on each side, never wider
 * than the column minus a minimum inset — on a two-tab bar a column is
 * ~480px, and a notch that wide reads as a panel, not a selection. Narrow
 * columns (a 6-tab bar on a 360px phone gives ~60px) get a smaller inset,
 * otherwise the walls sit right against a label like „Térkép”.
 *
 * The radius is the SAME on every width. It was briefly tied to the inset,
 * which on a phone meant a 4px corner: at that size a 3px stroke turning
 * through 4px does not read as a curve at all, and the notch looked square.
 * A radius wider than the inset simply means the bend flares out over the
 * neighbouring column on its way up to the line, which is what gives the
 * shape its scooped look — and on the first and last tab, where the flare
 * runs past the end of the bar, the line still meets the corner of the
 * screen at the right height, so nothing is cut.
 *
 * What the radius genuinely cannot exceed is half the gap between the two
 * walls (or the bends would cross and draw a bow tie — the trailing wall’s
 * spring swings the notch narrow for a moment, so this is a live case, and
 * `tabNotchPath` re-clamps per frame) and half the bar’s own height, for
 * the short compact bar where two full corners would otherwise be taller
 * than the wall between them.
 */
export function tabNotchShape(cellWidth, contentWidth = 0, cellHeight = Infinity, line = TAB_LINE) {
  const minPad = cellWidth < 96 ? 4 : 10;
  const fitted = contentWidth > 0 ? (cellWidth - (contentWidth + 2 * TAB_NOTCH_GAP)) / 2 : minPad;
  const pad = Math.max(minPad, Math.round(fitted));
  const r = Math.max(0, Math.min(TAB_NOTCH_R, (cellWidth - 2 * pad) / 2, (cellHeight - line) / 2));
  return { pad, r };
}

/** Where the two walls of tab `index` sit, in tablist coordinates. */
export function tabNotchWalls(cellWidth, index, pad) {
  return { l: index * cellWidth + pad, r: (index + 1) * cellWidth - pad };
}

/**
 * The S-curve, for the LEADING wall: ease-in-out cubic. Velocity rises from
 * zero, peaks at the halfway point and falls back to zero, and the wall
 * never passes its mark — it has to land cleanly, because the trailing wall
 * only sets off once it has arrived.
 */
export function tabNotchEaseS(u) {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(2 - 2 * u, 3) / 2;
}

const TAB_SPRING_DECAY = 5.6; // envelope is spent (< 0.5%) by the end of the phase
const TAB_SPRING_MAX_REL = 0.12; // relative overshoot on a short hop
const TAB_SPRING_MAX_PX = 40; // ...but never more than this many pixels past the mark

/**
 * How far past its mark the trailing wall swings, as a FRACTION of the
 * distance it travels. A spring pulled further does bounce further, so the
 * overshoot is proportional to the travel — 12% of it — which on a phone's
 * 60px column is ~7px and on a desktop column ~19px. The cap keeps a
 * multi-tab jump sane: 12% of an 800px leap would be a 96px lunge, so past
 * ~330px the swing stops growing and stays at 40px.
 */
export function tabNotchSpringOvershoot(distancePx) {
  const d = Math.abs(distancePx) || 1;
  return Math.min(TAB_SPRING_MAX_REL, TAB_SPRING_MAX_PX / d);
}

/**
 * The spring, for the TRAILING wall: the step response of an under-damped
 * spring, so it arrives with speed, swings past, comes back, and on a long
 * travel shows a second, much smaller wobble before it settles. The damping
 * is derived from `distancePx` through `tabNotchSpringOvershoot`, which is
 * what ties "springs more the further it goes" to actual pixels.
 *
 * `decay` is fixed so the envelope is spent by the end of the phase; the
 * frequency then follows from the wanted first overshoot, since for this
 * response `overshoot = e^(-decay·π/freq)`. Normalised: `u` runs 0 → 1 and
 * the result passes 1 on the way.
 */
export function tabNotchEaseSpring(u, distancePx) {
  if (u <= 0) return 0;
  if (u >= 1) return 1;
  const decay = TAB_SPRING_DECAY;
  const freq = (decay * Math.PI) / -Math.log(tabNotchSpringOvershoot(distancePx));
  const at = x => 1 - Math.exp(-decay * x) * (Math.cos(freq * x) + (decay / freq) * Math.sin(freq * x));
  // Divided by its own value at u = 1: a slow spring (the low frequency a
  // long travel asks for) is still a pixel or two short when the phase ends,
  // and clamping that away is a visible snap on the last frame.
  return at(u) / at(1);
}

/**
 * One frame of the move, at `t` ms. The wall facing the new tab travels
 * through the first phase alone — the notch stretches open across the gap —
 * and the other wall follows through the second, closing it.
 *
 * Only a TAB CHANGE goes through here. Every other reason the notch moves (a
 * resize, or the labels folding away under the reader's finger) is committed
 * straight to the new geometry: those are continuous, already-driven changes,
 * and running a 420ms two-phase animation towards a target that moves again
 * next frame would just smear it.
 */
export function tabNotchFrame(from, to, t, phase = TAB_PHASE_MS) {
  const rightward = to.l > from.l;
  const lead = tabNotchEaseS(t / phase);
  // The trailing wall is the springy one, and it springs by its OWN travel:
  // whichever wall goes second is the one whose distance sets the swing.
  const trailFrom = rightward ? from.l : from.r;
  const trailTo = rightward ? to.l : to.r;
  const trail = tabNotchEaseSpring((t - phase) / phase, trailTo - trailFrom);
  const at = (a, b, e) => a + (b - a) * e;
  return {
    l: at(from.l, to.l, rightward ? trail : lead),
    r: at(from.r, to.r, rightward ? lead : trail),
    done: t >= phase * 2,
  };
}

const px = v => Math.round(v * 100) / 100;

/**
 * The notch as one SVG path across the whole bar. `l` / `r` are the wall
 * positions in tablist coordinates; the stroke is centred on the path, so the
 * line runs at y = height − line / 2 — along the BOTTOM of the bar, where it
 * doubles as the boundary between the tabs and the page. Around the active
 * tab it lifts: the line breaks, turns UP both sides and runs over the top of
 * that tab, so the selected tab reads as a bubble pushed up out of the line
 * rather than a notch cut down into it.
 */
export function tabNotchPath(totalWidth, height, { l, r, radius }, line = TAB_LINE, stub = TAB_STUB) {
  const rad = px(Math.max(0, Math.min(radius, (r - l) / 2)));
  const yT = px(line / 2), yB = px(height - line / 2);
  const x1 = px(l), x2 = px(r);
  return `M${px(-stub)} ${yB} H${px(x1 - rad)} A${rad} ${rad} 0 0 0 ${x1} ${px(yB - rad)} V${px(yT + rad)} A${rad} ${rad} 0 0 1 ${px(x1 + rad)} ${yT} ` +
    `H${px(x2 - rad)} A${rad} ${rad} 0 0 1 ${x2} ${px(yT + rad)} V${px(yB - rad)} A${rad} ${rad} 0 0 0 ${px(x2 + rad)} ${yB} H${px(totalWidth + stub)}`;
}

/**
 * The selected tab's cell as a FILLED region: the same bubble the stroke
 * draws, closed along the bottom edge of the bar.
 *
 * Painted in the page's background colour, so the selected tab reads as
 * continuous with the content under it rather than as a slot in the bar — the
 * bar's own surface is the header colour, and that difference is what makes
 * the bubble look joined to the page. It follows the outward flare at the
 * bottom corners too: closing the fill with straight walls instead would leave
 * a bar-coloured sliver inside each of those curves.
 */
export function tabNotchFill(totalWidth, height, { l, r, radius }, line = TAB_LINE) {
  const rad = px(Math.max(0, Math.min(radius, (r - l) / 2)));
  const yT = px(line / 2), yB = px(height - line / 2), h = px(height);
  const x1 = px(l), x2 = px(r);
  return `M${px(x1 - rad)} ${h} V${yB} A${rad} ${rad} 0 0 0 ${x1} ${px(yB - rad)} V${px(yT + rad)} ` +
    `A${rad} ${rad} 0 0 1 ${px(x1 + rad)} ${yT} H${px(x2 - rad)} A${rad} ${rad} 0 0 1 ${x2} ${px(yT + rad)} ` +
    `V${px(yB - rad)} A${rad} ${rad} 0 0 0 ${px(x2 + rad)} ${yB} V${h} Z`;
}

const prefersReducedMotion = () => typeof window !== "undefined" &&
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const TAB_MOTION_MIN_FPS = 30; // below this a run counts as a strike
export const TAB_MOTION_STRIKES = 2; // …and this many strikes stop animating

/**
 * Whether the device is keeping up, judged from a run that just finished.
 * Pure, so the rule can be tested without a slow phone.
 *
 * A device too weak to draw this is better off without it: a two-phase move
 * that lands in four visible steps is worse than no move at all. But one bad
 * run proves nothing — the first tab switch of a session competes with the
 * tab's own first render, lazy chunks and Firestore snapshots — so it takes
 * repeated failures, and they have to be consecutive: a single good run means
 * the device can do this and the bad one was the page's fault, not the
 * phone's.
 *
 * @returns {number} the new strike count; at TAB_MOTION_STRIKES, stop.
 */
export function nextMotionStrikes(strikes, frames, elapsedMs, minFps = TAB_MOTION_MIN_FPS) {
  if (!(elapsedMs > 0) || frames < 2) return strikes; // nothing to judge
  const fps = (frames / elapsedMs) * 1000;
  return fps < minFps ? strikes + 1 : 0;
}

// Session-wide, deliberately module scope: the answer is about the device, not
// about one bar, and re-learning it on every mount would mean re-suffering it.
let motionStrikes = 0;
const motionAffordable = () => motionStrikes < TAB_MOTION_STRIKES;

export function TabBar({ tabs = [], active, onSelect, ariaLabel, maxWidth = 960, labelOpen = 1 }) {
  const { accent, th } = useAppTheme();
  const L = TAB_LINE;
  const listRef = useRef(null);
  const iconRefs = useRef([]);
  const labelRefs = useRef([]);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [contentW, setContentW] = useState({ icon: [], label: [] });
  const [pending, setPending] = useState(null);
  const shown = pending !== null && tabs.some(tb => tb.key === pending) ? pending : active;
  const activeIdx = tabs.findIndex(tb => tb.key === shown);
  // The parent has answered (or navigated elsewhere): its word is final.
  useEffect(() => { setPending(null); }, [active]);

  const n = Math.max(tabs.length, 1);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setBox(b => (b.w === rect.width && b.h === rect.height) ? b : { w: rect.width, h: rect.height });
      // Label widths change with the ≤480px label swap, which always comes
      // with a width change, so one observer covers both. A folding label
      // keeps its width — only its height goes — so the icon is measured
      // separately and the notch is interpolated between the two.
      const widths = list => list.slice(0, n).map(node => node ? node.getBoundingClientRect().width : 0);
      const icon = widths(iconRefs.current), label = widths(labelRefs.current);
      const same = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);
      setContentW(prev => (same(prev.icon, icon) && same(prev.label, label)) ? prev : { icon, label });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [n]);

  const open = Math.max(0, Math.min(1, labelOpen));
  const iconW = contentW.icon[activeIdx] || 0, labelW = contentW.label[activeIdx] || 0;
  const cellW = box.w / n;
  const shape = tabNotchShape(cellW, iconW + (Math.max(iconW, labelW) - iconW) * open, box.h, L);
  const target = activeIdx >= 0 && box.w > 0 ? tabNotchWalls(cellW, activeIdx, shape.pad) : null;

  const [walls, setWalls] = useState(null);
  const wallsRef = useRef(null);
  const rafRef = useRef(0);
  const animRef = useRef(null);
  const lastIdx = useRef(activeIdx);
  const tl = target ? target.l : 0, tr = target ? target.r : 0;

  // Cancel ONLY on unmount. This used to be the effect's own cleanup, and that
  // is what made tab switches "sometimes" not animate — most visibly in the
  // iOS PWA, where a switch usually happens mid-page. A tab change does not
  // change one thing, it changes several: the collapse reopens (labels unfold,
  // so the bar's height changes) and useScrollMemory restores that tab's own
  // offset (so the notch's inset changes). Each of those re-ran the effect a
  // frame or two later, the cleanup killed the animation that had just
  // started, and the re-run saw no tab change and committed the geometry
  // outright. The move existed for two frames and then snapped.
  useEffect(() => () => { cancelAnimationFrame(rafRef.current); animRef.current = null; }, []);

  useEffect(() => {
    if (activeIdx < 0 || !box.w) return;
    const commit = w => { wallsRef.current = w; setWalls(w); };
    const to = { l: tl, r: tr };
    const from = wallsRef.current;
    const tabChanged = lastIdx.current !== activeIdx;
    lastIdx.current = activeIdx;

    if (animRef.current && !tabChanged) {
      // Mid-flight, and the geometry moved under us. RETARGET rather than
      // snap: the run keeps its own timeline and simply aims somewhere else.
      animRef.current.to = to;
      return;
    }
    // Anything but a tab change (a resize, the labels folding under the
    // finger) lands immediately: those move again next frame, and animating
    // towards a moving target only smears it.
    if (!from || !tabChanged || prefersReducedMotion() || !motionAffordable()) { commit(to); return; }
    if (Math.abs(from.l - to.l) < 0.5 && Math.abs(from.r - to.r) < 0.5) { commit(to); return; }

    cancelAnimationFrame(rafRef.current);
    const run = { from, to, t0: performance.now(), frames: 0 };
    animRef.current = run;
    const step = now => {
      if (animRef.current !== run) return; // superseded by a newer move
      run.frames++;
      const f = tabNotchFrame(run.from, run.to, now - run.t0);
      if (f.done) {
        commit(run.to);
        animRef.current = null;
        motionStrikes = nextMotionStrikes(motionStrikes, run.frames, now - run.t0);
        return;
      }
      commit({ l: f.l, r: f.r });
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [tl, tr, activeIdx, box.w, box.h]);

  const drawn = walls || (target ? { l: tl, r: tr } : null);
  // No hairline border on the bar: the accent line runs along its bottom edge
  // now, and is the boundary itself.
  return (
    <div style={{ background: th.headerBg, position: "relative" }}>
      {!drawn && <div aria-hidden="true" style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: L, background: accent }} />}
      <div ref={listRef} role="tablist" aria-label={ariaLabel} style={{ maxWidth, margin: "0 auto", display: "flex", position: "relative" }}>
        {drawn && (
          <svg aria-hidden="true" width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} style={{
            position: "absolute", top: 0, left: 0, overflow: "visible", display: "block",
            opacity: activeIdx < 0 ? 0 : 1, transition: "opacity 0.2s ease", pointerEvents: "none",
          }}>
            {/* Fill first, outline over it. */}
            <path d={tabNotchFill(box.w, box.h, { ...drawn, radius: shape.r }, L)} fill={th.bg} stroke="none" />
            <path d={tabNotchPath(box.w, box.h, { ...drawn, radius: shape.r }, L)}
              fill="none" stroke={accent} strokeWidth={L} strokeLinejoin="round" />
          </svg>
        )}
        {tabs.map((tb, i) => (
          <button role="tab" aria-selected={shown === tb.key} key={tb.key} onClick={() => {
            setPending(tb.key);
            startTransition(() => {
              const answer = onSelect(tb.key);
              // A navigation guard may veto the switch; drop the optimistic highlight then.
              if (answer && typeof answer.then === "function") answer.then((ok) => { if (!ok) setPending(null); }, () => setPending(null));
            });
          }} style={{
            // The padding grows back as the label folds: without it the bar
            // would come to rest at ~33px, under the 44px a finger needs, and
            // a half-collapsed bar IS a resting state — the reader can stop
            // scrolling anywhere.
            flex: 1, minWidth: 0, fontSize: 12, fontWeight: 700, background: "none", border: "none",
            padding: `${12 + 2 * (1 - open)}px 0 ${8 + 4 * (1 - open)}px`,
            color: shown === tb.key ? accent : th.textMuted, cursor: "pointer", fontFamily: F,
            transition: "color 0.25s ease",
            position: "relative", display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.2,
          }}>
            <span ref={node => { iconRefs.current[i] = node; }} style={{ fontSize: 16, display: "inline-block" }}>{tb.icon}</span>
            <span ref={node => { labelRefs.current[i] = node; }} style={{
              display: "inline-block", overflow: "hidden",
              maxHeight: 16 * open, marginTop: 2 * open, opacity: open,
            }}>
              <span className="tab-label-full">{tb.label}</span>
              <span className="tab-label-short">{tb.labelShort || tb.label}</span>
            </span>
            {tb.badge && <span style={{ position: "absolute", top: 8, right: "calc(50% - 18px)", width: 8, height: 8, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Segmented control — iOS-style mutually-exclusive picker. Replaces the
 * loose `<Pill>` rows used as filters across the app (status filter,
 * period filter, discipline switcher, group selector, editor tab nav).
 * The grouped inset background visually communicates "these are
 * alternatives", which a row of free-floating pills doesn't.
 *
 * Theme: works in both light and dark — `th.bg` (always darker than
 * `th.card`) is the inset, the active segment lifts to `th.card` with a
 * subtle border + shadow.
 *
 * @param {object} props
 * @param {Array<{value: any, label: ReactNode, color?: string, disabled?: boolean, title?: string, dashed?: boolean, ariaLabel?: string}>} props.options
 *                                    `dashed` draws a dashed accent frame around the segment (e.g. the consensus grade).
 *                                    `ariaLabel` names a segment whose visible label is a short form (e.g. "Nincs" for "Nincs szavazat").
 * @param {any} props.value         - currently selected value (single-select)
 * @param {function} props.onChange - (value) => void
 * @param {string} [props.accent]   - accent color (used when variant="fill")
 * @param {object} props.th         - theme object from getTheme()
 * @param {"default"|"fill"} [props.variant]
 *   - "default" (iOS-style): active = lifted card with theme text. Color
 *     options show a small dot before the label that stays visible in
 *     both states; the dot does NOT compete with the active "lift" cue.
 *   - "fill": active = colored background (option.color or accent), white
 *     text. Use only when there's a strong reason to show the option's
 *     color in the active state (rare — generally avoid).
 * @param {boolean} [props.scroll]  - horizontal scroll for many options
 *                                    (no wrap). Use for editor tab bars
 *                                    with 5+ tabs that overflow on mobile.
 * @param {boolean} [props.fit] - with `fullWidth`: the segments share the
 *                                row by label length (padding 6px 8px) and a
 *                                label that still does not fit ellipsizes.
 * @param {boolean} [props.disabled] - locks the whole control (read-only
 *                                     surfaces such as a closed competition).
 *                                     Per-option `option.disabled` still works
 *                                     for greying out a single choice.
 * @param {string} [props.ariaLabel]
 */
export function Segmented({
  options = [], value, onChange,
  variant = "default", scroll = false, wrap = false, fullWidth = false, fit = false,
  disabled = false, ariaLabel,
}) {
  // `fit` only refines `fullWidth`: shares by label length instead of equal parts.
  const fitted = fullWidth && fit;
  const { accent, th } = useAppTheme();
  const acc = accent || DEFAULT_ACCENT;
  const text = th?.text || "#1d1d1f";
  const textMuted = th?.textMuted || "#6e6e73";
  const borderLight = th?.borderLight || "#f0f0f0";

  // Theme-aware translucent overlays for the inset panel and active
  // segment. Earlier we used solid `th.bg` for the inset, but that's
  // the same colour many cards / rows already use as their own
  // background — so the Segmented panel disappeared into the parent
  // and the "these are alternatives" cue was lost. rgba overlays adapt
  // to whatever sits behind them.
  const isDark = (text || "").toLowerCase() === "#f0f0f0";
  const insetBg = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
  const activeBgDefault = isDark ? "rgba(255,255,255,0.14)" : "#ffffff";
  const activeBorderRing = isDark ? "rgba(255,255,255,0.18)" : borderLight;
  // Solid stand-in for `insetBg` — a coloured option dot needs a measurable
  // surface to derive its ring from, and an rgba overlay cannot be measured.
  // Assumes the panel sits on a card, which is where Segmented lives.
  const panelBg = blendHex(isDark ? "#ffffff" : "#000000", th?.card || (isDark ? "#1e1e1e" : "#ffffff"), isDark ? 0.07 : 0.05);

  // Three layouts:
  //  - scroll: single horizontal row, swipe to scroll (8+ tabs that don't fit)
  //  - wrap:   flex-wrap multi-row (8+ tabs without scroll — labels can have
  //            a `.shortLabel` rendered on ≤480px via tab-label-* CSS classes)
  //  - default (inline-flex): single inline row, sized to content
  const containerStyle = scroll ? {
    display: "flex", overflowX: "auto", gap: 4,
    background: insetBg, borderRadius: 10, padding: 3,
    maxWidth: "100%", WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
  } : wrap ? {
    display: "flex", flexWrap: "wrap", gap: 3,
    background: insetBg, borderRadius: 10, padding: 3,
  } : fullWidth ? {
    // fullWidth: the row stretches to its container and the segments share
    // it equally — the settings-page pickers (theme, grade scale, goal unit)
    // that used to be a second, hand-rolled segmented control.
    display: "flex", gap: 0,
    background: insetBg, borderRadius: 10, padding: 3,
  } : {
    display: "inline-flex", gap: 0,
    background: insetBg, borderRadius: 10, padding: 3,
  };

  return (
    <div role="tablist" aria-label={ariaLabel}
      style={{ ...containerStyle, ...(disabled ? { opacity: 0.6 } : null) }}
      className={scroll ? "sendly-seg-scroll" : undefined}>
      {options.map(opt => {
        const isActive = opt.value === value;
        const optColor = opt.color;
        // Control-level lock and per-option lock are the same thing to the
        // button — the first covers a whole read-only surface, the second a
        // single unavailable choice.
        const optDisabled = disabled || opt.disabled;

        // Active background: variant="fill" uses the option's color (or
        // accent fallback); variant="default" uses a translucent overlay
        // (white on light, ~14% white on dark) for the iOS-style raised
        // look that adapts to whatever parent the segmented sits on.
        const activeBg = variant === "fill" ? (optColor || acc) : activeBgDefault;
        const activeFg = variant === "fill" ? "#fff" : text;

        return (
          <button
            key={String(opt.value)}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={optDisabled}
            title={opt.title}
            aria-label={opt.ariaLabel}
            onClick={() => !optDisabled && onChange(opt.value)}
            style={{
              padding: fitted ? "6px 8px" : "6px 14px",
              fontSize: 13,
              fontWeight: isActive ? 800 : 600,
              background: isActive ? activeBg : "transparent",
              color: isActive ? activeFg : textMuted,
              border: "none",
              borderRadius: 8,
              cursor: optDisabled ? "default" : "pointer",
              // Per-option only. A whole-control lock dims the container
              // instead — fading each segment to 0.5 would drop the SELECTED
              // one to the same weight as the rest, and a read-only control
              // still has to say which value it holds.
              opacity: opt.disabled ? 0.5 : 1,
              transition: "all 0.15s",
              boxShadow: isActive
                ? "0 1px 3px rgba(0,0,0,0.18), 0 0 0 0.5px " + activeBorderRing
                : "none",
              fontFamily: F,
              flexShrink: 0,
              whiteSpace: "nowrap",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: fullWidth ? "center" : undefined,
              flex: fitted ? "1 1 auto" : fullWidth ? 1 : undefined,
              // Equal shares mean equal shares: a long label (a wall name in the
              // Spraywall top switch) ellipsizes inside its own segment instead
              // of widening it and squeezing the neighbour.
              minWidth: fullWidth ? 0 : undefined,
              gap: 6,
              minHeight: 28,
              // Dashed frame marks a hinted option (the consensus grade); inset so it never changes the segment size.
              ...(opt.dashed ? { outline: "2px dashed " + acc + "73", outlineOffset: -2 } : null),
            }}
          >
            {optColor && (
              // The ring used to be a flat `rgba(0,0,0,0.15)`, which is DARKER
              // than the dark theme's panel — so a black group's dot had a
              // black outline on a near-black inset and simply wasn't there.
              // `ColorDot` derives the ring from the dot's own colour instead.
              <ColorDot
                color={variant === "fill" && isActive ? "#ffffff" : optColor}
                surface={variant === "fill" && isActive ? (optColor || acc) : panelBg}
              />
            )}
            {opt.shortLabel ? (
              <>
                <span className="tab-label-full">{opt.label}</span>
                <span className="tab-label-short">{opt.shortLabel}</span>
              </>
            ) : fitted ? (
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{opt.label}</span>
            ) : opt.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Switch — iOS-style on/off toggle. The compact, single-control replacement
 * for two-button "On / Off" rows and bare boolean checkboxes. Use the bare
 * <Switch> inline (e.g. next to a form-field label) where you'd otherwise put
 * a checkbox, or pair it with <ToggleRow> for the standard label + hint +
 * switch single-row layout used across Settings / Admin / Competitions /
 * Events / Platform admin.
 *
 * Reserve it for genuine booleans (a setting / feature flag turned on or off).
 * For "pick exactly one of N" use <Segmented>; for legal consent / one-time
 * acknowledgements and multi-select item lists keep a checkbox.
 *
 * @param {boolean} props.checked
 * @param {(next:boolean)=>void} props.onChange - called with the NEW value
 * @param {boolean} [props.disabled]
 * @param {string}  [props.accent]
 * @param {Object}  [props.th] - resolved theme tokens (getTheme result)
 * @param {number}  [props.size=28] - track height in px; knob/width derive from it
 * @param {string}  props.ariaLabel - names the icon-only control for SR users
 * @param {string}  [props.id] - lets an external <label htmlFor> target it
 */
export function Switch({ checked, onChange, disabled = false, accent, size = 28, ariaLabel, id }) {
  const { th } = useAppTheme();
  const acc = accent || DEFAULT_ACCENT;
  // iOS uses a mid-gray off-track (not the card/input background) so the white
  // knob stays visible on both themes. Detect dark the same way Segmented does.
  const isDark = (th?.text || "").toLowerCase() === "#f0f0f0";
  const offTrack = isDark ? "#3a3a3c" : "#e4e4e7";
  const offBorder = isDark ? "#48484a" : "#d4d4d8";
  const h = size;
  const knob = h - 6;
  const w = Math.round(h * 1.64);
  const tx = w - knob - 6; // travel distance: track width minus knob minus both gaps
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      id={id}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!checked); }}
      style={{
        position: "relative", width: w, height: h, flexShrink: 0,
        // borderRadius on the button itself so any focus/tap highlight is
        // pill-shaped, not a rectangle. outline + tap-highlight cleared so the
        // browser's default grey tap flash (≈rgba(0,0,0,0.18) — visible on
        // light themes, invisible on dark) never shows.
        border: "none", padding: 0, background: "transparent", borderRadius: 999,
        outline: "none", WebkitTapHighlightColor: "transparent",
        WebkitAppearance: "none", appearance: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span aria-hidden="true" style={{
        position: "absolute", inset: 0, borderRadius: 999,
        background: checked ? acc : offTrack,
        border: "1px solid " + (checked ? acc : offBorder),
        transition: "background 0.18s, border-color 0.18s",
      }} />
      <span aria-hidden="true" style={{
        position: "absolute", top: 3, left: 3, width: knob, height: knob,
        borderRadius: 999, background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        // translate3d (not translateX) keeps the knob on its own compositor
        // layer so it slides purely horizontally — translateX re-rasterized
        // each frame and sub-pixel snapping made it appear to bob vertically.
        transform: checked ? `translate3d(${tx}px,0,0)` : "translate3d(0,0,0)",
        transition: "transform 0.18s ease",
        willChange: "transform",
      }} />
    </button>
  );
}

/**
 * ToggleRow — single-row boolean setting: title (+ optional hint / icon) on
 * the left, <Switch> on the right. The whole row is the label, so tapping
 * anywhere toggles. Standard layout for on/off settings everywhere.
 *
 * @param {React.ReactNode} props.label
 * @param {React.ReactNode} [props.hint] - one-liner under the title
 * @param {string} [props.icon] - emoji/string prefix for the title
 * @param {boolean} props.checked
 * @param {(next:boolean)=>void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {string} [props.accent]
 * @param {Object} [props.th]
 * @param {number} [props.size]
 * @param {Object} [props.style] - extra row styles
 */
export function ToggleRow({ label, hint, icon, checked, onChange, disabled = false, size = 28, style, ariaLabel, divider = false, dividerBelow = false }) {
  const { th } = useAppTheme();
  const text = th?.text || "#1d1d1f";
  const textMuted = th?.textMuted || "#6e6e73";
  const isDark = (th?.text || "").toLowerCase() === "#f0f0f0";
  // borderLight (#f0f0f0) is nearly invisible on a white card, so the divider
  // uses the stronger th.border on light; dark keeps the subtler borderLight,
  // which already reads well on the dark card.
  const dividerColor = (isDark ? th?.borderLight : th?.border) || th?.border || "rgba(128,128,128,0.25)";
  return (
    // Outer wrapper carries the caller's spacing (style) and the optional
    // divider. The divider is a standalone straight hairline — NOT a borderTop
    // on the rounded row, which would curve at the corners.
    <div style={{ ...style }}>
      {divider && (
        <div aria-hidden="true" style={{ height: 1, background: dividerColor, margin: "0 -8px" }} />
      )}
      <div
        // `.sendly-toggle-row:hover` (index.html, gated to hover-capable
        // pointers) lights up the whole row so the label and the right-aligned
        // switch read as one control even on wide desktop layouts.
        className="sendly-toggle-row"
        onClick={() => { if (!disabled) onChange(!checked); }}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          // 8px vertical keeps the row compact but breathable; the 8px
          // horizontal padding + matching -8px margin make the hover band bleed
          // a few px past the label/switch while the content stays aligned with
          // the rest of the card. Every settings container has ≥12px padding,
          // so the bleed never causes overflow.
          padding: "8px 8px", margin: "0 -8px", borderRadius: 8,
          transition: "background 0.12s",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: text }}>{icon ? `${icon} ` : ""}{label}</div>
          {hint && <div style={{ fontSize: 12, color: textMuted, marginTop: 2, lineHeight: 1.4 }}>{hint}</div>}
        </div>
        <Switch checked={checked} onChange={onChange} disabled={disabled} size={size}
          ariaLabel={ariaLabel || (typeof label === "string" ? label : undefined)} />
      </div>
      {dividerBelow && (
        <div aria-hidden="true" style={{ height: 1, background: dividerColor, margin: "0 -8px" }} />
      )}
    </div>
  );
}

/** Mobile side-gutter for every modal backdrop. Change here to retune all
 *  modals at once: keep it small so cards are a few px wider on phones. On
 *  desktop the card's own maxWidth dominates, so this has no visible effect
 *  there. */
export const MODAL_GUTTER = 12;

/**
 * The layer of a BLOCKING gate — the consent modal a first sign-in (or a new
 * legal version) puts in front of the app. It must sit above every app modal:
 * a deep-link intent can open one behind the gate (the event sign-up landing's
 * hand-off opens the registration form as soon as the new member's documents
 * arrive, which is before the consent is given), and at the form's 2100 over
 * the gate's old 200 the visitor could no longer reach the consent's boxes
 * (found by the J09 browser test). App modals stay below 20000 — pinned by
 * `blockingGateLayer.test.js` —, so the gate is always the one on top and the
 * form waits underneath until the consent is given.
 */
export const BLOCKING_GATE_Z_INDEX = 20000;

/** Stacking order for anchored dropdown panels (`OverflowMenu`, `LangSelect`,
 *  and the admin competition-list import menu).
 *
 *  The app's z-scale, from the bottom: in-page content and its stickies (1–5),
 *  DROPDOWNS (40), the sticky app header (50), FABs and the drill-in toggle
 *  (60), ProfileMenu's header popover (60–62), modals (100+).
 *
 *  Dropdowns MUST stay below the header. They are `position: absolute` inside
 *  scrolling content, so scrolling carries them upward with their anchor; at
 *  the header's own 50 they tied and won on DOM order, and the panel rode up
 *  OVER the top nav bar. At 40 it slides under it, like any other content. */
export const Z_DROPDOWN = 40;

/**
 * ModalBackdrop — the shared full-screen overlay every modal sits in: fixed
 * inset-0, dimmed (optionally blurred) background, flex-centered, with the
 * standard mobile gutter (MODAL_GUTTER). Extracted so backdrop-wide tweaks
 * (gutter, dim, blur) live in ONE place instead of being copy-pasted across
 * ~35 modals. Render the modal's card as children.
 *
 * It also owns the two things every modal needs and each one used to wire by
 * hand — the body-scroll lock and Escape-to-close:
 *
 *   - `<ScrollLock />` is mounted for you. 39 of the 40 callers paired one
 *     manually and three simply forgot, which left the page scrolling behind
 *     the modal. Pass `lockScroll={false}` for an overlay that deliberately
 *     lets the page move underneath.
 *   - Escape closes via `onClose`, through the shared `useEscapeKey` stack, so
 *     a modal opened on top of another takes the key alone instead of both
 *     closing at once. Follows `closeOnBackdrop` by default — a modal that must
 *     not be dismissed from outside (consent) is not dismissable by keyboard
 *     either — and `closeOnEscape` overrides that on its own.
 *
 * Backdrop click closes via `onClose`, but only when the click lands on the
 * backdrop itself (`target === currentTarget`) — clicks bubbling from the
 * card are ignored, so no per-modal stopPropagation is needed on the card.
 * For conditional dismissal ("not while importing"), let the passed `onClose`
 * be undefined: that closes the backdrop click and the Escape key together.
 *
 * @param {function} [props.onClose]
 * @param {boolean}  [props.closeOnBackdrop=true]
 * @param {boolean}  [props.closeOnEscape=closeOnBackdrop]
 * @param {boolean}  [props.lockScroll=true]
 * @param {number}   [props.zIndex=100]
 * @param {number}   [props.dim=0.45]    - backdrop black alpha (0..1)
 * @param {boolean}  [props.blur=false]  - frosted backdrop
 * @param {string}   [props.ariaLabel]
 * @param {string}   [props.role="dialog"]
 * @param {Object}   [props.style]       - rare extra overrides
 */
export function ModalBackdrop({ children, onClose, closeOnBackdrop = true, closeOnEscape = closeOnBackdrop, lockScroll = true, zIndex = 100, dim = 0.45, blur = false, ariaLabel, role = "dialog", style }) {
  // This backdrop sits at the ambient depth; everything it renders is one
  // level deeper, so a modal (or a dropdown) opened from inside this one takes
  // Escape ahead of it. See useEscapeKey — mount order alone gets this
  // backwards, because React runs a child's effects before its parent's.
  const depth = useContext(EscapeDepthContext);
  useEscapeKey(onClose, closeOnEscape && !!onClose);
  // Tells StatusBarTintBar that the screen is dimmed, and by how much.
  useLayoutEffect(() => {
    _pushModalDim(dim);
    return () => _popModalDim(dim);
  }, [dim]);
  return (
    <EscapeDepthContext.Provider value={depth + 1}>
    <div
      role={role}
      aria-modal="true"
      aria-label={ariaLabel}
      onClick={closeOnBackdrop && onClose ? (e) => { if (e.target === e.currentTarget) onClose(); } : undefined}
      style={{
        position: "fixed", inset: 0, zIndex,
        background: `rgba(0,0,0,${dim})`,
        ...(blur ? { backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" } : {}),
        display: "flex", alignItems: "center", justifyContent: "center",
        // Gutter stays a plain 12px on every edge. Adding the safe-area insets
        // here was a mistake: modal cards size themselves in vh (80–92), so the
        // extra top+bottom padding pushed a tall card past the visible area
        // instead of protecting it. If a specific modal ever needs status-bar
        // clearance, it belongs on that modal's card, not on every backdrop.
        padding: MODAL_GUTTER, fontFamily: F,
        ...style,
      }}
    >
      {lockScroll ? <ScrollLock /> : null}
      {children}
    </div>
    </EscapeDepthContext.Provider>
  );
}

/** Corner radius of a modal card, and therefore of the header and footer that
 *  sit flush against it. Modal cards are the 20 end of the radius scale. */
export const MODAL_RADIUS = 20;

/**
 * ModalHeader — the sticky strip at the top of a scrolling modal: an optional
 * accent stripe, the identity area (passed as children) and the close X. Nine
 * modals wrote this byte-identically; the tenth got a close button only after a
 * user reported not being able to leave the JAM scorecard (`97069eb`), because
 * three components sharing only `ModalBackdrop` had nothing to propagate the
 * affordance through. That is what this primitive is for.
 *
 * Sticky is what makes it worth having: the close X stays on screen while the
 * body scrolls, so a long modal is never a trap.
 *
 * @param {Object}   props.theme       - `th`
 * @param {function} [props.onClose]   - renders the close X; omit for a modal
 *   whose own children carry the trailing controls
 * @param {string}   [props.closeLabel] - overrides the context-translated
 *   `common.close` (only needed inside a fixed-language subtree, e.g. TV)
 * @param {string}   [props.accentStrip] - colour of the 5px top stripe
 * @param {"center"|"start"} [props.align="center"]
 * @param {number}   [props.gap=12]
 * @param {number}   [props.radius=MODAL_RADIUS] - match the card's radius
 * @param {string}   [props.padding="12px 16px"]
 * @param {Object}   [props.style]
 */
export function ModalHeader({ children, onClose, closeLabel, accentStrip, align = "center", gap = 12, radius = MODAL_RADIUS, padding = "12px 16px", style }) {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 1,
      background: th.card, borderBottom: "1px solid " + th.border,
      borderTopLeftRadius: radius, borderTopRightRadius: radius,
      ...style,
    }}>
      {accentStrip ? <div style={{ height: 5, background: accentStrip, borderTopLeftRadius: radius, borderTopRightRadius: radius }} /> : null}
      <div style={{ display: "flex", alignItems: align === "start" ? "flex-start" : "center", gap, padding }}>
        {children}
        {onClose ? <IconBtn icon={IconClose} onClick={onClose} ariaLabel={closeLabel || t('common.close')} tone="muted" /> : null}
      </div>
    </div>
  );
}

/**
 * ModalScrollBody — the scrolling part of a modal, with the two things a
 * scrollport needs and half of them were missing: the slim `sendly-modal-scroll`
 * bar (10 of 36 had it, so the same modal type shipped with two different
 * chromes) and the bottom fade that says "there is more below" (12 modal bodies
 * had none — on mobile the overlay scrollbar auto-hides, so without it nothing
 * on screen says the content continues).
 *
 * Pass `padding` rather than putting it in `style`. `ScrollFadeOverlay` is
 * `position: sticky; bottom: 0` and a sticky element cannot leave its parent's
 * CONTENT box, so a scrollport's own bottom padding parks the gradient that far
 * above the real bottom edge and the padding reads as a solid strip (`77284d0`,
 * measured at 16px). Here the bottom breathing room is a spacer INSIDE the
 * scrollport instead, which the fade is free to overlay.
 *
 * With a sticky `<ModalActionBar>` at the bottom, pass `fade={false}`: the bar's
 * own `borderTop` is the affordance, and a gradient in the same place would sit
 * on the Save button (`f8851ce`).
 *
 * @param {Object}   props.theme        - `th`; supplies the default fade colour
 * @param {string}   [props.fadeColor]  - gradient end colour (default `th.card`)
 * @param {boolean}  [props.fade=true]  - false when a ModalActionBar is present
 * @param {string|number} [props.maxHeight] - e.g. "85vh"
 * @param {number}   [props.padding=0]  - applied top/left/right + bottom spacer
 * @param {number}   [props.radius=0]   - bottom corner radius when this element
 *   IS the card (so the gradient doesn't square off the rounded corners)
 * @param {number}   [props.fadeHeight=56]
 * @param {Object}   [props.scrollRef]  - when the caller also needs the node
 * @param {string}   [props.className]
 * @param {Object}   [props.style]
 */
export function ModalScrollBody({ children, fadeColor, fade = true, maxHeight, padding = 0, radius = 0, fadeHeight = 56, scrollRef, className, style, ...rest }) {
  const { th } = useAppTheme();
  const innerRef = useRef(null);
  const ref = scrollRef || innerRef;
  const intensity = useScrollFadeBottom(ref);
  return (
    <div
      ref={ref}
      className={className ? `sendly-modal-scroll ${className}` : "sendly-modal-scroll"}
      style={{
        overflow: "auto",
        // `none`, not `contain`: on iOS a scrollport rubber-bands at its own
        // ends, so pulling down inside a modal slid the whole body away from
        // the card's top edge and left a white gap where the header had been.
        // `contain` only stops the scroll from chaining to the page (which the
        // ScrollLock already prevents); `none` also drops the bounce, which is
        // the part that reads as the modal coming apart. Ignored on iOS < 16,
        // where the behaviour is simply what it is today.
        overscrollBehavior: "none",
        ...(maxHeight != null ? { maxHeight } : {}),
        ...(padding ? { padding: `${padding}px ${padding}px 0` } : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
      {padding ? <div aria-hidden="true" style={{ height: padding }} /> : null}
      {fade ? <ScrollFadeOverlay visible={intensity} color={fadeColor || th?.card} height={fadeHeight} borderRadius={radius} /> : null}
    </div>
  );
}

/**
 * ModalActionBar — the sticky footer holding a modal's primary actions. Its
 * `borderTop` doubles as the "more below" affordance, which is why the modal's
 * `<ModalScrollBody>` takes `fade={false}` when this is present.
 *
 * Render it as the LAST child of the scroll body, not as a sibling: sticky
 * positions against the scrollport, so outside it the bar would simply sit at
 * the end of the content.
 *
 * @param {Object} props.theme        - `th`
 * @param {number} [props.radius=0]   - bottom corner radius of the card
 * @param {string|number} [props.padding="10px 0"]
 * @param {string} [props.justify="flex-end"]
 * @param {number} [props.gap=8]
 * @param {Object} [props.style]
 */
export function ModalActionBar({ children, radius = 0, padding = "10px 0", justify = "flex-end", gap = 8, style }) {
  const { th } = useAppTheme();
  return (
    <div style={{
      position: "sticky", bottom: 0, zIndex: 1,
      display: "flex", justifyContent: justify, gap, alignItems: "center",
      background: th.card, borderTop: "1px solid " + th.border,
      padding,
      borderBottomLeftRadius: radius, borderBottomRightRadius: radius,
      ...style,
    }}>
      {children}
    </div>
  );
}

/**
 * Primary action button with optional danger/full/small variants.
 *
 * `style` is for LAYOUT only — `flex: 1`, a margin, a `minWidth` — never for
 * the padding, radius or colours, which are what make it the one primary
 * button. `type` passes through untouched (a form's submit button says so);
 * `title` / `ariaLabel` name a button whose label is an icon or a number.
 * `designSystemGates.test.js` (R4) fails on any hand-drawn accent-filled
 * white-text `<button>` — 23 of them used to sit next to this component.
 */
export function GreenBtn({ onClick, children, full, small, danger, disabled, accent, style, type, title, ariaLabel, id }) {
  // The `accent` prop is a SEMANTIC override — the grey "Bezárás", the amber
  // finals controls, the violet series link — and nine call sites use it that
  // way. The other 62 pass nothing and mean "the gym's colour", which is what
  // this reads from the provider.
  //
  // It used to arrive as `accent={accent}` from every caller. When the theme
  // moved into the tree those props were removed, but this component still
  // only looked at its own; `makeB(undefined)` then produced
  // `background: undefined`, and every ordinary button in the app rendered as
  // the browser's default grey. That is the failure this fallback closes.
  const { accent: themeAccent } = useAppTheme();
  const b = makeB(accent || themeAccent || DEFAULT_ACCENT);
  return (
    <button id={id} type={type} title={title} aria-label={ariaLabel} disabled={disabled} onClick={disabled ? undefined : onClick} style={{
      width: full ? "100%" : "auto",
      padding: small ? "8px 18px" : "14px 28px",
      borderRadius: 14, border: "none", fontWeight: 700,
      fontSize: small ? 13 : 15,
      background: disabled ? "#ccc" : danger ? b.danger : b.bg,
      color: "#fff", cursor: disabled ? "default" : "pointer",
      fontFamily: F, transition: "all 0.15s",
      ...style,
    }}>{children}</button>
  );
}

/**
 * SecondaryBtn — the outlined partner of GreenBtn: the "Mégse" / "Vissza"
 * beside a primary. It draws exactly GreenBtn's box (padding, radius, type
 * size, weight, `border: none`) so the two always line up; the frame is an
 * inset shadow because a real 1 px border would make it 2 px taller than its
 * neighbour. Hand-drawn cancel buttons, each with its own BTN_PAD and radius,
 * left one Spraywall footer with three button sizes in a row.
 * `style` is for layout only (flex, margin), like GreenBtn's.
 */
export function SecondaryBtn({ onClick, children, small, disabled, style, type = "button", title, ariaLabel }) {
  const { th } = useAppTheme();
  return (
    <button type={type} title={title} aria-label={ariaLabel} disabled={disabled} onClick={disabled ? undefined : onClick} style={{
      padding: small ? "8px 18px" : "14px 28px",
      borderRadius: 14, border: "none", fontWeight: 700,
      fontSize: small ? 13 : 15,
      background: "transparent", color: th.text,
      boxShadow: "inset 0 0 0 1px " + th.border,
      cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1,
      fontFamily: F, transition: "all 0.15s", whiteSpace: "nowrap", flexShrink: 0,
      ...style,
    }}>{children}</button>
  );
}

/**
 * Card — the plain rounded container, themed by DEFAULT: give it `theme` and
 * the surface and the neutral border come from there.
 *
 * The literal fallbacks are the pre-theme values and exist only so a caller
 * without a theme still renders something. They are not a design choice: on a
 * dark theme a Card that forgot its `theme` is a white rectangle — which is
 * exactly what 34 of 35 call sites were working around, each with its own
 * hand-written `background: th.card` in `style`.
 *
 * A SEMANTIC border still belongs at the call site: the accent ring on an
 * active competition, the dashes on a draft, the amber edge on a finals
 * warning. Only the neutral 1px border is the default.
 */
/**
 * The props that make a non-button element (a card, a list row) a real button: the
 * `button` role — so it is announced, and found by its text as an accessible name —
 * a tab stop, and Enter / Space like a native button. A key press inside a nested
 * control (an icon button on the row) is that control's, not the row's.
 */
export function pressable(onClick) {
  return {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); }
    },
  };
}

/**
 * The card surface. With `onClick` the whole card is a button (`pressable`); its
 * text is the accessible name. `role` / `ariaLabel` name a non-clickable card as a
 * landmark-like unit (`CardShell`: an `article` named by its title).
 */
export function Card({ children, style, onClick, id, role, ariaLabel }) {
  const { th } = useAppTheme();
  return (
    <div id={id} {...(onClick ? pressable(onClick) : { role })} aria-label={ariaLabel} style={{
      background: th?.card || "#fff",
      borderRadius: 14,
      border: "1px solid " + (th?.border || "#e0e0e0"),
      ...style,
    }}>{children}</div>
  );
}

/**
 * StarRow — the single renderer of a 1–5 star row, interactive or display-only.
 *
 * Interactive (an `onClick` is given): a `group` named `groupLabel` of five
 * buttons ("4/5 csillag") with a generous tap zone. Display-only (no
 * `onClick`): compact, not focusable, and ONE image to assistive technology —
 * "Az út értékelése: 4/5" — whose stars are hidden from it (five "buttons" that
 * do nothing would be read out, and tabbed past, as controls). The row keeps
 * no selection state — what a tap on the already selected star means (clear,
 * delete) is the caller's decision. `color` switches from the emoji glyph to
 * a text `★` in that colour. The labels default to the `rating.*` i18n keys; a
 * caller may pass its own `groupLabel` / `starLabel`.
 */
export function StarRow({ value, onClick, size = 28, color, groupLabel, starLabel }) {
  const { t } = useTranslation();
  const [hover, setHover] = useState(0);
  const isInteractive = !!onClick;
  const group = groupLabel || t("rating.group");
  const labelOf = starLabel || ((i) => t("rating.star", { count: i }));
  const rowProps = isInteractive
    ? { role: "group", "aria-label": group }
    : { role: "img", "aria-label": t("rating.display", { label: group, value: value || 0 }) };
  return (
    <div {...rowProps} style={{ display: "flex", gap: 2 }} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i}
          {...(isInteractive ? { role: "button", tabIndex: 0, "aria-label": labelOf(i) } : { "aria-hidden": true })}
          onClick={() => { if (onClick) onClick(i); setHover(0); }}
          onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(i); setHover(0); } } : undefined}
          onMouseEnter={isInteractive ? () => setHover(i) : undefined}
          style={{
            cursor: isInteractive ? "pointer" : "default",
            fontSize: size,
            opacity: i <= (hover || value) ? 1 : 0.25,
            transition: "opacity 0.15s",
            lineHeight: 1,
            padding: isInteractive ? "6px 4px" : 0,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            userSelect: "none",
            ...(color ? { color } : null),
          }}>
          {color ? "★" : "⭐"}
        </span>
      ))}
    </div>
  );
}

/**
 * WarnNote — the amber inline notice for a non-blocking caution the user
 * should read before acting: a retroactive side effect, a missing
 * prerequisite, import warnings. The amber is a semantic colour, not a
 * surface, so it deliberately does NOT follow the theme — it reads the same
 * on light and dark. Not for errors (red) or success (green), and not for
 * buttons: the paused-competition button shares the palette but is a control.
 * `children` may be plain text or a title + rows; `style` carries per-surface
 * trim (margins, a wider padding).
 */
export function WarnNote({ children, style, icon = "⚠", role = "note" }) {
  return (
    <div role={role} style={{
      padding: 12, marginBottom: 10, borderRadius: 12,
      background: AMBER.bg, border: "1px solid " + AMBER.border, color: AMBER.ink,
      fontSize: 12, fontWeight: 600, lineHeight: 1.45, fontFamily: F,
      ...style,
    }}>
      {icon ? <span aria-hidden="true" style={{ marginRight: 6 }}>{icon}</span> : null}
      {children}
    </div>
  );
}

/** The aspect ratio every cover image is authored at — 1200×400, the size the
 *  upload hints advertise (`comp.editor.coverHint`, `events.editor.coverHint`,
 *  `admin.coverHint`). */
export const COVER_ASPECT = "3 / 1";

/**
 * Cover banner for a competition, series, event or gym.
 *
 * Every surface used to render covers into a fixed-height box (120–160 px) at
 * full width, which only matches 3:1 around 400 px wide. Wider than that, a
 * correctly sized 1200×400 upload got center-cropped — on the admin editor's
 * ~900 px pane it lost nearly half its height, top and bottom, which is exactly
 * what the hint promised would not happen.
 *
 * Locking the BOX to the ratio we ask for instead of to a pixel height means a
 * correct upload is never cropped at any width, and an off-ratio one crops the
 * same way everywhere rather than differently on each surface.
 *
 * `style` carries the per-surface trim (corner radius, margins, border).
 * `onError` — optional, the load-failure handler of the `<img>`.
 */
export function CoverImage({ src, alt = "", style, loading, onError }) {
  if (!src) return null;
  return (
    <img src={src} alt={alt} loading={loading} onError={onError} style={{
      width: "100%", aspectRatio: COVER_ASPECT, objectFit: "cover",
      display: "block", borderRadius: 8, ...style,
    }} />
  );
}

/**
 * The cover image inside an editor: the `CoverImage` box capped at 480 px (a
 * thumbnail on a wide admin pane, not a banner mid-form), with a delete button
 * in its top-right corner when `onRemove` is given. Without `onRemove` it is
 * read-only (a closed competition). One copy for the gym, competition, series
 * and event editors, so a cover is deleted the same way everywhere — the
 * competition editor used to show a ✕, which the icon semantics reserve for
 * dismissing and clearing, while deleting an image is `IconTrash`.
 */
export function CoverPreview({ src, onRemove, alt = "" }) {
  const { t } = useTranslation();
  if (!src) return null;
  return (
    <div style={{ position: "relative", marginBottom: 8, maxWidth: 480 }}>
      <CoverImage src={src} alt={alt} style={{ borderRadius: 10 }} />
      {onRemove && (
        <IconBtn icon={IconTrash} size={14} padding={0} onClick={onRemove} title={t("common.delete")} style={{
          position: "absolute", top: 6, right: 6, width: 28, height: 28, borderRadius: "50%",
          background: "rgba(0,0,0,0.55)", color: "#fff",
        }} />
      )}
    </div>
  );
}

/** Small green "climbed" check badge — mirrors the map marker's climbed
 *  indicator (circle + white tick). Rendered on a climber's avatar so a
 *  climber is visually distinct from someone who only left a rating. */
function ClimbedBadge() {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  return (
    <span
      title={t('route.climbedBadge')} aria-label={t('route.climbedBadge')}
      style={{
        position: "absolute", top: -2, right: -2, width: 15, height: 15,
        borderRadius: "50%", background: "#16a34a", border: "2px solid " + th.card,
        display: "flex", alignItems: "center", justifyContent: "center", boxSizing: "border-box",
      }}
    >
      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    </span>
  );
}

/**
 * RouteClimberList — unified "who climbed + who rated" list for a single route.
 * One row per person: avatar + name + date on the left, their rating stars +
 * flash badge on the right, and their comment indented under the name. A
 * climber's avatar carries a small green check (ClimbedBadge) so climbers are
 * visually distinct from people who only left a rating — raters who never
 * logged an ascent are appended as dateless, checkless rows so no comment is
 * lost. Rows become clickable (→ profile modal) when `onSelectClimber` is
 * provided (cap-gated by the caller).
 *
 * Used by RoutesTab cards and the RouteModal "Statisztikák" section.
 *
 * @param {Object} props
 * @param {Array} props.climberAscents     - ascents for one route, sorted recent-first
 * @param {Map}   props.userByUid          - uid → { name, photoURL } (built once by caller)
 * @param {Array} props.ratings            - rating docs for one route (userId, stars, comment)
 * @param {Function} [props.onSelectClimber] - if provided, rows become clickable buttons
 * @param {Object} props.theme - resolved theme tokens
 * @param {Function} props.t   - i18n
 * @param {number} [props.maxVisible=10]
 */
/** Inline-style helper that opts a data display back into text selection.
 *
 * The app default (set in index.html on body) is `user-select: none`
 * for chrome — labels, buttons, expander headers, filter pills, etc. Spread
 * this object on **data** spans so the value can still be copy-pasted:
 * emails, phones, names (person/gym/comp/event/route/setter), descriptions,
 * rules, comments, addresses, URLs, slugs, start numbers, audit reasons.
 *
 * Decide on EVERY new UI element: is this chrome (skip) or data (use this)?
 * See CLAUDE.md → "Text Selection Policy".
 *
 * Declared above its first consumer (RouteClimberList) so the module reads
 * top-down without a use-before-define. */
export const SELECTABLE = {
  userSelect: "text",
  WebkitUserSelect: "text",
  WebkitTouchCallout: "default",
};

/**
 * Hidden on screen, still read by assistive technology: a table `caption` that
 * names a table whose visible title is the surrounding group's (the platform
 * statistics tables), so the title is not shown twice.
 */
export const VISUALLY_HIDDEN = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1,
  overflow: "hidden", clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0,
};

/**
 * UserRow — the app's standard "a person in a list" card.
 *
 * ONE look for every roster: competition participants / judges / organizers /
 * entry-fee payers, event participants / coaches / organizers / waitlist. A
 * 34px avatar (photo, or the monogram circle when there is none), the name on
 * the first line, a muted second line under it (e-mail, and the group where
 * the group is not already shown on the row itself), and whatever controls
 * that surface needs on the right.
 *
 * The rules that are NOT negotiable per caller, and why they live here:
 *
 * - The name owns the width. Anything beside it that sets `flexShrink: 0`
 *   wins over the name when space runs out, and on a phone that ended with
 *   rows rendered as a badge next to a group name with no person in them.
 *   Secondary identifiers go on the SECOND line, never beside the name.
 * - `trailing` wraps as one block. The layout classes give the identity a
 *   240px flex basis, so the controls drop to their own row rather than
 *   squeezing the name — container-driven, no media query (see
 *   `.sendly-user-card-*` in index.html).
 * - Name and secondary are SELECTABLE. They are data — an organiser copying
 *   an address out of a roster is a real thing people do.
 *
 * Guests (competition/event guest competitors) have no account, hence no
 * photo and no e-mail: they land on the monogram circle and a shorter second
 * line, with no special-casing needed at the call site.
 *
 * @param {string}  props.name          Display name. Also feeds the monogram.
 * @param {string}  [props.photoURL]    Public profile photo; monogram if absent.
 * @param {node}    [props.secondary]   Second line — e-mail, group, "· "-joined.
 * @param {node}    [props.badges]      Rendered BEFORE the name (guest, role).
 * @param {node}    [props.nameSuffix]  Rendered AFTER the name (start number).
 * @param {node}    [props.trailing]    Right-hand controls; wraps as one block.
 * @param {boolean} [props.trailingBelow] Put `trailing` under `children`
 *   instead of beside the name. For rows with sub-items (a payer's guests):
 *   wrapped, the controls otherwise land between the person and their own
 *   sub-rows and read as a divider between them.
 * @param {node}    [props.children]    Extra content under the identity row.
 * @param {object}  props.th            Theme palette.
 * @param {object}  [props.style]       Container override (rare).
 */
/**
 * Avatar — the round photo-or-monogram every people list draws. Seven lists
 * hand-rolled it at three sizes with two colour schemes; the shape is one:
 * a photo cropped to a circle, else the initials on a tinted disc.
 *
 * @param {string} [photoURL]
 * @param {string} [name]  initials source
 * @param {number} [size=34]
 * @param {string} [bg]  disc colour (default the theme's light border)
 * @param {string} [color]  initials colour
 */
export function Avatar({ photoURL, name, size = 34, bg, color, style }) {
  const { th } = useAppTheme();
  const dim = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (photoURL) return <img src={photoURL} alt="" style={{ ...dim, objectFit: "cover", ...style }} />;
  return (
    <div style={{
      ...dim, background: bg || th.borderLight, color: color || th.textMuted,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size <= 24 ? 10 : size >= 36 ? 13 : 12, fontWeight: 700, ...style,
    }}>{initials(name)}</div>
  );
}

export function UserRow({
  name, photoURL, secondary, badges, nameSuffix, trailing, trailingBelow,
  children, style,
}) {
  const { th } = useAppTheme();
  const controls = trailing ? (
    <div className="sendly-user-card-trailing" style={trailingBelow ? { marginTop: 6 } : undefined}>
      {trailing}
    </div>
  ) : null;
  // A named group: every row of a list carries the same controls (the waiting
  // list's „Előléptetés", a participant's pencil), and the person is what tells
  // one row's from the next — for a screen reader as much as for a test.
  return (
    <div role="group" aria-label={typeof name === "string" ? name : undefined} style={{
      padding: 10, marginBottom: 8, background: th.bg, borderRadius: 12,
      border: "1px solid " + th.borderLight, ...style,
    }}>
      <div className="sendly-user-card-row">
        <div className="sendly-user-card-identity">
          <Avatar photoURL={photoURL} name={name} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 13, fontWeight: 600, color: th.text,
              display: "flex", alignItems: "center", gap: 6, minWidth: 0,
            }}>
              {badges}
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...SELECTABLE }}>{name}</span>
              {nameSuffix}
            </div>
            {secondary && (
              <div style={{
                fontSize: 12, color: th.textMuted,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", ...SELECTABLE,
              }}>{secondary}</div>
            )}
          </div>
        </div>
        {!trailingBelow && controls}
      </div>
      {children}
      {trailingBelow && controls}
    </div>
  );
}

/** Join the bits of a UserRow's second line, dropping the ones a given person
 *  does not have. A guest with no account and no group simply gets no line. */
export function userRowSecondary(...parts) {
  const line = parts.filter(Boolean).join(" · ");
  return line || null;
}

/**
 * The violet palette of the "Nevezés" section on the competition and event
 * cards — the one block on those cards that is deliberately NOT theme-neutral,
 * because the colour is what separates "what YOU have to do" from the grey
 * admin block below it.
 *
 * It used to be five hard-coded light-theme hexes shared by both cards. On a
 * dark card that put a near-white lavender panel (`#EEEDFE`) against `#1e1e1e`
 * — the section glowed, and its near-black body text was unreadable on it.
 *
 * Dark mode keeps the hue and inverts the relationship: a translucent violet
 * wash over whatever the card already is (so it works on `card`, on `bg`, and
 * inside a modal without a second set of values), with light violet text. The
 * filled button keeps a solid fill in both themes — white-on-violet is legible
 * either way, and a translucent CTA would read as disabled.
 *
 * `isDark` is detected the same way `Segmented` does it: the dark theme's
 * `text` token is `#f0f0f0`.
 */
export function getRegPalette(th) {
  const isDark = (th?.text || "").toLowerCase() === "#f0f0f0";
  if (!isDark) {
    return {
      surface: "#EEEDFE", border: "#CECBF6", outline: "#AFA9EC",
      dot: "#534AB7", heading: "#3C3489", body: "#26215C",
      chipBg: "#CECBF6", chipFg: "#3C3489",
      btnBg: "#534AB7", btnFg: "#ffffff", link: "#3C3489",
      dangerLink: "#712B13", feeUnpaid: "#A32D2D",
      // NOT the `#22c55e` / `#f59e0b` used for status text elsewhere in the
      // app: those are tuned for a white or grey background and only reach
      // 1.97:1 / 1.86:1 on this lavender surface — colour you can see, text you
      // cannot read. The darker steps clear AA at 12px (6.17:1 / 6.14:1) and
      // still read plainly as green and amber. `#92400e` is the app's existing
      // dark-amber text token; the green goes one Tailwind step past the
      // existing `#15803d`, which lands at 4.34:1 here — just short.
      feePaid: "#166534", feePartial: "#92400e",
    };
  }
  return {
    surface: "rgba(99,89,201,0.18)", border: "rgba(129,120,224,0.34)",
    outline: "rgba(139,130,232,0.55)",
    // `dot` also colours the start number, which is 12px text — so it has to
    // clear 4.5:1 against the blended surface, not just look like a dot.
    dot: "#9A92F0", heading: "#B9B2F0", body: "#E4E1FA",
    chipBg: "rgba(129,120,224,0.24)", chipFg: "#C7C1F5",
    btnBg: "#6C63D9", btnFg: "#ffffff", link: "#B9B2F0",
    // The two reds are the reason this section needs status colours of its own.
    // A dark red on a dark violet wash is not "a bit hard to read" — the
    // brick `#712B13` measured 1.39:1 on the blended surface and `#A32D2D`
    // 2.00:1, i.e. barely distinguishable from the background. Both are
    // lightened while keeping their hue (brick stays warm, unpaid stays true
    // red) so the two still read as different kinds of warning.
    dangerLink: "#F0A090", feeUnpaid: "#F49696",
    // The app-wide `#22c55e` / `#f59e0b` DO work here (6.21:1 / 6.59:1) —
    // a mid-tone reads well on a dark wash and badly on a light one, which is
    // why the light branch needs its own darker pair rather than sharing these.
    feePaid: "#22c55e", feePartial: "#f59e0b",
  };
}

/**
 * Ink for a session's occupancy line — "Megtelt" / "3 szabad hely" — wherever
 * one is printed: the competition card's structure box and the public register
 * landing both draw the same row, and this is the one thing on it that is not
 * a plain theme token.
 *
 * Two tones only. `full` and `low` are the states worth shouting about;
 * anything roomier stays `th.textMuted`, which the caller supplies.
 *
 * The light pair cannot carry over to dark: `#dc2626` on `#1e1e1e` measures
 * 3.44:1 and `#b45309` 3.32:1, and this is 12px bold status text — the
 * difference between "urgent" and "unreadable". The dark pair keeps both hues
 * and lightens them (6.0:1 / 7.7:1 on `card`, more on `bg`); the amber is the
 * same `#f59e0b` getRegPalette uses on its dark wash, for the same reason.
 *
 * `isDark` is sniffed from the theme's `text` token, exactly as `Segmented`
 * and `getRegPalette` do it.
 */
function getOccupancyTones(th) {
  const isDark = (th?.text || "").toLowerCase() === "#f0f0f0";
  return isDark
    ? { full: "#f87171", low: "#f59e0b" }
    : { full: "#dc2626", low: "#b45309" };
}

/**
 * Resolve a tone NAME from `getSessionOccupancy` into an actual colour.
 * The rule returns names so it can stay theme-free and testable; this is the
 * one place that turns a name into ink, so the three surfaces that print an
 * occupancy line cannot drift apart on colour.
 */
export function occupancyToneColor(th, tone) {
  const occ = getOccupancyTones(th);
  return tone === "full" ? occ.full : tone === "low" ? occ.low : th.textMuted;
}

/** People icon — a HEADCOUNT figure. Never an action on a person (that is
 *  `IconUserMinus`), and never a person's identity. */
export function IconUsers({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/**
 * A session's HEAD-COUNT CAP, drawn as icon + number: `👥 30`.
 *
 * Belongs to the session's descriptor (next to its name and time), NOT to the
 * occupancy status on the right — those are different kinds of fact. The cap
 * is set once by the organizer and never moves; the occupancy changes by the
 * minute. While they shared the one right-hand slot, "Megtelt" and "4 szabad
 * hely" kept overwriting the cap, which is exactly when a climber most wants
 * to know how big the session was.
 *
 * Icon rather than the words "max. 30 fő": the text form measures ~62px, and
 * at 375px that broke the session row onto a second line on every card. The
 * icon carries the "people" sense in ~26px, and — being a glyph — needs no
 * translation. The words survive as the accessible name.
 */
export function SessionCapacity({ max, separator = true, style }) {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  if (!(max > 0)) return null;
  const label = t("calendar.modal.cap", { n: max });
  return (
    // `role="img"` + `aria-label` rather than a bare label on a generic span:
    // the visible content is only the digits, so without the role a screen
    // reader announces "30" with nothing to say what 30 is. (The role also
    // keeps the leading separator out of the announcement.)
    <span
      role="img"
      title={label}
      aria-label={label}
      style={{
        // The separator brings its own spacing; without one the group still
        // needs to stand off whatever precedes it.
        marginLeft: separator ? 0 : 6,
        // Muted, normal weight — deliberately the SAME treatment the start
        // time gets. The capacity is a descriptor fact of exactly that class:
        // something the organizer set once, useful when deciding, not more
        // important than when the session starts. Bolding it (at any level)
        // gave it emphasis the time does not have, and on the wrapper the
        // separator inherited it too, so the row showed two visibly different
        // "·" and read as two different characters.
        color: th.textMuted, whiteSpace: "nowrap", ...style,
      }}
    >
      {/* The row reads "{name} · {time} · 👥{max}" — the capacity is a third
          item in the session's descriptor, so it takes the same separator the
          first two use. `separator={false}` is for the one caller that can
          have nothing in front of it (the registration form's sub-line on a
          session with neither start time nor duration), where a leading dot
          would be pointing at nothing. */}
      {separator ? " · " : null}
      {/* Ordinary inline flow, NOT inline-flex. An inline-flex box takes its
          baseline from its FIRST FLEX ITEM — here the icon, whose baseline is
          its bottom edge — so the whole group, digits included, rode 1px above
          the session name's baseline (measured). As plain inline content the
          digits are ordinary text and land on the shared baseline by
          themselves, leaving only the glyph to position.
          -0.15em drops the 12px icon 1.8px below the baseline, putting its
          centre on the centre of the 12px text's cap-height band, which is
          where the eye reads it as aligned. */}
      <span style={{ display: "inline-block", verticalAlign: "-0.15em", marginRight: 4 }}>
        <IconUsers />
      </span>
      {max}
    </span>
  );
}

/**
 * The colour swatch for any gym-picked colour — competition / event group,
 * difficulty level, hold colour — everywhere one is shown.
 *
 * Filled with the group's true colour plus a hairline ring in that colour's
 * readable ink (see `groupTone`). The ring is drawn on EVERY swatch, but it is
 * only visible on the ones that need it: for a colour that already contrasts
 * with the surface the ink IS the colour, so the ring melts into the fill.
 * That is what keeps a black group visible on the dark theme and a white one
 * on the light theme without turning either into grey.
 *
 * `surface` is the background the dot sits on — pass the tinted chip colour if
 * the dot is inside one, otherwise the card/panel background.
 */
export function ColorDot({ color, size = 10, surface = "#ffffff", title, style }) {
  const tone = groupTone(color, surface);
  return (
    <span aria-hidden="true" title={title} style={{
      display: "inline-block", width: size, height: size, borderRadius: 999,
      background: tone.fill, flexShrink: 0,
      boxShadow: "0 0 0 1px " + tone.ring,
      ...style,
    }} />
  );
}

/**
 * RouteDot — the two-colour swatch that says WHICH route this row is about: the
 * hold colour is the disc, the difficulty is the pip inside it, and the
 * difficulty's ink is the ring around both. Six competition surfaces wrote it
 * byte-identically and differed only in diameter.
 *
 * The pip is exactly half the diameter everywhere. Every hand-written copy chose
 * that ratio independently (20/10, 28/14, 32/16, 40/20, 56/28), so it is the
 * shape, not a parameter.
 *
 * A route can be ungraded, and a route can have no hold colour — both are
 * ordinary states, not errors. With no grade the pip becomes a `?`; with
 * neither the disc is hatched in the accent instead of being filled with a
 * colour the route does not have. `diff` is still the placeholder object in
 * that case (that is where the grey ring comes from), which is why the state is
 * carried by `graded` rather than by passing `diff={null}`.
 *
 * Not `ColorDot`: that one is a single gym-picked colour with a contrast ring
 * (see `groupTone`), used for group and hold swatches. This is two colours and
 * has no contrast maths — the gym's own difficulty border IS the ring.
 *
 * @param {Object}  [props.hold]     - `{ color }`, or null when the route has none
 * @param {Object}  props.diff       - `{ color, border }`; the placeholder object
 *   when the route is ungraded
 * @param {boolean} [props.graded=true]
 * @param {number}  [props.size=32]  - outer diameter
 * @param {number}  [props.ring=2]   - outer border width
 * @param {number}  [props.pipRing=0] - border width around the pip
 * @param {string}  [props.accent]   - only used by the two unknown states
 * @param {Object}  [props.style]
 */
export function RouteDot({ hold, diff, graded = true, size = 32, ring = 2, pipRing = 0, style }) {
  const { accent } = useAppTheme();
  const hasHold = !!hold?.color;
  const ink = (hasHold || graded) ? (diff?.border || diff?.color || accent) : accent;
  const fill = hasHold ? hold.color
    : graded ? diff?.color
      : `repeating-linear-gradient(45deg, transparent, transparent 4px, ${accent}33 4px, ${accent}33 6px)`;
  return (
    <div aria-hidden="true" style={{
      width: size, height: size, borderRadius: "50%", background: fill,
      ...(ring ? { border: ring + "px solid " + ink } : {}),
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      ...style,
    }}>
      {graded ? (
        <div style={{
          width: size / 2, height: size / 2, borderRadius: "50%", background: diff?.color,
          ...(pipRing ? { border: pipRing + "px solid " + (diff?.border || diff?.color) } : {}),
        }} />
      ) : (
        <span style={{ fontSize: Math.round(size * 0.32), fontWeight: 700, color: hasHold ? "#fff" : accent }}>?</span>
      )}
    </div>
  );
}

/**
 * LegendDot — one entry of a chart legend: the mark, then what it means.
 *
 * A legend's only job is to be recognisable as the thing the chart draws, so
 * the MARK stays a prop while the geometry does not. Three surfaces wrote this
 * by hand and each picked its own: the scorecard matrix a 13px dot at gap 6
 * with a 2.5px inset ring, the session timeline a 12px dot at gap 4 with a 2px
 * border, the occupancy heatmap a 10px dot at gap 4. Two of those numbers are
 * off the spacing scale, and none of the three legends sat next to another one
 * — which is exactly why nobody saw them diverge.
 *
 * `color` is the disc fill and `ring` is the ink around it; they compose, and
 * that composition is what fixed a real mismatch. The matrix's zone CELL is a
 * white disc with an amber ring, but its legend dot was drawn transparent, so
 * on the dark theme the legend showed the card through while the cell it
 * described stayed white. Passing `color="#fff" ring={C_ZONE}` states the
 * actual cell.
 *
 * With a `ring` and no `color` the disc falls back to `th.borderLight` — the
 * neutral stand-in for "whatever colour that route happens to be", which is
 * what the timeline's markers are filled with. Transparent would claim the
 * marks are hollow; they are not.
 *
 * Not `ColorDot`: that one is a gym-picked colour with a contrast ring, sized
 * for inline use next to a name. This is a chart key — the label is part of it.
 */
export function LegendDot({ color, ring, dashed = false, label, size = 12, style }) {
  const { th } = useAppTheme();
  const fill = color || (ring ? th.borderLight : "transparent");
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span aria-hidden="true" style={{
        width: size, height: size, borderRadius: "50%", flexShrink: 0,
        background: fill,
        ...(ring ? { border: `2px ${dashed ? "dashed" : "solid"} ${ring}` } : {}),
        ...style,
      }} />
      <span>{label}</span>
    </span>
  );
}

const EDGE_DRAG_THRESHOLD = 4;

/**
 * The scrolling half of a horizontally scrolling row with edge arrows: which
 * edge has more row beyond it, paging by the visible width, and mouse drag
 * that scrolls like a finger (a drag never ends in a tap of what lies under
 * the pointer). Put `ref` on the scroller and spread `handlers` onto it; draw
 * a `ScrollEdgeArrow` per side while `canLeft` / `canRight` hold. `onSync`
 * (optional) runs after every re-check with the scroller, for a caller that
 * tracks more than the two flags. Users: `LogDayChart`, `LogOpinion` (scroll mode).
 */
export function useEdgeScroll({ onSync } = {}) {
  const ref = useRef(null);
  const drag = useRef(null);
  const swallowClick = useRef(false);
  const onSyncRef = useRef(onSync);
  onSyncRef.current = onSync;
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < max - 1);
    if (typeof onSyncRef.current === "function") onSyncRef.current(el);
  }, []);

  // The width can change under us (rotation, a modal resizing).
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(sync);
      observer.observe(el);
    }
    window.addEventListener("resize", sync);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [sync]);

  const scrollTo = useCallback((left, smooth = true) => {
    const el = ref.current;
    if (!el) return;
    if (smooth && typeof el.scrollTo === "function") el.scrollTo({ left, behavior: "smooth" });
    else el.scrollLeft = left;
  }, []);

  const page = useCallback((direction) => {
    const el = ref.current;
    if (el) scrollTo(el.scrollLeft + direction * el.clientWidth);
  }, [scrollTo]);

  const handlers = {
    onScroll: sync,
    onPointerDown: (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0 || !ref.current) return;
      drag.current = { x: e.clientX, left: ref.current.scrollLeft, moved: false };
    },
    onPointerMove: (e) => {
      const d = drag.current;
      if (!d || !ref.current) return;
      const dx = e.clientX - d.x;
      if (!d.moved && Math.abs(dx) > EDGE_DRAG_THRESHOLD) d.moved = true;
      if (d.moved) ref.current.scrollLeft = d.left - dx;
    },
    onPointerUp: () => endDrag(),
    onPointerLeave: () => endDrag(),
    onPointerCancel: () => endDrag(),
    onClickCapture: (e) => {
      if (swallowClick.current) {
        e.stopPropagation();
        e.preventDefault();
        swallowClick.current = false;
      }
    },
  };
  function endDrag() {
    if (drag.current?.moved) {
      swallowClick.current = true;
      setTimeout(() => { swallowClick.current = false; }, 0);
    }
    drag.current = null;
  }

  return { ref, canLeft, canRight, sync, page, scrollTo, handlers };
}

/**
 * One edge arrow of a `useEdgeScroll` row: an absolutely positioned strip over
 * the edge (the row's wrapper is `position: relative`) that fades from the card
 * colour, so it never takes width from the row, with a muted `IconBtn`.
 * `offset` reaches out into the card padding so the icon clears the first item.
 */
export function ScrollEdgeArrow({ side, icon, label, onClick, height, offset = -12, width = 40, testId }) {
  const { th } = useAppTheme();
  return (
    <div data-testid={testId} style={{
      position: "absolute", top: 0, [side]: offset, width, height,
      display: "flex", alignItems: "center", justifyContent: side === "left" ? "flex-start" : "flex-end",
      pointerEvents: "none",
      background: "linear-gradient(to " + (side === "left" ? "right" : "left") + ", " + th.card + " 55%, transparent)",
    }}>
      <IconBtn icon={icon} tone="muted" size={16} padding={8} title={label} onClick={onClick}
        style={{ pointerEvents: "auto" }} />
    </div>
  );
}

export function RouteClimberList({ climberAscents, userByUid, ratings, onSelectClimber, maxVisible = 10 }) {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => {
    const ratingByUid = new Map();
    for (const r of ratings || []) if (r?.userId) ratingByUid.set(r.userId, r);
    const climberUids = new Set();
    const out = [];
    for (const a of climberAscents || []) {
      if (!a?.userId) continue;
      climberUids.add(a.userId);
      // `repeatCount` rides along on the ascent doc (maintained by the climb-log
      // trigger), so "how many times did THIS climber come back" costs nothing
      // extra to show — and it is the one number here that keeps moving after
      // everyone who can do the route has ticked it once.
      out.push({ key: a.id, userId: a.userId, climbed: true, date: a.date || null, flash: !!a.flash, repeats: Number(a.repeatCount) || 0, rating: ratingByUid.get(a.userId) || null });
    }
    // Raters who never logged an ascent — appended dateless + checkless so no
    // comment/rating is lost and they read clearly as "rated, didn't climb".
    for (const r of ratings || []) {
      if (!r?.userId || climberUids.has(r.userId)) continue;
      const hasContent = (r.stars > 0) || (r.comment && r.comment.trim());
      if (!hasContent) continue;
      out.push({ key: "r_" + (r.id || r.userId), userId: r.userId, climbed: false, date: null, flash: false, repeats: 0, rating: r });
    }
    return out;
  }, [climberAscents, ratings]);

  if (rows.length === 0) return null;
  const visible = expanded ? rows : rows.slice(0, maxVisible);
  const overflow = expanded ? 0 : Math.max(0, rows.length - maxVisible);
  const clickable = !!onSelectClimber;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {visible.map((row, i) => {
        const u = userByUid?.get(row.userId);
        const name = u?.name || "?";
        const photo = u?.photoURL || null;
        const stars = row.rating?.stars || 0;
        const comment = row.rating?.comment?.trim();
        const RowTag = clickable ? "button" : "div";
        return (
          <RowTag
            key={row.key}
            onClick={clickable ? () => onSelectClimber(row.userId) : undefined}
            aria-label={clickable ? name : undefined}
            style={{
              display: "flex", alignItems: comment ? "flex-start" : "center", gap: 10,
              padding: "9px 4px", width: "100%", textAlign: "left",
              background: "none", border: "none", fontFamily: F,
              borderBottom: i < visible.length - 1 ? "1px solid " + th.borderLight : "none",
              cursor: clickable ? "pointer" : "default",
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <div style={{
                width: 36, height: 36, borderRadius: "50%", overflow: "hidden",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: photo ? "transparent" : th.borderLight, color: th.textMuted,
                border: "1px solid " + th.border, fontSize: 13, fontWeight: 700,
              }}>
                {photo
                  ? <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }} />
                  : initials(name)}
              </div>
              {row.climbed && <ClimbedBadge />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: th.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", ...(clickable ? {} : SELECTABLE) }}>{name}</div>
              {row.date && (
                <div style={{ fontSize: 12, color: th.textMuted, marginTop: 1 }}>
                  {row.date}
                  {/* Only when they actually came back — on a route nobody has
                      repeated this would be a column of "🔁 0". */}
                  {row.repeats > 0 && <span style={{ marginLeft: 6 }}>🔁 {row.repeats}</span>}
                </div>
              )}
              {comment && <div style={{ fontSize: 12.5, color: th.textMuted, marginTop: 3, lineHeight: 1.4, ...SELECTABLE }}>„{comment}"</div>}
            </div>
            {(stars > 0 || row.flash) && (
              <div style={{ textAlign: "right", flexShrink: 0, whiteSpace: "nowrap" }}>
                {stars > 0 && <div style={{ color: "#eab308", fontSize: 12, letterSpacing: 1 }}>{"★".repeat(stars)}</div>}
                {row.flash && <div style={{ fontSize: 12, fontWeight: 800, color: "#2f8f2f", marginTop: stars > 0 ? 2 : 0 }}>{t('route.flashBadge')}</div>}
              </div>
            )}
          </RowTag>
        );
      })}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          title={t('route.climbersExpand', { count: overflow })}
          aria-label={t('route.climbersExpand', { count: overflow })}
          style={{
            width: "100%", marginTop: 8, padding: "6px 0",
            background: "none", color: th.textMuted, border: "none",
            fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F,
          }}
        >+{overflow}</button>
      )}
    </div>
  );
}

/** Standard button padding presets — see CLAUDE.md "Spacing Scale". */
export const BTN_PAD = {
  sm: "6px 12px",
  md: "8px 14px",
  lg: "10px 16px",
};

/**
 * The button that lives INSIDE a `WarnNote`. The strip is the light amber
 * palette in both themes, so a theme-coloured button (`th.text` on dark) put
 * white text on beige; this one takes the strip's own ink instead.
 */
export function WarnNoteBtn({ onClick, children, ariaLabel }) {
  return (
    <button type="button" onClick={onClick} aria-label={ariaLabel} style={{
      padding: BTN_PAD.sm, borderRadius: 8, border: "1px solid " + AMBER.ink,
      background: "transparent", color: AMBER.ink, fontSize: 13, fontWeight: 700,
      cursor: "pointer", fontFamily: F, display: "inline-flex", alignItems: "center", gap: 6,
      whiteSpace: "nowrap", flexShrink: 0,
    }}>{children}</button>
  );
}

/**
 * ShowMoreBtn — the one "reveal the next batch" footer for a capped list.
 * It belongs AFTER the last rendered row, never in the list header: a header
 * control announces the truncation on a line the reader has already scrolled
 * past, so a long list looks complete right where it stops. Caller passes the
 * already-translated label (usually with the remaining count).
 */
export function ShowMoreBtn({ onClick, children, style }) {
  const { accent } = useAppTheme();
  return (
    <div style={{ textAlign: "center", padding: "8px 0 4px", ...style }}>
      <button onClick={onClick} style={{
        padding: BTN_PAD.md, borderRadius: 8, border: "1px solid " + accent,
        background: "transparent", color: accent, fontSize: 13, fontWeight: 700,
        cursor: "pointer", fontFamily: F,
      }}>{children}</button>
    </div>
  );
}

/* ── Tabular numerics ──
   `tabular-nums` makes every digit the same width. That serves two different
   purposes, and conflating them is why the aligned version sat here with zero
   consumers for so long: anyone who wanted steady digits found a constant that
   also right-aligned, did not want the alignment, and wrote the raw CSS.

   `tabularDigits` — the digit metric alone. For a number that CHANGES IN PLACE:
   a countdown, a wall clock, a live score, a PIN. Nothing is being aligned to
   anything; the point is that "1:11" and "8:88" occupy the same width, so a
   200px countdown on a gym wall does not jitter every second. Also what a
   `<table>` wants at the element level, so every cell inherits the metric while
   each column keeps its own alignment.

   `tabularNum` / `tabularNumCenter` — the metric PLUS an alignment, for a
   column of numbers stacked vertically (rankings, scores, attempts, ratings).
   Right-align is what makes decimal points line up between rows; for mixed
   integer/decimal columns pair it with a consistent `.toFixed(N)` so the dots
   actually align. Never put these on a whole table — the name column would
   follow. */
export const tabularDigits = {
  fontVariantNumeric: "tabular-nums",
};
export const tabularNum = {
  ...tabularDigits,
  textAlign: "right",
};
export const tabularNumCenter = {
  ...tabularDigits,
  textAlign: "center",
};

/* ── Stroke icons for action buttons. Inherit currentColor so the parent
   button can drive the color (e.g. danger red, muted gray). ────────── */

/** Trash icon — for deleting objects (files, items, settings). */
export function IconTrash({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </svg>
  );
}

/** User-minus icon — for removing a person (kick, unfriend, remove admin/coach).
 *  Softer than a trash icon: signals "remove from this list", not "destroy". */
export function IconUserMinus({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 11h-6" />
    </svg>
  );
}

/** Lock icon — status indicator for non-public / private resources. */
export function IconLock({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Close / dismiss icon — for closing modals and clearing inputs. */
/** Crosshair / target — used for the "show on map" action. The outer
 *  ring + inner dot + cross lines read instantly as "find this on the
 *  map", which an unlabeled colored route dot doesn't.
 */
export function IconTarget({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function IconClose({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

/** Info icon — a circled "i". Only inside `InfoTip` (a help bubble); never a
 *  status or error marker. */
export function IconInfo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <circle cx="12" cy="7.6" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Repeat icon — two looping arrows. For "again" (the Mégegyszer button, the
 *  repeat dot and its legend); it replaces the 🔁 emoji, which Windows draws as
 *  a blue box. */
export function IconRepeat({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11V9a3 3 0 0 1 3-3h15" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v2a3 3 0 0 1-3 3H3" />
    </svg>
  );
}

/** Dice icon — a die showing five. For "another random suggestion" (the spray
 *  route editor's Másik név button); not for repeat or reset. */
export function IconDice({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="16" cy="8" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="8" cy="16" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="16" cy="16" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Pencil icon — edit affordance badge (e.g. the avatar-change overlay). */
export function IconPencil({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
    </svg>
  );
}

/** Copy icon — two overlapping sheets. For one-click "copy to clipboard"
 *  affordances (share links, codes). Pair with IconCheck for copied feedback. */
export function IconCopy({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/** Check / done icon — success confirmation (e.g. copied-to-clipboard). */
export function IconCheck({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

/** Chevrons — the "move up" / "move down" reorder pair in the editors
 *  (groups, sessions, series). Five editors drew them as `↑` / `↓` / `▲` / `▼`
 *  text in a bare button; the glyph rendered at a different size in each. */
export function IconChevronUp({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

export function IconChevronDown({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

/** Chevrons — "back" / "previous" and "next" (the Spraywall wall and problem
 *  headers, the ‹ › paging over the problem photo). Not the reorder pair. */
export function IconChevronLeft({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M15 6l-6 6 6 6" />
    </svg>
  );
}

export function IconChevronRight({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

/** Sliders — opens a filter modal ("Szűrők"). */
export function IconSliders({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12" />
      <circle cx="16" cy="6" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="18" cy="18" r="2" />
    </svg>
  );
}

/** Up / down arrows — the sort control of a list. */
export function IconSort({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4" />
    </svg>
  );
}

/** Archive box — old / archived versions (the Spraywall "Régi fal-verziók"). */
export function IconArchive({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <rect x="3" y="4" width="18" height="5" rx="1" />
      <path d="M5 9v10h14V9M10 13h4" />
    </svg>
  );
}

/** Four corners — open something full screen. */
export function IconExpand({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />
    </svg>
  );
}

/** Four inward-pointing corners — leave a full-screen / focus view (the pair of IconExpand). */
export function IconShrink({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />
    </svg>
  );
}

/** Curved arrow to the left — undo the last step. */
export function IconUndo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </svg>
  );
}

/** Curved arrow to the right — redo an undone step. */
export function IconRedo({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13" />
    </svg>
  );
}

/** Four tiles — an overview / management area of several items. */
export function IconGrid({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <rect x="4" y="4" width="7" height="7" rx="1" />
      <rect x="13" y="4" width="7" height="7" rx="1" />
      <rect x="4" y="13" width="7" height="7" rx="1" />
      <rect x="13" y="13" width="7" height="7" rx="1" />
    </svg>
  );
}

/** Filled star — a benchmark / official mark (currentColor; not the rating row, that is StarRow). */
export function IconStar({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5L2.5 9.4l6.6-.9z" />
    </svg>
  );
}

/** Plus — "new …" inside a menu item. */
export function IconPlus({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Three dots — the trigger of an overflow ("Továbbiak") menu. */
export function IconMore({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}

// Bell — used for the "watch this route" toggle. `filled` shows the active
// (watching) state by filling the bell with the current colour.
export function IconBell({ size = 16, filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

/**
 * The Spraywall identity icon comes in three forms of ONE drawing — variant E
 * „Útvonal" of mockups/spraywall-logo-variants.html: a wall panel wider at the
 * top (an overhang leaning out) with a problem on it, four holds joined from
 * the bottom hold to the top hold. None of the three is an action glyph, so
 * never put one in an IconBtn for an unrelated action.
 *
 * SPRAY_ICON_PANEL / SPRAY_ICON_ROUTE / SPRAY_ICON_HOLDS are the shared 24-grid
 * geometry of the two small forms, so the mono and the coloured icon cannot
 * drift apart. Each hold is [cx, cy, r, role].
 */
const SPRAY_ICON_PANEL = "M6.5 20.5 L3.5 3.5 H20.5 L17.5 20.5 Z";
const SPRAY_ICON_ROUTE = "M9 16.6 L14.6 13.2 L9.6 10 L13.4 7.2";
const SPRAY_ICON_HOLDS = [[9, 16.6, 2, "start"], [14.6, 13.2, 1.6, "hand"], [9.6, 10, 1.6, "hand"], [13.4, 7.2, 2, "finish"]];
// The problem view's role colours (SPRAY_ROLE_COLORS), so the icon says what a
// marked problem on the wall photo looks like.
const SPRAY_ICON_ROLE_COLORS = { start: "#16a34a", hand: "#eab308", finish: "#dc2626" };

/**
 * IconSprayWall — the single-colour form, for every place where the icon sits
 * next to text and takes the text's colour (box titles, timeline rows, the
 * journal window). Uses currentColor.
 */
export function IconSprayWall({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d={SPRAY_ICON_PANEL} />
      <path d={SPRAY_ICON_ROUTE} strokeWidth="1.5" />
      {SPRAY_ICON_HOLDS.map(([cx, cy, r]) => <circle key={`${cx},${cy}`} cx={cx} cy={cy} r={r} fill="currentColor" stroke="none" />)}
    </svg>
  );
}

/**
 * IconSprayWallColor — the coloured form, for places whose neighbours are colour
 * emoji (the top tab bar, the admin Spraywall row, the TV Spray walls slide
 * title): a single-colour glyph there read as a gap in the row. Fixed colours, NOT currentColor, so like an emoji it does not turn
 * accent on the active tab (the label and the top line mark that). Filled
 * plywood with a darker edge, so it reads on a light and on a dark header.
 */
export function IconSprayWallColor({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: "block", flexShrink: 0 }} aria-hidden="true">
      <path d={SPRAY_ICON_PANEL} fill="#e9cf9f" stroke="#9a6b35" strokeWidth="1.2" strokeLinejoin="round" />
      <path d={SPRAY_ICON_ROUTE} fill="none" stroke="#7a5528" strokeWidth="1.3" strokeLinejoin="round" />
      {SPRAY_ICON_HOLDS.map(([cx, cy, r, role]) => (
        <circle key={`${cx},${cy}`} cx={cx} cy={cy} r={r} fill={SPRAY_ICON_ROLE_COLORS[role]} stroke="rgba(0,0,0,.35)" strokeWidth="0.6" />
      ))}
    </svg>
  );
}

// The large marker drawing, 64-unit grid, generated from the approved mockup
// (variant E) so the map shows exactly the picture that was chosen. The hold
// outlines are organic blobs, hence literal paths: [d, fill, highlight
// ellipse [cx, cy, rx, ry], bolt centre [cx, cy]].
const SPRAY_MARKER_HOLDS = [
  ["M49.4 18 C49.3 18.5 48.8 19 48.3 19.3 C47.9 19.7 47 19.9 46.5 19.9 C45.9 19.8 45.2 19.2 45 18.8 C44.8 18.4 44.9 17.8 45.2 17.3 C45.4 16.9 45.9 16.3 46.5 16.1 C47 15.9 48.2 16 48.7 16.3 C49.1 16.7 49.4 17.5 49.4 18Z", "#9ca3af", [46.2, 17.2, 0.8, 0.5], [47, 18]],
  ["M17.3 16.6 C16.9 16.8 16 16.6 15.4 16.4 C14.9 16.2 14.2 16 14 15.6 C13.8 15.2 13.7 14.4 14 14 C14.3 13.6 15.2 13.3 15.7 13.3 C16.3 13.2 17.1 13.5 17.4 13.8 C17.8 14.1 17.9 14.7 17.9 15.2 C17.8 15.6 17.7 16.4 17.3 16.6Z", "#78716c", [15.3, 14.3, 0.7, 0.5], [16, 15]],
  ["M47.1 41.6 C46.5 41.3 45.7 40.9 45.6 40.5 C45.4 40 45.8 39.3 46.2 38.9 C46.6 38.5 47.3 38.2 48 38.1 C48.6 38.1 49.5 38.3 49.9 38.7 C50.3 39.1 50.4 39.8 50.3 40.4 C50.2 40.9 49.8 41.8 49.3 42 C48.7 42.2 47.7 41.8 47.1 41.6Z", "#a8a29e", [47.2, 39.2, 0.9, 0.6], [48, 40]],
  ["M12.1 38.2 C12.1 37.8 12.3 37.3 12.6 36.9 C12.9 36.6 13.7 36.2 14.2 36.3 C14.7 36.3 15.3 36.8 15.6 37.1 C15.9 37.5 16 38 15.9 38.5 C15.8 38.9 15.3 39.6 14.8 39.8 C14.3 39.9 13.3 39.9 12.8 39.6 C12.4 39.4 12.2 38.7 12.1 38.2Z", "#6b7280", [13.3, 37.3, 0.7, 0.5], [14, 38]],
  ["M35.7 45.8 C36.1 45.5 36.9 45.4 37.4 45.4 C37.9 45.5 38.4 45.9 38.6 46.3 C38.9 46.7 39 47.3 38.9 47.7 C38.7 48 38 48.5 37.5 48.6 C37.1 48.7 36.4 48.4 36 48.1 C35.5 47.9 35 47.5 35 47.1 C34.9 46.7 35.3 46 35.7 45.8Z", "#57534e", [36.4, 46.4, 0.7, 0.4], [37, 47]],
  ["M23.5 45 C23.6 45.7 23 46.9 22.3 47.3 C21.6 47.7 20.2 47.5 19.3 47.3 C18.5 47.1 17.6 46.7 17.2 46.1 C16.8 45.5 16.4 44.4 16.7 43.7 C17.1 43.1 18.3 42.3 19.2 42.2 C20.1 42.1 21.3 42.5 22 43 C22.7 43.5 23.4 44.3 23.5 45Z", "#a855f7", [18.9, 43.9, 1.2, 0.7], [20, 45]],
  ["M37.9 35 C37.9 35.7 37.7 36.7 37.1 37.1 C36.5 37.5 35.1 37.4 34.4 37.2 C33.6 37 33 36.4 32.7 35.9 C32.4 35.4 32.4 34.7 32.7 34.1 C32.9 33.5 33.6 32.6 34.3 32.4 C35 32.3 36.3 32.6 36.9 33.1 C37.5 33.5 37.9 34.3 37.9 35Z", "#a855f7", [34, 34, 1, 0.7], [35, 35]],
  ["M25.7 25 C25.8 25.6 25.5 26.5 24.9 26.9 C24.3 27.3 23 27.8 22.3 27.6 C21.5 27.5 20.8 26.6 20.5 26 C20.2 25.4 20.2 24.6 20.5 24 C20.8 23.4 21.6 22.6 22.3 22.4 C23 22.3 24.1 22.9 24.7 23.3 C25.3 23.7 25.7 24.4 25.7 25Z", "#a855f7", [22, 24, 1, 0.7], [23, 25]],
  ["M35.9 13 C35.9 13.7 35.6 14.6 35 15 C34.4 15.4 33.2 15.4 32.3 15.3 C31.5 15.2 30.1 14.8 29.7 14.3 C29.3 13.7 29.6 12.5 30 11.8 C30.4 11.2 31.4 10.5 32.3 10.4 C33.1 10.2 34.5 10.4 35.1 10.9 C35.7 11.3 35.9 12.3 35.9 13Z", "#a855f7", [31.9, 11.9, 1.2, 0.7], [33, 13]],
];
// The problem's four holds, ringed in role colour: [cx, cy, r, role].
const SPRAY_MARKER_RINGS = [[20, 45, 5.4, "start"], [35, 35, 5, "hand"], [23, 25, 5, "hand"], [33, 13, 5.4, "finish"]];
// T-nut grid on the panel: rows every 6 units, each row running between the
// panel's slanted edges (left edge 6→13, right edge 58→51 from top to bottom).
const SPRAY_MARKER_TNUTS = (() => {
  const out = [];
  for (let y = 13; y < 49; y += 6) {
    const f = (y - 9) / 42, xl = 6 + 7 * f, xr = 58 - 7 * f;
    for (let x = xl + 4; x < xr - 2; x += 6) out.push([Math.round(x * 10) / 10, y]);
  }
  return out;
})();

/**
 * SprayWallMarkerGlyph — the one shared shape of the spray wall marker: the
 * large, detailed form of the identity icon, about 40x40 — a dark rounded
 * square with a white outline holding a plywood wall with a problem marked on
 * it. Square, never round, so it cannot pass for a route marker. Must be
 * placed inside an <svg>; it is centred on 0,0 and the caller translates /
 * scales it. Shared by the map marker (ZoomableMap) and the legend row
 * (MapTab) so the two cannot drift apart.
 */
export function SprayWallMarkerGlyph() {
  return (
    <g transform="translate(-20, -20) scale(0.625)">
      <rect x={2} y={2} width={60} height={60} rx={9} fill="#1f2937" stroke="#fff" strokeWidth={3.2} />
      <path d="M13 51 L6 9 H58 L51 51 Z" fill="#e9cf9f" stroke="#b98f55" strokeWidth={1} />
      <path d="M13 51 H51 L50 57 H14 Z" fill="#c9a46c" stroke="#b98f55" strokeWidth={1} />
      {SPRAY_MARKER_TNUTS.map(([x, y]) => <circle key={`t${x},${y}`} cx={x} cy={y} r={0.7} fill="#a07b45" opacity={0.55} />)}
      <path d="M20 45 C26 41 34 41 35 35 C36 29 22 30 23 25 C24 19 36 22 33 13" fill="none" stroke="#7a5528" strokeWidth={2} strokeLinecap="round" strokeDasharray="2.6 3" />
      {SPRAY_MARKER_HOLDS.map(([d, fill, [hx, hy, hrx, hry], [bx, by]]) => (
        <g key={`h${bx},${by}`}>
          <path d={d} fill={fill} stroke="rgba(0,0,0,.38)" strokeWidth={0.7} />
          <ellipse cx={hx} cy={hy} rx={hrx} ry={hry} fill="#fff" opacity={0.35} />
          <circle cx={bx} cy={by} r={0.55} fill="rgba(0,0,0,.5)" />
        </g>
      ))}
      {SPRAY_MARKER_RINGS.map(([cx, cy, r, role]) => (
        <g key={`r${cx},${cy}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#fff" strokeWidth={3.8} opacity={0.85} />
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={SPRAY_ICON_ROLE_COLORS[role]} strokeWidth={2.2} />
        </g>
      ))}
    </g>
  );
}

/**
 * IconBtn — minimal icon-only button used for delete / remove / close actions.
 * Uses currentColor so the icon matches the `tone`. Default size aims for a
 * comfortable ~32px tap target while staying compact in dense rows.
 *
 *   tone: "danger" (red) | "muted" (themeMuted) | "default" (themeText)
 *   size: icon glyph size in px (button is icon + 2*padding)
 *   ariaExpanded: optional, for a button that opens a popover (InfoTip)
 */
export function IconBtn({ icon: Icon, onClick, title, ariaLabel, ariaPressed, ariaExpanded, tone = "danger", size = 16, padding = 6, disabled, style }) {
  const { th: theme } = useAppTheme();
  const color = tone === "danger" ? "#dc2626" : tone === "muted" ? (theme?.textMuted || "#888") : (theme?.text || "#333");
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      aria-label={ariaLabel || title}
      aria-pressed={ariaPressed}
      aria-expanded={ariaExpanded}
      disabled={disabled}
      style={{
        background: "none", border: "none", cursor: disabled ? "default" : "pointer",
        color, padding, lineHeight: 0, borderRadius: 8, flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        ...style,
      }}
    >
      <Icon size={size} />
    </button>
  );
}

/**
 * InfoTip — a help text that an experienced user no longer needs, folded into a
 * small ⓘ icon with an anchored bubble (docs/spec/21n-log-help-hints.md). NOT
 * for errors, warnings or state: anything the user must read to avoid a mistake
 * stays visible text.
 *
 * A 32 px tap target (`IconBtn`, the 16 px `IconInfo`, muted; accent while
 * open). A tap opens a bubble with `children`; it closes on a second tap, the ✕,
 * a tap outside or Escape. The bubble is `position: absolute` inside a
 * `position: relative` wrapper — never `position: fixed` — placed once on open:
 * centred on the icon, clamped into the nearest clipping ancestor (or the
 * viewport) with a 12 px margin, below the icon or above it when there is no
 * room below.
 *
 *   children  the bubble content
 *   label     the icon's aria-label / title (default `help.info`)
 *   glow      play the three-pulse glow once (the first time the texts turn into icons)
 *   onOpen    called when the bubble opens (the GA4 hook point of the caller)
 *   style     the wrapper — use a negative margin so the 32 px target does not grow the line
 */
const TIP_GAP = 8;
const TIP_MARGIN = 12;
const TIP_WIDTH = 300;

// The close callback of the one bubble that is open right now. Opening a bubble
// (by pointer OR keyboard) closes the previous one; the document `pointerdown`
// only covers the pointer.
let openTipClose = null;

/** The box the bubble must stay inside: the nearest clipping ancestor, cut to the viewport. */
function tipClipBox(el) {
  const vw = window.innerWidth || 0;
  const vh = window.innerHeight || 0;
  let box = { left: 0, right: vw, top: 0, bottom: vh };
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const cs = window.getComputedStyle(p);
    if (cs.overflowX !== "visible" || cs.overflowY !== "visible") {
      const r = p.getBoundingClientRect();
      box = {
        left: Math.max(box.left, r.left), right: Math.min(box.right, r.right),
        top: Math.max(box.top, r.top), bottom: Math.min(box.bottom, r.bottom),
      };
      break;
    }
  }
  return box;
}

export function InfoTip({ children, label, glow = false, onOpen, style }) {
  const { t } = useTranslation();
  const { th, accent } = useAppTheme();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const wrapRef = useRef(null);
  const bubbleRef = useRef(null);
  const name = label || t("help.info");

  const close = useCallback(() => { setOpen(false); setPos(null); }, []);
  const toggle = () => {
    if (open) { close(); return; }
    if (openTipClose && openTipClose !== close) openTipClose();
    openTipClose = close;
    setOpen(true);
    onOpen?.();
  };

  // Forget this bubble when it closes or unmounts (only if it is still the registered one).
  useEffect(() => {
    if (!open) return undefined;
    return () => { if (openTipClose === close) openTipClose = null; };
  }, [open, close]);

  useEscapeKey(close, open);

  // A tap anywhere else closes the bubble; a tap on the icon itself is the toggle's.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      close();
    };
    // Close on a WIDTH change (rotation, a resized window) only: scrolling on a
    // phone collapses the URL bar, which fires `resize` with a new height.
    const openWidth = window.innerWidth;
    const onResize = () => { if (window.innerWidth !== openWidth) close(); };
    document.addEventListener("pointerdown", onDown);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      window.removeEventListener("resize", onResize);
    };
  }, [open, close]);

  // Place the bubble once it exists, before the first paint. Without a layout
  // (jsdom) it stays below the icon at its natural width.
  useLayoutEffect(() => {
    if (!open || !wrapRef.current || !bubbleRef.current) return;
    const icon = wrapRef.current.getBoundingClientRect();
    if (!icon.width) { setPos({ left: 0, width: TIP_WIDTH, below: true, arrow: 12 }); return; }
    const box = tipClipBox(wrapRef.current);
    const width = Math.max(120, Math.min(TIP_WIDTH, box.right - box.left - 2 * TIP_MARGIN));
    const center = icon.left + icon.width / 2;
    const leftAbs = Math.min(Math.max(center - width / 2, box.left + TIP_MARGIN), box.right - TIP_MARGIN - width);
    const height = bubbleRef.current.offsetHeight;
    const roomBelow = box.bottom - icon.bottom - TIP_GAP - TIP_MARGIN;
    const roomAbove = icon.top - box.top - TIP_GAP - TIP_MARGIN;
    const below = height <= roomBelow || roomBelow >= roomAbove;
    setPos({
      left: leftAbs - icon.left, width, below,
      arrow: Math.max(12, Math.min(center - leftAbs - 6, width - 24)),
    });
  }, [open]);

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-flex", flexShrink: 0, verticalAlign: "middle", ...style }}>
      {glow && (
        <style>{`@keyframes sendly-tip-glow { 0%, 100% { box-shadow: 0 0 0 0 transparent; } 50% { box-shadow: 0 0 0 6px ${accent}38; } }`}</style>
      )}
      <IconBtn
        icon={IconInfo} tone="muted" size={16} padding={8} title={name} ariaLabel={name}
        ariaExpanded={open} onClick={toggle}
        style={{
          borderRadius: 999,
          // while it glows the icon is accent-coloured too (the ring alone is easy to miss)
          ...(glow ? { color: accent, animation: "sendly-tip-glow 1.1s ease-in-out 3" } : null),
          ...(open ? { color: accent, background: accent + "1f" } : null),
        }}
      />
      {open && (
        <div
          ref={bubbleRef} role="dialog" aria-label={name}
          style={{
            position: "absolute", zIndex: 5, boxSizing: "border-box",
            width: pos ? pos.width : TIP_WIDTH, maxWidth: "calc(100vw - 24px)", left: pos ? pos.left : 0,
            ...(pos && !pos.below ? { bottom: "100%", marginBottom: TIP_GAP } : { top: "100%", marginTop: TIP_GAP }),
            visibility: pos ? "visible" : "hidden",
            padding: "12px 40px 12px 12px", borderRadius: 12,
            background: th.card, color: th.text, border: "1px solid " + th.border,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            fontFamily: F, fontSize: 12, lineHeight: 1.5, fontWeight: 500, textAlign: "left",
            // a caller may sit in an uppercase label (a FactRow key); the bubble is plain text
            textTransform: "none", letterSpacing: "normal",
          }}
        >
          {children}
          <IconBtn
            icon={IconClose} tone="muted" size={16} padding={8} title={t("common.close")}
            onClick={close} style={{ position: "absolute", top: 4, right: 4, borderRadius: 999 }}
          />
          <i
            aria-hidden="true"
            style={{
              position: "absolute", width: 12, height: 12, boxSizing: "border-box", background: th.card,
              left: pos ? pos.arrow : 12,
              ...(pos && !pos.below
                ? { bottom: -6, borderRight: "1px solid " + th.border, borderBottom: "1px solid " + th.border, transform: "rotate(45deg)" }
                : { top: -6, borderLeft: "1px solid " + th.border, borderTop: "1px solid " + th.border, transform: "rotate(45deg)" }),
            }}
          />
        </div>
      )}
    </span>
  );
}

/**
 * SearchInput — the shared search field. Magnifier glyph on the left, and a
 * clear (✕) button on the right that appears as soon as there is text. Use this
 * everywhere instead of hand-rolling an <input> with a positioned icon: it
 * guarantees the clear affordance, the leading icon, and consistent sizing
 * across the app (gym selector, admin user search, settings list, …).
 *
 *   value:     current query string
 *   onChange:  called with the NEW string (not the event) — pass a setter
 *   theme/th:  theme object (either prop name works)
 *   size:      "md" (default — main UI: 44px / radius 12 / 14px) or
 *              "sm" (dense admin lists & modal pickers: 36px / radius 10 / 13px)
 *   height:    explicit px height override (rarely needed; defaults from size)
 *   accent:    optional focus tint; falls back to the border colour
 *   style:     applied to the wrapper (use for flex sizing in a toolbar row,
 *              or marginBottom inside a stacked form)
 */
// No `radius` here: a search box is a field, so it rounds like every other
// field (`INPUT_RADIUS`). The 12 / 10 split this used to carry was the same
// drift D3 removed everywhere else.
const SEARCH_SIZES = {
  md: { height: 44, fontSize: 14, icon: 16, iconLeft: 12, padLeft: 38, clearPad: 38, clearIcon: 14 },
  sm: { height: 36, fontSize: 13, icon: 15, iconLeft: 10, padLeft: 34, clearPad: 34, clearIcon: 13 },
};

export function SearchInput({
  value, onChange, placeholder,
  size = "md", height, autoFocus, ariaLabel, style, inputRef, id,
}) {
  const { th: theme, th: thProp, accent } = useAppTheme();
  const th = theme || thProp;
  const { t } = useTranslation();
  const sz = SEARCH_SIZES[size] || SEARCH_SIZES.md;
  const h = height || sz.height;
  return (
    <div style={{ position: "relative", ...style }}>
      <div style={{ position: "absolute", left: sz.iconLeft, top: "50%", transform: "translateY(-50%)", color: th.textMuted, pointerEvents: "none", lineHeight: 0 }}>
        <svg width={sz.icon} height={sz.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <TextInput
        inputRef={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={ariaLabel || placeholder}
        autoFocus={autoFocus}
        style={{
          // The box is the field's; only the geometry is the search box's —
          // a fixed height, and the horizontal padding that keeps the text
          // clear of the magnifier and the clear button.
          height: h,
          padding: `0 ${value ? sz.clearPad : sz.iconLeft + 2}px 0 ${sz.padLeft}px`,
          fontSize: sz.fontSize,
          ...(accent ? { border: "1px solid " + accent } : null),
        }}
      />
      {value && (
        <IconBtn
          icon={IconClose}
          tone="muted"
          size={sz.clearIcon}
          onClick={() => onChange("")}
          title={t('common.clearSearch')}
          style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)" }}
        />
      )}
    </div>
  );
}

/** Inline SVG flag icons. */
function FlagHU({ size = 20 }) {
  const h = size * 0.7;
  return (
    <svg width={size} height={h} viewBox="0 0 20 14" aria-hidden="true" style={{ borderRadius: 4, flexShrink: 0 }}>
      <rect y="0" width="20" height="4.67" fill="#CE2939"/>
      <rect y="4.67" width="20" height="4.67" fill="#fff"/>
      <rect y="9.33" width="20" height="4.67" fill="#477050"/>
    </svg>
  );
}

function FlagGB({ size = 20 }) {
  const h = size * 0.7;
  return (
    <svg width={size} height={h} viewBox="0 0 60 42" aria-hidden="true" style={{ borderRadius: 4, flexShrink: 0 }}>
      <rect width="60" height="42" fill="#012169"/>
      <path d="M0 0L60 42M60 0L0 42" stroke="#fff" strokeWidth="7"/>
      <path d="M0 0L60 42M60 0L0 42" stroke="#C8102E" strokeWidth="4"/>
      <path d="M30 0V42M0 21H60" stroke="#fff" strokeWidth="10"/>
      <path d="M30 0V42M0 21H60" stroke="#C8102E" strokeWidth="6"/>
    </svg>
  );
}

const FLAGS = { hu: FlagHU, en: FlagGB };
const LANG_LABELS = { hu: "Magyar", en: "English" };

/**
 * Language dropdown with flag icons. The trigger is named "Nyelv: Magyar" /
 * "Language: English" (`settings.language` + the current language) — the compact
 * variant shows only a flag, so without the name it said nothing at all — and
 * opens a `listbox` of the languages, each an `option` in its own language
 * (`lang`), the current one `aria-selected`.
 */
export function LangSelect({ value, onChange, options = ["hu", "en"], fontFamily, compact }) {
  const { th: theme, accent } = useAppTheme();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const Flag = FLAGS[value] || FlagHU;
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen(!open)}
        aria-haspopup="listbox" aria-expanded={open}
        aria-label={`${t("settings.language")}: ${LANG_LABELS[value] || value}`}
        style={{
        width: compact ? "auto" : "100%", display: "flex", alignItems: "center", gap: compact ? 0 : 10,
        padding: compact ? "8px 10px" : "10px 14px", borderRadius: 12, fontSize: 14, fontWeight: 600,
        background: theme.inputBg, color: theme.text, border: "2px solid " + theme.border,
        cursor: "pointer", fontFamily: fontFamily || F,
      }}>
        <Flag size={22} />
        {!compact && <span style={{ flex: 1, textAlign: "left" }}>{LANG_LABELS[value] || value}</span>}
        {!compact && <span aria-hidden="true" style={{ fontSize: 12, color: theme.textMuted, transition: "transform 0.2s",
          transform: open ? "rotate(180deg)" : "rotate(0)" }}>▼</span>}
      </button>
      {open && (
        <div role="listbox" aria-label={t("settings.language")} style={{
          position: "absolute", top: "calc(100% + 4px)",
          ...(compact ? { right: 0, minWidth: 160 } : { left: 0, right: 0 }),
          zIndex: Z_DROPDOWN,
          background: theme.card, border: "1px solid " + theme.border, borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)", overflow: "hidden",
        }}>
          {options.map(k => {
            const OptFlag = FLAGS[k] || FlagHU;
            const isActive = k === value;
            return (
              <button key={k} type="button" role="option" aria-selected={isActive} lang={k}
                onClick={() => { onChange(k); setOpen(false); }} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "10px 14px", border: "none", fontSize: 14, fontWeight: isActive ? 700 : 500,
                background: isActive ? (accent || theme.border) + "18" : "transparent",
                color: theme.text, cursor: "pointer", fontFamily: fontFamily || F,
              }}>
                <OptFlag size={22} />
                {LANG_LABELS[k] || k}
                {isActive && <span aria-hidden="true" style={{ marginLeft: "auto", color: accent, fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Shared difficulty filter pills with hatched ungraded circle.
 * @param {object} props
 * @param {object} diff - difficulty map { key: { color, border, label, points } }
 * @param {string[]} selected - currently selected keys (empty = all selected)
 * @param {function} onToggle - (key) => void
 * @param {boolean} hasUngraded - whether to show the "_ungraded" pill
 * @param {string} accent
 * @param {object} th - theme object
 * @param {function} t - translation function
 */
export function DifficultyFilterPills({ diff, selected, onToggle, hasUngraded,}) {
  const { accent } = useAppTheme();
  const { t } = useTranslation();
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {sortedEntries(diff).map(([k, v]) => {
        const active = selected.length > 0 && selected.includes(k);
        return (
          <DifficultyPill key={k} active={active} onClick={() => onToggle(k)} color={v.color} border={v.border}>
            {tDiffLabel(t, k, v.label)} ({v.points}p)
          </DifficultyPill>
        );
      })}
      {hasUngraded && (() => {
        const active = selected.length > 0 && selected.includes("_ungraded");
        return (
          <Pill active={active} onClick={() => onToggle("_ungraded")}>
            <svg width="12" height="12" viewBox="0 0 12 12" style={{ flexShrink: 0 }}>
              <defs><pattern id={"hatch-df-" + (active ? "on" : "off")} patternUnits="userSpaceOnUse" width="3" height="3" patternTransform="rotate(45)"><line x1="0" y1="0" x2="0" y2="3" stroke={active ? "#fff" : accent} strokeWidth="1.5" opacity="0.5" /></pattern></defs>
              <circle cx="6" cy="6" r="5" fill={"url(#hatch-df-" + (active ? "on" : "off") + ")"} stroke={active ? "#fff" : accent} strokeWidth="1" />
            </svg>
            ?
          </Pill>
        );
      })()}
    </div>
  );
}

/**
 * One difficulty filter pill: the colour dot of a difficulty level (ring in
 * its `border`) before the label. `DifficultyFilterPills` draws one per level;
 * the Spraywall filter draws one per grade band — a band IS a difficulty level
 * of the gym's boulder set, so both surfaces must paint it identically. No
 * `color` (a Spraywall fallback band) → no dot.
 */
export function DifficultyPill({ active, onClick, color, border, children }) {
  return (
    <Pill active={active} onClick={onClick}>
      {color ? (
        <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, border: "1px solid " + (border || color), display: "inline-block", flexShrink: 0 }} />
      ) : null}
      {children}
    </Pill>
  );
}

/**
 * HoldColorPicker — the hold-colour swatch row of the route forms (the route
 * builder and the route modal's edit expander): a "none" swatch, then one per
 * colour of the gym's palette in `sortedEntries` order. A `role="group"` named
 * by `label`; every swatch is a toggle button (`aria-pressed`) named by the
 * colour's label, so the picker is reachable by role + name, not only by sight.
 * `value` is the colour key, or `null` for "no hold colour".
 *
 * `size` is the swatch diameter in px: 34 (the builder, roomy, the picked one
 * lifts by 1.2×) or 28 (the dense edit expander, no lift).
 */
export function HoldColorPicker({ value, onChange, holdColors, label, size = 28 }) {
  const { accent, th } = useAppTheme();
  const { t } = useTranslation();
  const roomy = size > 28;
  const swatch = (key, background, name) => {
    const picked = value === key;
    return (
      <button key={String(key)} type="button" onClick={() => onChange(key)}
        title={name} aria-label={name} aria-pressed={picked}
        style={{
          width: size, height: size, borderRadius: "50%", background,
          border: picked ? "3px solid " + accent : "2px solid " + th.border,
          cursor: "pointer", padding: 0,
          ...(roomy ? { transition: "all 0.15s", transform: picked ? "scale(1.2)" : "scale(1)" } : null),
        }} />
    );
  };
  return (
    <div role="group" aria-label={label} style={{ display: "flex", flexWrap: "wrap", gap: roomy ? 8 : 6 }}>
      {swatch(null, "repeating-linear-gradient(45deg, transparent, transparent 3px, " + th.border + " 3px, " + th.border + " 5px)", t("route.holdColorNone"))}
      {sortedEntries(holdColors).map(([k, v]) => swatch(k, v.color, tHoldLabel(t, k, v.label)))}
    </div>
  );
}

/**
 * useSuggestCombobox — an accessible type-ahead over a short list of known
 * values (the setter names of the route builder and of the Routes tab filter).
 * The CALLER owns the text and the filtering (the Routes tab shows nothing for
 * an empty query, the builder lists everything); this owns the open state, the
 * keyboard-active option and the ARIA wiring of the WAI-ARIA list combobox.
 *
 *   const { inputProps, list } = useSuggestCombobox({ suggestions, onPick, listLabel, onInputChange: setText });
 *   <div style={{ position: "relative" }}>
 *     <TextInput {...inputProps} value={text} />
 *     {list}
 *   </div>
 *
 * `inputProps` carries the `combobox` role, `aria-autocomplete="list"`,
 * `aria-expanded`, `aria-controls` / `aria-activedescendant`, and the handlers
 * (typing opens the list; ArrowDown / ArrowUp move, wrapping; Enter picks the
 * active option; Escape closes the list and stays inside it, so it does not
 * also close a modal around the field). `list` is the absolutely positioned
 * `listbox` (`null` while closed or empty) — put it inside a `position:
 * relative` wrapper. A mouse or touch pick uses `mousedown` + `preventDefault`,
 * so the input keeps focus and no blur timer is needed.
 *
 * `onInputChange(value, event)` is called after the list has been opened; spread
 * `inputProps` BEFORE any `onChange` of your own only if you want to replace it.
 */
export function useSuggestCombobox({ suggestions, onPick, listLabel, onInputChange }) {
  const { th } = useAppTheme();
  const baseId = useId();
  const listId = baseId + "-list";
  const optionId = (i) => baseId + "-opt-" + i;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const count = suggestions.length;
  const expanded = open && count > 0;
  // The list can shrink while an option is active (the user keeps typing).
  const active = expanded && activeIndex < count ? activeIndex : -1;

  useEffect(() => {
    if (active >= 0) document.getElementById(optionId(active))?.scrollIntoView?.({ block: "nearest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const close = () => { setOpen(false); setActiveIndex(-1); };
  const pick = (value) => { close(); onPick(value); };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (count === 0) return;
      e.preventDefault();
      const down = e.key === "ArrowDown";
      if (!expanded) { setOpen(true); setActiveIndex(down ? 0 : count - 1); return; }
      setActiveIndex(down ? (active + 1) % count : (active <= 0 ? count - 1 : active - 1));
    } else if (e.key === "Enter") {
      if (active < 0) return;
      e.preventDefault();
      pick(suggestions[active]);
    } else if (e.key === "Escape" && expanded) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  const inputProps = {
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": expanded,
    "aria-controls": expanded ? listId : undefined,
    "aria-activedescendant": active >= 0 ? optionId(active) : undefined,
    onChange: (e) => { setOpen(true); setActiveIndex(-1); onInputChange?.(e.target.value, e); },
    onFocus: () => setOpen(true),
    onBlur: close,
    onKeyDown,
  };

  const list = expanded ? (
    <div id={listId} role="listbox" aria-label={listLabel}
      style={{ position: "absolute", top: "100%", left: 0, right: 0, background: th.card, border: "1px solid " + th.border, borderRadius: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.15)", zIndex: 10, maxHeight: 150, overflowY: "auto" }}>
      {suggestions.map((s, i) => (
        <div key={s} id={optionId(i)} role="option" aria-selected={i === active}
          onMouseDown={(e) => { e.preventDefault(); pick(s); }}
          style={{ padding: BTN_PAD.md, fontSize: 13, cursor: "pointer", color: th.text, background: i === active ? th.bg : "transparent", borderBottom: "1px solid " + (th.borderLight || th.border) }}>{s}</div>
      ))}
    </div>
  ) : null;

  return { inputProps, list, open: expanded };
}

/**
 * FilterModal — the centred filter panel of the map and the Spraywall route
 * list: a dimming layer and a pointer-transparent centring layer holding the
 * card. Deliberately NOT a ModalBackdrop: it is a filter panel, not a dialog,
 * and keeps the map's two-layer fade + scale-in; it still mounts its own
 * ScrollLock, carries `aria-modal` and closes on Escape (the shared stack).
 * The register rows in modalScrollAffordance / designSystemGates (R9) name it.
 *
 * Props: `title`, `onClose`, `clearLabel` + `onClear` (the accent link shows
 * only with both — a caller passes `onClear` only while a filter is active),
 * `footer` (after the sections), `children` (`FilterSection`s), `testId`.
 */
// `resultCount` + `onShow`: the "{count} út mutatása" button under the
// sections. Filters apply live, so the button only closes — but it shows the
// result size before the modal goes away, on the map and the spray list alike.
export function FilterModal({ title, onClose, clearLabel, onClear, resultCount, onShow, children, testId }) {
  const { th, accent } = useAppTheme();
  const { t } = useTranslation();
  useEscapeKey(onClose, typeof onClose === "function");
  return (
    <>
      <ScrollLock />
      <style>{`
        @keyframes filterModalIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes filterModalFade { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
          zIndex: 80, animation: "filterModalFade 0.2s ease",
        }}
      />
      <div
        style={{
          position: "fixed", inset: 0, zIndex: 81,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: 16, pointerEvents: "none",
        }}
      >
        <div
          role="dialog" aria-modal="true" aria-label={title}
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: "auto",
            width: "100%", maxWidth: 560,
            background: th.card, borderRadius: 20,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            // Same reason as ModalScrollBody's: without it the card
            // rubber-bands away from its own top edge on iOS.
            maxHeight: "85vh", overflowY: "auto", overscrollBehavior: "none",
            padding: "16px 16px 20px",
            animation: "filterModalIn 0.18s cubic-bezier(.2,.8,.2,1)",
            fontFamily: F,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: th.text }}>{title}</h3>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              {clearLabel && typeof onClear === "function" && (
                <button type="button" onClick={onClear} style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  color: accent, fontSize: 12, fontWeight: 800, padding: "6px 8px", fontFamily: F,
                }}>{clearLabel}</button>
              )}
              <IconBtn icon={IconClose} tone="muted" size={18} title={t("common.close")} onClick={onClose} />
            </div>
          </div>
          {children}
          {typeof onShow === "function" && Number.isInteger(resultCount) && (
            <div style={{ marginTop: 16 }}>
              <GreenBtn full onClick={onShow}>{t("filter.showResults", { count: resultCount })}</GreenBtn>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

/** One section of a FilterModal: a top hairline and an optional uppercase muted title. */
export function FilterSection({ title, children }) {
  const { th } = useAppTheme();
  return (
    <div style={{ padding: "12px 0 6px", borderTop: "1px solid " + th.border }}>
      {title ? (
        <div style={{ fontSize: 12, fontWeight: 600, color: th.textMuted, letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>{title}</div>
      ) : null}
      {children}
    </div>
  );
}

/**
 * FilterChips — the removable active-filter chips under a filtered list's
 * toolbar (map, Spraywall list). `chips`: `[{ key, label, onRemove, dotColor?,
 * dotBorder?, hatched? }]` — a colour dot for a difficulty, a hatched one for
 * "ungraded". `style` carries the per-surface padding / margin. Nothing is
 * rendered for an empty list.
 */
export function FilterChips({ chips, style, testId }) {
  const { accent } = useAppTheme();
  const { t } = useTranslation();
  if (!Array.isArray(chips) || chips.length === 0) return null;
  return (
    <div data-testid={testId} style={{ display: "flex", flexWrap: "wrap", gap: 6, ...style }}>
      {chips.map(c => (
        <div key={c.key} style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "4px 4px 4px 10px", borderRadius: 999,
          background: accent + "1A", border: "1px solid " + accent + "33",
          color: accent, fontSize: 12, fontWeight: 600, fontFamily: F,
        }}>
          {c.dotColor && (
            <span style={{
              width: 10, height: 10, borderRadius: "50%",
              background: c.dotColor, border: "1px solid " + (c.dotBorder || c.dotColor),
              flexShrink: 0,
            }} />
          )}
          {c.hatched && (
            <svg width="10" height="10" viewBox="0 0 10 10" style={{ flexShrink: 0 }}>
              <defs><pattern id={"chip-hch-" + c.key} patternUnits="userSpaceOnUse" width="3" height="3" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="3" stroke={accent} strokeWidth="1.5" opacity="0.6" />
              </pattern></defs>
              <circle cx="5" cy="5" r="4" fill={"url(#chip-hch-" + c.key + ")"} stroke={accent} strokeWidth="1" />
            </svg>
          )}
          <span>{c.label}</span>
          <IconBtn
            icon={IconClose} size={10} padding={0}
            onClick={c.onRemove}
            ariaLabel={t("filter.removeChip", { name: c.label })}
            style={{
              width: 18, height: 18, borderRadius: 999,
              background: "#fff", color: accent, marginLeft: 2,
            }} />
        </div>
      ))}
    </div>
  );
}

/**
 * The bottom padding a scrolling list reserves under `NewRouteFab`: the
 * button's ≈44 px plus its 20 px bottom gap plus the iOS home-indicator inset,
 * so a fully scrolled list never hides its last row behind the button.
 */
export const NEW_ROUTE_FAB_CLEARANCE = "calc(env(safe-area-inset-bottom, 0px) + 88px)";

/**
 * NewRouteFab — the centred accent "new route" pill pinned to the bottom of
 * the screen (map, Spraywall list). A pill with an inline plus glyph and its
 * own accent shadow, not a GreenBtn (the R4 register row of
 * designSystemGates). zIndex 60: above the fullscreen map (50), below modals.
 */
export function NewRouteFab({ onClick, label }) {
  const { accent } = useAppTheme();
  return (
    <ViewportLayer anchor="bottom" zIndex={60} padding="0 12px calc(env(safe-area-inset-bottom, 0px) + 20px)">
      <button type="button" onClick={onClick} style={{
        pointerEvents: "auto",
        display: "flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 999,
        background: accent, color: "#fff", border: "none", cursor: "pointer", fontFamily: F,
        fontSize: 14, fontWeight: 700, boxShadow: "0 4px 20px " + accent + "59",
        maxWidth: 460,
      }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        {label}
      </button>
    </ViewportLayer>
  );
}

/**
 * Size presets for `StatBarGroup` + `StatBarRow`. `sm` is the default
 * everywhere in the admin / personal-stats screens; `lg` scales every
 * dimension up so the same component can stand in for the TV-slide
 * grade distribution where the chart is viewed from across a gym
 * lobby; `md` is the middle step for a half-width TV card (the Spray
 * walls slide's 2x2 grid), where `sm` is unreadable across a room and
 * `lg` is too big. A new size is a new entry here, never a per-call override.
 */
const BAR_SIZES = {
  sm: {
    dot: 8,
    labelFontSize: 12,
    labelMaxWidth: 200,
    barHeight: 12,
    barRadius: 6,
    countFontSize: 12,
    countFontWeight: 700,
    rowGap: 4,
    columnGap: 8,
    minBarWidth: "minmax(100px, 1fr)",
  },
  md: {
    dot: 16,
    labelFontSize: 20,
    labelMaxWidth: 300,
    barHeight: 20,
    barRadius: 10,
    countFontSize: 22,
    countFontWeight: 900,
    rowGap: 8,
    columnGap: 12,
    minBarWidth: "minmax(120px, 1fr)",
  },
  lg: {
    dot: 22,
    labelFontSize: 24,
    labelMaxWidth: 420,
    barHeight: 32,
    barRadius: 16,
    countFontSize: 26,
    countFontWeight: 900,
    rowGap: 14,
    columnGap: 16,
    minBarWidth: "minmax(160px, 1fr)",
  },
};

/**
 * StatBarGroup — CSS grid wrapper for a set of StatBarRow children.
 *
 * Uses a 4-column grid (`auto auto minmax(…,1fr) auto`) so the label
 * column auto-sizes to the widest label within the section, the bar
 * column stretches to fill remaining width, and all bars within the
 * section share the exact same width and right-edge alignment.
 *
 * @param {object} props
 * @param {object} props.th - Theme object.
 * @param {React.ReactNode} props.children - StatBarRow elements.
 * @param {string} [props.flashLegend] - Optional tiny legend shown above the count
 *   column, e.g. "( ) = flash". Only render when the group contains flash splits.
 * @param {"sm"|"md"|"lg"} [props.size="sm"] - Dimension preset. Must match the
 *   `size` prop passed to the child `StatBarRow`s.
 */
export function StatBarGroup({ children, flashLegend, size = "sm" }) {
  const { th } = useAppTheme();
  const s = BAR_SIZES[size] || BAR_SIZES.sm;
  return (
    // `width: 100%` so a flex-column parent (e.g. the TV slide
    // container) actually stretches the grid to the full row width —
    // without it, grid containers inside a flex column behave like
    // shrink-to-fit and the 1fr bar column stops short of the row end.
    // Block parents (admin page) work either way, so this is a no-op
    // for the existing call sites.
    <div style={{
      display: "grid",
      gridTemplateColumns: `auto auto ${s.minBarWidth} auto`,
      alignItems: "center",
      rowGap: s.rowGap,
      columnGap: s.columnGap,
      width: "100%",
    }}>
      {flashLegend && (
        <React.Fragment>
          <div />
          <div />
          <div />
          <div style={{
            fontSize: 12, color: th.textMuted, fontWeight: 600,
            textAlign: "right", whiteSpace: "nowrap", marginBottom: 2,
          }}>
            {flashLegend}
          </div>
        </React.Fragment>
      )}
      {children}
    </div>
  );
}

/**
 * StatBarRow — Unified row for difficulty pyramids, completion bars, and grade distributions.
 *
 * Must be rendered inside a `StatBarGroup` — uses `React.Fragment` to contribute
 * 4 cells (dot, label, bar, count) to the parent grid, so labels align across rows
 * and bars share a common stretched width. Supports an optional flash/solid split.
 *
 * When `flashValue === 0` (no split), the bar is rendered in the FULL solid color,
 * never in the muted tint — that tint is reserved for the non-flash segment when
 * a row actually has a flash split, so single-color bars don't look washed out.
 *
 * @param {object} props
 * @param {string} props.color - Bar fill color.
 * @param {string} props.label - Left-side text label.
 * @param {number} [props.max=100] - Denominator for bar percentage calculation.
 * @param {number} [props.value=0] - Total value (solid + flash combined).
 * @param {number} [props.flashValue=0] - Subset of `value` rendered as the "flash" segment.
 * @param {React.ReactNode} props.count - Right-side count display (pre-formatted).
 * @param {object} props.th - Theme object.
 * @param {string} [props.flashColor] - Override color for the flash segment (defaults to `color`).
 * @param {string} [props.nonFlashColorSuffix="66"] - Hex alpha suffix for the non-flash segment.
 * @param {number} [props.idealValue] - Optional reference value rendered as a vertical
 *   marker line on the bar (e.g. ideal pyramid target). Position is clamped to the bar
 *   width if it exceeds `max`.
 * @param {number} [props.sprayValue] - Spraywall part of the bar, drawn as a fainter
 *   segment AFTER the route part. It is ADDED to `value` (not a subset of it, unlike
 *   `flashValue`), so a caller passes the route amount as `value` and the spray
 *   amount here; `max` and the `count` text cover the total.
 * @param {"sm"|"md"|"lg"} [props.size="sm"] - Dimension preset; must equal the
 *   `size` of the enclosing `StatBarGroup`.
 */
export function StatBarRow({ color, label, max = 100, value = 0, flashValue = null, count, flashColor, nonFlashColorSuffix = "66", idealValue, sprayValue = 0, size = "sm" }) {
  const { th } = useAppTheme();
  const s = BAR_SIZES[size] || BAR_SIZES.sm;
  const safeMax = max > 0 ? max : 1;
  // Flash mode is opt-in via passing flashValue (even 0). Without it we render a
  // single solid bar (completion bars, distributions without flash data, etc.).
  const flashMode = flashValue != null;
  const fv = flashMode ? flashValue : 0;
  const flashPct = Math.max(0, Math.min(100, (fv / safeMax) * 100));
  const nonFlashPct = Math.max(0, Math.min(100, ((value - fv) / safeMax) * 100));
  const solid = flashColor || color;
  const nonFlashBg = flashMode ? (color + nonFlashColorSuffix) : solid;
  const hasIdeal = typeof idealValue === "number" && idealValue > 0;
  const idealPct = hasIdeal ? Math.max(0, Math.min(100, (idealValue / safeMax) * 100)) : 0;
  const sprayPct = sprayValue > 0 ? Math.max(0, Math.min(100 - flashPct - nonFlashPct, (sprayValue / safeMax) * 100)) : 0;
  return (
    <React.Fragment>
      {/* dot — same inset-shadow trick as the bar fills so dark
          colours stay visible against the dark theme background. */}
      <div style={{ width: s.dot, height: s.dot, borderRadius: "50%", background: color, boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }} />
      {/* label */}
      <div style={{
        fontSize: s.labelFontSize, color: th.text, minWidth: 0, maxWidth: s.labelMaxWidth,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </div>
      {/* bar — 1px inset shadow ring on each bar fill so dark colours
          (notably the black difficulty `#1f1f1f`) stay visible against
          the dark-theme container background (`#111`), and very light
          colours don't disappear into a light bg. The alpha is low
          enough that mid-range colours read unchanged.
          The rail (empty bar) is `th.bg` — the same trick on every
          size. It only LOOKS visibly "black" when the row sits inside
          a slightly-lighter card (`th.card`); a caller that drops the
          chart straight on the page bg has to add its own card
          wrapper or the rail will disappear into the parent. */}
      <div style={{ position: "relative", height: s.barHeight, background: th.bg, borderRadius: s.barRadius, overflow: "hidden", display: "flex" }}>
        {fv > 0 && (
          <div style={{ height: "100%", width: flashPct + "%", background: solid, minWidth: 2, transition: "width 0.3s", boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }} />
        )}
        {(value - fv) > 0 && (
          <div style={{ height: "100%", width: nonFlashPct + "%", background: nonFlashBg, minWidth: 2, transition: "width 0.3s", boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }} />
        )}
        {sprayValue > 0 && (
          <div style={{ height: "100%", width: sprayPct + "%", background: color + "33", minWidth: 2, transition: "width 0.3s", boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }} />
        )}
        {hasIdeal && (
          <div
            title={`Ideal: ${Math.round(idealValue)}`}
            style={{
              position: "absolute", top: -1, bottom: -1,
              left: `calc(${idealPct}% - 1px)`,
              width: 2, background: th.text, opacity: 0.85,
              pointerEvents: "none",
            }}
          />
        )}
      </div>
      {/* count */}
      <div style={{
        fontSize: s.countFontSize, fontWeight: s.countFontWeight, color: th.textMuted,
        ...tabularNum,
      }}>
        {count}
      </div>
    </React.Fragment>
  );
}

/**
 * ClimbingLevelCard — "current form" card combining a Top-N average grade with an
 * intensity-zone distribution bar. Inspired by KAYA's "Climbing Level": the Top-N
 * average is a stabler form indicator than a single hardest send, and the zone bar
 * (warmup / hard / limit, relative to your own ceiling) shows whether you mostly
 * cruise or project. Presentational only — callers compute the data (gym-local vs
 * cross-gym) and pass pre-translated strings, so this stays i18n-agnostic.
 *
 * @param {object} props.th - Theme object.
 * @param {string} [props.accent] - Default colour for the Top-N grade label.
 * @param {string} props.title - Card title (e.g. "Climbing level").
 * @param {string} props.topNGrade - Top-N average grade label (pre-formatted).
 * @param {string} [props.topNColor] - Colour for the grade label (defaults to accent).
 * @param {string} props.topNLabel - Sub-label, e.g. "Top-10 average".
 * @param {React.ReactNode} [props.note] - Optional faint line under the grade (pre-translated).
 * @param {string} props.zonesTitle - Heading above the zone bar.
 * @param {{warmup:number, hard:number, limit:number}} props.zones - Per-zone send counts.
 * @param {{warmup:string, hard:string, limit:string}} props.zoneLabels - Per-zone labels.
 * @param {object} [props.style] - Extra wrapper styles (e.g. marginBottom).
 */
export function ClimbingLevelCard({ title, topNGrade, topNColor, topNLabel, note = null, style }) {
  const { th, accent } = useAppTheme();
  return (
    <div style={{ padding: 12, borderRadius: 14, background: th.card, border: "1px solid " + th.border, ...style }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: th.text, marginBottom: 12 }}>{title}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: topNColor || accent }}>{topNGrade}</div>
        <div style={{ fontSize: 12, color: th.textMuted }}>{topNLabel}</div>
      </div>
      {/* `note`: an already-translated faint line about what the level counts
          (e.g. the Spraywall benchmark rule) — the card stays i18n-free. */}
      {note && <div style={{ fontSize: 12, color: th.textMuted, marginTop: 8 }}>{note}</div>}
    </div>
  );
}

/** Intensity-zone distribution bar — warmup / hard / limit shares.
 *
 *  Split out of ClimbingLevelCard: the card answers "what level do I climb at"
 *  from first sends and is timeless, while this bar answers "how did THIS
 *  PERIOD split between cruising and projecting" from climb volume. Two
 *  questions, two data sources, two time scopes — one card could not host both
 *  without lying about one of them. The ceiling it measures against is the
 *  hardest first send (NOT the card's Top-N average), and the split is counted
 *  in difficulty bands below it — both of which `hint` says out loud wherever
 *  the bar is placed.
 */
export function IntensityZoneBar({ title, zones, zoneLabels, hint, style }) {
  const { th, accent } = useAppTheme();
  const z = zones || {};
  const total = (z.warmup || 0) + (z.hard || 0) + (z.limit || 0);
  // Monochrome accent ramp (pale → saturated = warmup → limit). Saturated
  // traffic-light colours (green/amber/red) would clash with the gym's own
  // difficulty / hold colours — a green "warmup" segment reads as the green
  // difficulty grade. A single-hue ramp can never be misread that way; the
  // labels carry the meaning, the opacity carries the intensity.
  const ac = accent || "#888";
  const segs = [
    { key: "warmup", val: z.warmup || 0, color: ac + "40", label: zoneLabels.warmup },
    { key: "hard", val: z.hard || 0, color: ac + "90", label: zoneLabels.hard },
    { key: "limit", val: z.limit || 0, color: ac, label: zoneLabels.limit },
  ];
  return (
    <div style={style}>
      <div style={{ fontSize: 13, fontWeight: 700, color: th.text, marginBottom: 8 }}>{title}</div>
      <div style={{ display: "flex", height: 14, borderRadius: 999, overflow: "hidden", background: th.bg, boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }}>
        {total > 0 && segs.map(s => s.val > 0 ? (
          <div key={s.key} title={`${s.label}: ${s.val}`} style={{ width: (s.val / total * 100) + "%", background: s.color, minWidth: 2, boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }} />
        ) : null)}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
        {segs.map(s => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 10, height: 10, borderRadius: 4, background: s.color, boxShadow: "inset 0 0 0 1px rgba(128,128,128,0.35)" }} />
            <span style={{ fontSize: 12, color: th.textMuted }}>{s.label} {total > 0 ? Math.round(s.val / total * 100) : 0}%</span>
          </div>
        ))}
      </div>
      {hint && <div style={{ fontSize: 12, color: th.textMuted, marginTop: 8 }}>{hint}</div>}
    </div>
  );
}

/** Character counter shown below text inputs.
 *  Only visible when the user has typed ≥ 80% of the limit — the counter is a
 *  warning, not decoration, so it stays out of the way until the limit matters.
 *
 *  `always` opts out of that: on the short/full description pair the budget IS
 *  the point (the short one exists to stay short, and you plan a long one
 *  against 4000), so hiding the number until 80% left the author guessing. */
export function CharCount({ value, max, always = false }) {
  const { th: theme } = useAppTheme();
  const len = (value || "").length;
  const ratio = len / max;
  if (!always && ratio < 0.8) return null;
  const color = ratio >= 1 ? "#dc2626" : (theme?.textMuted || "#999");
  return (
    <div style={{ fontSize: 12, color, marginTop: 2, ...tabularNum }}>
      {len}/{max}
    </div>
  );
}

/** Long-form text behind a labelled disclosure row, collapsed by default.
 *
 *  The short description of a competition/event is shown fixed on the card;
 *  the long one lives here, so a full A4 worth of text never pushes the
 *  registration button below the fold. Renders nothing when `text` is empty,
 *  which is what every consumer relies on instead of guarding the call site.
 *
 *  `label` is passed in (not translated here) — `ui.js` stays i18n-free. */
export function CollapsibleText({ text, label, defaultOpen = false, onOpen, style }) {
  const { th, accent } = useAppTheme();
  const [open, setOpen] = useState(defaultOpen);
  const body = (text || "").trim();
  if (!body) return null;
  return (
    <div style={{
      border: "1px solid " + th.border, borderRadius: 10,
      marginBottom: 10, overflow: "hidden", ...style,
    }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => { setOpen(o => { if (!o) onOpen?.(); return !o; }); }}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 8,
          padding: "8px 12px", background: "transparent", border: "none",
          cursor: "pointer", fontFamily: F, textAlign: "left",
        }}
      >
        <span style={{
          fontSize: 12, color: th.textMuted, display: "inline-block",
          transition: "transform 0.2s", transform: open ? "rotate(90deg)" : "rotate(0deg)",
        }}>&#9654;</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: accent }}>{label}</span>
      </button>
      {/* Body padding is symmetric with the header's own 12px, NOT indented to
          line up under the label. Aligning to the label cost 32px on the left
          while the right kept 12 — so the text sat further in from its own box
          than the box sits from its container, and ran visibly closer to the
          edge on the right. On a long description that lopsidedness is the
          first thing you notice. */}
      {open && (
        <div style={{
          padding: "0 12px 12px", fontSize: 13, lineHeight: 1.6,
          color: th.text, whiteSpace: "pre-wrap", ...SELECTABLE,
        }}>
          {body}
        </div>
      )}
    </div>
  );
}

/* ==========================================================================
 * ViewportLayer — the shared edge-anchored `position: fixed` strip that every
 * app-level overlay sits in (toast stack, update banner, cookie bar).
 *
 * `position: fixed` resolves against the LAYOUT viewport, which on mobile is
 * not the box the user can actually see. Two states pull the two apart, and
 * both are reachable from the competition screens:
 *
 *   - Pinch / double-tap zoom. The viewport meta deliberately keeps
 *     `user-scalable=yes`, and both scoring surfaces are small buttons tapped
 *     in fast succession (JAM +/- counters, the judge's TOP/ZONE/FALL row) —
 *     the cadence a browser reads as double-tap-to-zoom. `touch-action:
 *     manipulation` in index.html stops the gesture; this stops the fallout
 *     from a page that is zoomed for any other reason.
 *   - Software keyboard. The judge modal auto-focuses its climber search on
 *     every return to the competitor list, so it pops open while the undo
 *     toast for the attempt just recorded is still on screen.
 *
 * In either state a `bottom: 0` layer anchors below the visible area and reads
 * as floating mid-screen, drifting with every pan — which is exactly how the
 * undo toast behaved after recording a competition attempt.
 *
 * The correction is a pure inset: the strip moves UP from the layout viewport
 * bottom by however much of it is hidden. It is never re-anchored to a
 * computed `top` — chasing `top: visualBottom - height` is what stranded the
 * body-scroll fade mid-screen in 3bcf16c (see WindowScrollFadeBottom). When
 * the visual viewport matches the window — desktop, and a PWA with no keyboard
 * and no zoom — every inset is 0 and this renders identically to the plain
 * `left/right/bottom: 0` layer it replaces.
 * ========================================================================= */

/**
 * Pins a fixed layer to the visible viewport edge. Writes straight to the
 * node's style rather than through state: visualViewport `scroll` fires on
 * every pan frame, and re-rendering the stack per frame would restart each
 * toast's CSS countdown animation from the top.
 */
function useVisualViewportPin(ref, anchor) {
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    // Without the API the plain layout-viewport anchor is all there is.
    if (!vv) return;
    let raf = 0;
    const apply = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const node = ref.current;
        if (!node) return;
        // Clamped: iOS rubber-band overscroll drives offsetTop past both ends
        // for a few frames, and a negative inset would throw the layer off the
        // opposite edge mid-bounce.
        const px = (n) => `${Math.max(0, Math.round(n))}px`;
        node.style.left = px(vv.offsetLeft);
        node.style.right = "auto";
        node.style.width = px(vv.width);
        if (anchor === "top") node.style.top = px(vv.offsetTop);
        else node.style.bottom = px(window.innerHeight - (vv.offsetTop + vv.height));
      });
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, [ref, anchor]);
}

/**
 * ViewportLayer — full-width fixed strip glued to the visible top or bottom
 * edge. Children are centred and stack in a column.
 *
 * @param {"top"|"bottom"} [props.anchor="bottom"]
 * @param {number}  [props.zIndex=2000]
 * @param {number}  [props.gap=8]     - gap between stacked children
 * @param {string|number} [props.padding] - overrides the default edge gutter
 *
 * The strip spans the full width but is `pointer-events: none`, so the app
 * underneath stays clickable everywhere the overlay is not actually painted.
 * Children opt back in with `pointerEvents: "auto"` (see ToastRow).
 */
export function ViewportLayer({
  anchor = "bottom", zIndex = 2000, gap = 8, padding,
  children, style, ...rest
}) {
  const ref = useRef(null);
  useVisualViewportPin(ref, anchor);
  const gutter = anchor === "top"
    ? "calc(env(safe-area-inset-top, 0px) + 10px) 12px 0"
    : "0 12px calc(env(safe-area-inset-bottom, 0px) + 16px)";
  return (
    <div ref={ref} {...rest} style={{
      position: "fixed", left: 0, right: 0, [anchor]: 0,
      display: "flex", flexDirection: "column", alignItems: "center", gap,
      // border-box because the pin writes an explicit px width onto the node.
      boxSizing: "border-box",
      padding: padding ?? gutter,
      pointerEvents: "none",
      zIndex,
      ...style,
    }}>
      {children}
    </div>
  );
}

/**
 * FocusLayer — the full-window working layer of ONE editor (today the
 * Spraywall hold editor's focus view, docs/spec/21p-spraywall-hold-editor.md).
 *
 * Not a modal: no dimming, no backdrop click, no `role="dialog"`. A dialog or
 * a `useConfirm` opened from inside it stays an ordinary `ModalBackdrop`
 * layer, which is why the layer sits at 90 — under the 100+ modal band and
 * the toast strip, over the pinned header and the FABs. Escape is the
 * caller's `useEscapeKey`, like every other layer: the layer renders its
 * children one `EscapeDepthContext` level deeper (as `ModalBackdrop` does),
 * so a handler registered inside it takes the key before the page's own,
 * and a dialog opened inside it before that handler.
 *
 * Portaled into `<body>` so an ancestor that opens a containing block (a
 * `transform`, a `filter`) cannot shrink the fixed layer to its own box.
 * `statusBarInset()` keeps an iPad PWA's status bar off the top bar, and a
 * `ScrollLock` keeps the page under it still.
 *
 * Also the full-screen marking view of the spray problem editor (21k): a view
 * that covers the whole window must not be a `ModalBackdrop`, whose dim is what
 * `StatusBarTintBar` paints behind the iOS status bar.
 *
 * @param {number} [props.zIndex=90]
 * @param {function} [props.onEscape] - Escape inside the layer (registered one
 *   level deeper than the page, so it takes the key first)
 */
function FocusLayerEscape({ onEscape }) {
  useEscapeKey(onEscape, true);
  return null;
}

export function FocusLayer({ children, zIndex = 90, onEscape }) {
  const { th } = useAppTheme();
  const depth = useContext(EscapeDepthContext);
  const layer = (
    <EscapeDepthContext.Provider value={depth + 1}>
    <div
      data-testid="focus-layer"
      style={{
        position: "fixed", inset: 0, zIndex,
        background: th.bg, color: th.text, fontFamily: F,
        paddingTop: statusBarInset(), boxSizing: "border-box",
        display: "flex", flexDirection: "column",
      }}
    >
      {/* The page under the layer must not scroll along with a drag that runs past the photo. */}
      <ScrollLock />
      {onEscape ? <FocusLayerEscape onEscape={onEscape} /> : null}
      {children}
    </div>
    </EscapeDepthContext.Provider>
  );
  return typeof document !== "undefined" && document.body ? ReactDOM.createPortal(layer, document.body) : layer;
}

/* ==========================================================================
 * Toast system — lightweight global toasts with optional undo button.
 *
 * Wrap the app in <ToastProvider> and use `useToast()` from any child:
 *   const { push, dismiss } = useToast();
 *   const id = push({ text: "Logged: Blue V5", undoLabel: "Undo", onUndo, durationMs: 5000 });
 *
 * The toast auto-dismisses after `durationMs` (default 4000ms) with a shrinking
 * progress bar. Clicking `onUndo` calls the callback and dismisses immediately.
 * ========================================================================= */
const ToastCtx = createContext(null);
const ConfirmCtx = createContext(null);

/** Hook: returns `{ push, dismiss }` for showing/hiding toasts. */
/**
 * Copy text to the clipboard, with the outcome reported through the toast.
 * Eight call sites did this by hand in two styles, one of them with no
 * `catch` — on a browser that refuses the write (no permission, insecure
 * context) that one threw an unhandled rejection and showed nothing. Returns
 * whether the copy succeeded, for callers that flip an inline "copied" flag.
 */
export function useCopyToClipboard() {
  const toast = useToast();
  return useCallback(async (text, { okText, failText } = {}) => {
    try {
      await navigator.clipboard.writeText(text);
      if (okText) toast.push({ text: okText });
      return true;
    } catch {
      if (failText) toast.push({ text: failText, durationMs: 5000 });
      return false;
    }
  }, [toast]);
}

export function useToast() {
  return useContext(ToastCtx) || { push: () => "", dismiss: () => {} };
}

/**
 * Hook: run an async action behind an indeterminate busy toast.
 *
 *   const runBusy = useBusyAction();
 *   const res = await runBusy(() => doTheThing(), {
 *     text: t("thing.running"), errorText: t("thing.failed"),
 *   });
 *
 * For the multi-second operations an admin fires from a menu item or a card:
 * closing a competition (rankings + the climbs sync), mirroring a closed
 * competition's tops into statistics. Those surfaces close on click, so there
 * is no button left to disable and no spinner slot to fill — without this the
 * app simply goes quiet for a few seconds.
 *
 * Returns the action's own return value; `undefined` if it threw, or if
 * another run is still in flight. That guard is the point: two overlapping
 * closes or syncs cannot be prevented by a disabled button that isn't there.
 *
 * `errorText` is the prefix for the failure toast (the thrown message is
 * appended). Omit it only when the caller surfaces the failure itself.
 */
export function useBusyAction() {
  const toast = useToast();
  const busyRef = useRef(false);
  return useCallback(async (fn, opts = {}) => {
    // 10 minutes: a safety net, not an expectation. The toast is dismissed by
    // hand the moment the action settles; this only stops a call that never
    // does from leaving a permanent toast on screen.
    const { text, errorText, durationMs = 600000 } = opts;
    if (busyRef.current) return undefined;
    busyRef.current = true;
    const busyId = toast.push({ text, indeterminate: true, durationMs });
    try {
      return await fn();
    } catch (e) {
      if (errorText) {
        const detail = callableErrorText(e, "");
        toast.push({ text: errorText + (detail ? ` — ${detail}` : ""), durationMs: 6000 });
      } else {
        console.error("busy action failed:", e);
      }
      return undefined;
    } finally {
      toast.dismiss(busyId);
      busyRef.current = false;
    }
  }, [toast]);
}

/**
 * Hook: returns an async confirm fn that resolves to true/false.
 * Usage:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, body, danger: true }))) return;
 * Outside a ToastProvider it degrades to a resolve-false stub.
 */
export function useConfirm() {
  return useContext(ConfirmCtx) || (() => Promise.resolve(false));
}

/**
 * ToastProvider — mount once near the app root. Renders a fixed stack of
 * toasts at the bottom-center of the viewport.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  // Single-slot confirm dialog managed by useConfirm(). One open confirm at
  // a time is enough — real flows never stack confirm prompts.
  const [confirmState, setConfirmState] = useState(null);

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) { clearTimeout(timer); timersRef.current.delete(id); }
    setToasts(list => list.filter(t => t.id !== id));
  }, []);

  const push = useCallback((opts) => {
    const id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    const durationMs = opts.durationMs ?? 4000;
    const expiresAt = Date.now() + durationMs;
    setToasts(list => [...list, { id, ...opts, durationMs, expiresAt }]);
    // Call onExpire (e.g. commit deferred write) when the toast auto-dismisses
    // without an undo. We delay by +40ms so the bar animation finishes cleanly.
    const timer = setTimeout(() => {
      try { opts.onExpire?.(); } catch { /* swallow — toast must still vanish */ }
      setToasts(list => list.filter(t => t.id !== id));
      timersRef.current.delete(id);
    }, durationMs + 40);
    timersRef.current.set(id, timer);
    return id;
  }, []);

  const confirm = useCallback((config) => new Promise((resolve) => {
    setConfirmState({ ...(config || {}), resolve });
  }), []);

  const closeConfirm = useCallback((value) => {
    setConfirmState(prev => {
      if (prev) { try { prev.resolve(value); } catch {} }
      return null;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    timersRef.current.forEach(t => clearTimeout(t));
    timersRef.current.clear();
  }, []);

  const ctx = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastCtx.Provider value={ctx}>
    <ConfirmCtx.Provider value={confirm}>
      {children}
      <ViewportLayer aria-live="polite" aria-atomic="true" anchor="bottom" zIndex={2000}>
        {toasts.map(t => (
          <ToastRow key={t.id} toast={t} onClose={dismiss} />
        ))}
      </ViewportLayer>
      <ConfirmModal
        open={!!confirmState}
        onClose={() => closeConfirm(false)}
        onConfirm={() => closeConfirm(true)}
        onSecondary={confirmState?.secondaryLabel ? () => closeConfirm("secondary") : undefined}
        title={confirmState?.title}
        body={confirmState?.body}
        confirmLabel={confirmState?.confirmLabel}
        cancelLabel={confirmState?.cancelLabel}
        secondaryLabel={confirmState?.secondaryLabel}
        danger={confirmState?.danger}
        typedConfirm={confirmState?.typedConfirm}
      />
    </ConfirmCtx.Provider>
    </ToastCtx.Provider>
  );
}

function ToastRow({ toast, onClose, }) {
  const { accent, th } = useAppTheme();
  const { id, text, undoLabel, onUndo, durationMs, indeterminate } = toast;

  const handleUndo = () => {
    try { onUndo?.(); } catch { /* user's onUndo should be safe */ }
    onClose(id);
  };

  return (
    <div role="status" style={{
      pointerEvents: "auto",
      minWidth: 260, maxWidth: 480, width: "100%",
      background: th.card, color: th.text,
      border: "1px solid " + th.border, borderRadius: 14,
      boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
      overflow: "hidden", fontFamily: F,
      animation: "sendly-toast-in 0.18s ease-out",
    }}>
      <style>{`
        @keyframes sendly-toast-in { from { opacity: 0; transform: translateY(8px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes sendly-toast-countdown { from { transform: scaleX(1);} to { transform: scaleX(0);} }
        @keyframes sendly-toast-sweep { from { transform: translateX(-100%);} to { transform: translateX(250%);} }
      `}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{text}</div>
        {onUndo && (
          <button onClick={handleUndo} style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: accent, fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
            textTransform: "uppercase", padding: "6px 8px", fontFamily: F,
          }}>{undoLabel || "Undo"}</button>
        )}
        {/* Hit area expanded via padding + min dims; icon itself stays small. */}
        <IconBtn icon={IconClose} tone="muted" ariaLabel="Dismiss" onClick={() => onClose(id)}
          padding="8px 10px" style={{ minWidth: 36, minHeight: 36 }} />
      </div>
      {/* Countdown by default; a sweeping bar when the caller passes
          `indeterminate` for an operation still running. Deliberately NOT a
          percentage: the only jobs that use this are single round trips with
          no progress to report, and a fake percentage that jumps 0 → 100 is
          worse than an honest "working on it". `durationMs` still applies as
          a safety net, so a call that never settles cannot leave the toast up
          forever. */}
      <div style={{ height: 4, background: th.border, overflow: "hidden" }}>
        <div style={indeterminate ? {
          height: "100%", width: "40%", background: accent,
          animation: "sendly-toast-sweep 1.1s ease-in-out infinite",
        } : {
          height: "100%", width: "100%", background: accent,
          transformOrigin: "left center",
          animation: `sendly-toast-countdown ${durationMs}ms linear forwards`,
        }} />
      </div>
    </div>
  );
}

/** The red of a destructive dialog: the confirm button, the accent strip, and
 *  the type-to-confirm field's border once the word matches. */
const DIALOG_DANGER = "#dc2626";

/** Shared by the two dialogs when no theme is passed (a confirm can be raised
 *  from a code path that has none). One object so the two cannot drift. */
const DIALOG_THEME = { card: "#fff", text: "#111", border: "#ddd", textMuted: "#666", inputBg: "#fff" };

/** Every dialog button, before its role colours it. */
const DIALOG_BTN = { padding: "10px 18px", borderRadius: 10, fontSize: 14, cursor: "pointer", fontFamily: F };

/**
 * DialogShell — the small centred dialog that `ConfirmModal` and `InputModal`
 * both are: a 5px accent strip, a title, a body paragraph, and then whatever
 * the particular dialog needs. They shared this trunk byte for byte.
 *
 * Not `ModalScrollBody`: a dialog is short by construction — a question and
 * two buttons — so it does not scroll and has nothing to fade. A dialog that
 * needs a scrollport is not a dialog any more; it is a modal.
 *
 * @param {function} props.onClose
 * @param {string}   [props.title]
 * @param {string}   [props.body]       - pre-wrapped, so 
 survives
 * @param {string}   [props.stripColor] - accent, or the danger red
 * @param {number}   [props.maxWidth=420]
 * @param {Object}   [props.theme]
 */
export function DialogShell({ children, onClose, title, body, stripColor = DEFAULT_ACCENT, maxWidth = 420, }) {
  const { th: theme } = useAppTheme();
  const th = theme || DIALOG_THEME;
  return (
    <ModalBackdrop onClose={onClose} ariaLabel={title} zIndex={3000} dim={0.5}>
      <div style={{
        width: "100%", maxWidth, background: th.card, color: th.text,
        borderRadius: MODAL_RADIUS, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden",
      }}>
        <div style={{ height: 5, background: stripColor }} />
        <div style={{ padding: 20 }}>
          {title && <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8 }}>{title}</div>}
          {body && <div style={{ fontSize: 14, color: th.textMuted, lineHeight: 1.45, marginBottom: 14, whiteSpace: "pre-wrap" }}>{body}</div>}
          {children}
        </div>
      </div>
    </ModalBackdrop>
  );
}

/**
 * DialogButtons — the right-aligned row a dialog ends with: cancel, an optional
 * secondary action, and the confirm.
 *
 * `disabled` is what the type-to-confirm field drives: the confirm greys out to
 * `th.border` and stops responding until the word matches. It wraps because the
 * three-button variant (cancel + secondary + a danger CTA) can outgrow a phone.
 *
 * @param {Object}   props.theme
 * @param {string}   [props.accent]
 * @param {string}   props.cancelLabel
 * @param {function} props.onCancel
 * @param {string}   [props.secondaryLabel] - rendered only with `onSecondary`
 * @param {function} [props.onSecondary]
 * @param {string}   props.confirmLabel
 * @param {function} props.onConfirm
 * @param {boolean}  [props.danger=false]
 * @param {boolean}  [props.disabled=false]
 */
export function DialogButtons({

  cancelLabel, onCancel, secondaryLabel, onSecondary, confirmLabel, onConfirm,
  danger = false, disabled = false,
}) {
  const { th, accent } = useAppTheme();
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
      <button onClick={onCancel} style={{
        ...DIALOG_BTN, border: "1px solid " + th.border,
        background: "transparent", color: th.text, fontWeight: 700,
      }}>{cancelLabel}</button>
      {secondaryLabel && onSecondary && (
        <button onClick={onSecondary} style={{
          ...DIALOG_BTN, border: "1px solid " + accent,
          background: "transparent", color: accent, fontWeight: 800,
        }}>{secondaryLabel}</button>
      )}
      <button onClick={disabled ? undefined : onConfirm} disabled={disabled} style={{
        ...DIALOG_BTN, border: "none",
        background: disabled ? th.border : (danger ? DIALOG_DANGER : accent),
        color: "#fff", fontWeight: 800,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}>{confirmLabel}</button>
    </div>
  );
}

/* ==========================================================================
 * ConfirmModal — in-app replacement for window.confirm / window.prompt.
 *
 * <ConfirmModal open onClose={...} onConfirm={...} title="..." body="..." />
 *
 * Variants:
 *  - `danger` styles the confirm button red
 *  - Pass `typedConfirm={{ word, label, placeholder }}` to require typing a word
 *    before the confirm button activates (e.g. "DELETE"). Matching is
 *    case- and accent-insensitive (`confirmWordMatches`).
 * ========================================================================= */
export function ConfirmModal({
  open, onClose, onConfirm, title, body,
  // Default labels fall back to the active language from LanguageContext.
  // Callers can still override either label with a custom translated string.
  confirmLabel, cancelLabel,
  // Optional secondary action (e.g. "Archive" alongside confirm "Delete").
  secondaryLabel, onSecondary,
  danger = false,
  typedConfirm, // { word, label, placeholder }
}) {
  const { accent, th: theme } = useAppTheme();
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.confirm');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const [typed, setTyped] = useState("");
  useEffect(() => { if (open) setTyped(""); }, [open]);
  if (!open) return null;
  const th = theme || DIALOG_THEME;
  const requiresType = !!typedConfirm?.word;
  // Same forgiving match as the leave-gym / delete-account danger zones:
  // case and accents are ignored, so "torles" opens a "TÖRLÉS" gate. The gate
  // is there to stop a misclick, not to test typing.
  const canConfirm = !requiresType || confirmWordMatches(typed, typedConfirm.word);

  return (
    // 2 buttons (cancel + confirm) fit comfortably at 420; the 3-button variant
    // (cancel + secondary + danger) needs more room so they stay on a single row
    // instead of wrapping the danger CTA onto its own line on roomy phones.
    <DialogShell
      onClose={onClose} title={title} body={body}
      stripColor={danger ? DIALOG_DANGER : accent}
      maxWidth={secondaryLabel && onSecondary ? 520 : 420}
    >
      {requiresType && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: th.textMuted, marginBottom: 6 }}>
            {typedConfirm.label || t('common.typedConfirmLabel', { word: typedConfirm.word })}
          </div>
          <TextInput autoFocus type="text" value={typed} onChange={e => setTyped(e.target.value)}
            placeholder={typedConfirm.placeholder || typedConfirm.word}
            // A semantic border: it turns danger-red the moment the typed word
            // matches, which is the only feedback that the gate has opened.
            style={{ border: "1.5px solid " + (canConfirm ? DIALOG_DANGER : th.border) }}
          />
        </div>
      )}
      <DialogButtons
 danger={danger} disabled={!canConfirm}
        cancelLabel={resolvedCancelLabel} onCancel={onClose}
        secondaryLabel={secondaryLabel} onSecondary={onSecondary}
        confirmLabel={resolvedConfirmLabel} onConfirm={onConfirm}
      />
    </DialogShell>
  );
}

/**
 * InputModal — in-app replacement for `window.prompt` when the user must
 * type a value. Supports text or numeric input. Confirm resolves to the
 * entered string (or number if `inputType: "number"`); cancel resolves
 * to `null`. Use via `useInputPrompt()` hook for Promise-style usage.
 */
export function InputModal({
  open, onClose, onConfirm, title, body, inputType = "text",
  inputLabel, placeholder, defaultValue = "", min, max, step,
  confirmLabel, cancelLabel,

}) {
  const { accent } = useAppTheme();
  const { t } = useTranslation();
  const resolvedConfirmLabel = confirmLabel ?? t('common.ok');
  const resolvedCancelLabel = cancelLabel ?? t('common.cancel');
  const [value, setValue] = useState("");
  useEffect(() => { if (open) setValue(defaultValue == null ? "" : String(defaultValue)); }, [open, defaultValue]);
  if (!open) return null;
  const submit = () => {
    if (inputType === "number") {
      const n = parseFloat(value);
      if (Number.isNaN(n)) return;
      onConfirm?.(n);
    } else {
      onConfirm?.(value);
    }
  };
  return (
    <DialogShell onClose={onClose} title={title} body={body} stripColor={accent}>
      {/* The label points at the field (`Field`) — it was a loose div, so the
          field had no name; without a label the dialog's title names it. */}
      <Field label={inputLabel || null} labelStyle={{ marginBottom: 6 }}>
        <TextInput
          autoFocus type={inputType === "number" ? "number" : "text"}
          inputMode={inputType === "number" ? "decimal" : undefined}
          value={value} onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") submit(); }}
          placeholder={placeholder}
          aria-label={inputLabel ? undefined : title}
          min={min} max={max} step={step}
          size="lg"
          style={{ marginBottom: 14 }}
        />
      </Field>
      <DialogButtons

        cancelLabel={resolvedCancelLabel} onCancel={onClose}
        confirmLabel={resolvedConfirmLabel} onConfirm={submit}
      />
    </DialogShell>
  );
}

/** Google "G" logo SVG for the login button. */
export function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

export function AppleIcon() {
  // Monochrome Apple logo — inherits the button's text colour via currentColor
  // so it reads correctly in both light and dark themes.
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.54c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.09-2.01-3.76-2.04-1.6-.16-3.12.94-3.93.94-.81 0-2.06-.92-3.39-.9-1.74.03-3.35 1.01-4.25 2.57-1.81 3.14-.46 7.79 1.3 10.34.86 1.25 1.89 2.65 3.23 2.6 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.39.81 1.4-.02 2.28-1.27 3.13-2.53.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.72-1.04-2.75-4.13zM14.47 4.84c.71-.86 1.19-2.06 1.06-3.25-1.02.04-2.26.68-3 1.54-.66.76-1.24 1.98-1.08 3.15 1.14.09 2.3-.58 3.02-1.44z"/>
    </svg>
  );
}

/**
 * OverflowMenu — Dropdown "more" menu for action overflow.
 *
 * items: [
 *   { type: "section", label },           // section header
 *   { type: "divider" },                  // separator line
 *   { label, icon?, onClick, hidden?, danger? },  // action item
 * ]
 *
 * If only 1 visible normal item exists (no sections/dividers), renders inline button instead.
 */
export function OverflowMenu({ label = "⋯", items = [], align = "left", compact = false, ariaLabel, triggerStyle: triggerStyleOverride }) {
  const { th } = useAppTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  // Viewport coordinates for the open panel, null until it has been measured.
  // The panel is PORTALED to <body> and positioned by hand rather than sitting
  // absolutely inside the wrapper, because an absolute panel can only paint as
  // high as its nearest stacking context allows — and callers put this menu
  // inside cards that quietly open one. A closed competition's card is drawn
  // at `opacity: 0.8`, which IS a stacking context, so `Z_DROPDOWN` ranked the
  // panel within that one card and the next card in the list painted straight
  // over the open menu. A portal has no ancestor left to be trapped by (and
  // the panel stops inheriting the card's fade).
  const [pos, setPos] = useState(null); // { top, left, maxHeight } in viewport coords

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    const trigger = ref.current;
    const panel = panelRef.current;
    if (!trigger || !panel) return;
    const MARGIN = 8;
    const t = trigger.getBoundingClientRect();
    const r = panel.getBoundingClientRect();
    // Horizontal: `align` only says which edge to PREFER; it cannot know where
    // the trigger sits. A card's ⋯ button is mid-row, so a 220px left-aligned
    // panel ran off the right edge of a phone — clamp it back in. A panel
    // wider than the viewport can't satisfy both sides, so the left margin
    // wins and the labels stay readable from the start.
    let left = align === "right" ? t.right - r.width : t.left;
    const maxLeft = window.innerWidth - MARGIN - r.width;
    if (left > maxLeft) left = maxLeft;
    if (left < MARGIN) left = MARGIN;
    // Vertical: below the trigger, flipped above it when it fits there and not
    // below. A fixed panel cannot be scrolled into view — a menu opened on the
    // last card would just be unreachable. When it fits on NEITHER side (a long
    // admin ⋯ menu on a short phone screen) it takes the roomier side, capped
    // to that room, and scrolls inside: left at full height below, its bottom
    // items were clipped by `overflow: hidden` and could not be reached at all.
    const spaceBelow = window.innerHeight - MARGIN - (t.bottom + 4);
    const spaceAbove = t.top - 4 - MARGIN;
    let top = t.bottom + 4;
    let maxHeight = null;
    if (r.height > spaceBelow) {
      if (r.height <= spaceAbove) {
        top = t.top - 4 - r.height;
      } else if (spaceAbove > spaceBelow) {
        top = MARGIN;
        maxHeight = spaceAbove;
      } else {
        maxHeight = spaceBelow;
      }
    }
    setPos({ top, left, maxHeight });
  }, [open, align]);

  // A hand-positioned panel goes stale the moment anything moves under it.
  // Closing beats chasing: this menu is transient, and a scroll or a rotate is
  // the user looking away from it. `capture` so an ancestor's scroll counts,
  // not only the window's.
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      // A capped panel scrolls inside itself; that is the menu being used,
      // not the page moving under it.
      if (e?.type === "scroll" && panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    // The panel lives in a portal, so it is NOT inside `ref` — without the
    // second test, mousedown on a menu item would unmount the panel before
    // that item's own click could fire, and every entry would be dead.
    const handler = (e) => {
      if (ref.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Escape closes the menu through the shared stack, so a menu opened inside
  // a modal takes the key instead of dismissing the modal under it.
  useEscapeKey(() => setOpen(false), open);

  const handleItemClick = useCallback((onClick) => {
    setOpen(false);
    onClick?.();
  }, []);

  // Filter hidden items for visibility calculations
  const visibleItems = items.filter(it => !it.hidden);

  // Compute which sections are empty (all their normal items are hidden)
  // Walk through visibleItems and strip empty sections + trailing dividers
  const resolvedItems = [];
  for (let i = 0; i < visibleItems.length; i++) {
    const it = visibleItems[i];
    if (it.type === "section") {
      // Check if next items (until next section/divider/end) have at least one normal item
      let hasContent = false;
      for (let j = i + 1; j < visibleItems.length; j++) {
        if (visibleItems[j].type === "section" || visibleItems[j].type === "divider") break;
        hasContent = true; break;
      }
      if (hasContent) resolvedItems.push(it);
    } else if (it.type === "divider") {
      // Only add divider if there's content after it and content before it
      if (resolvedItems.length > 0 && resolvedItems[resolvedItems.length - 1]?.type !== "divider") {
        const hasAfter = visibleItems.slice(i + 1).some(x => !x.type || x.type === "section");
        if (hasAfter) resolvedItems.push(it);
      }
    } else {
      resolvedItems.push(it);
    }
  }

  const normalItems = resolvedItems.filter(it => !it.type);

  // If only 1 normal item and no sections/dividers, render inline button.
  // `compact` opts out: its callers rely on a fixed-size square trigger to keep
  // a dense row from reflowing, and a labelled pill would defeat that.
  if (!compact && normalItems.length === 1 && resolvedItems.length === 1) {
    const it = normalItems[0];
    return (
      <button onClick={it.onClick} style={{
        padding: "6px 14px", borderRadius: 10, border: "1px solid " + (th?.border || "#ddd"),
        background: "transparent", color: it.danger ? "#A32D2D" : (th?.text || "#333"),
        fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: F,
        display: "flex", alignItems: "center", gap: 6,
      }}>
        {it.icon && <span>{it.icon}</span>}{it.label}
      </button>
    );
  }

  if (normalItems.length === 0) return null;

  // `compact` — an icon-only 32×32 square with no chevron, for dense rows where
  // the trigger must never change width. `triggerStyle` lets a caller retint it
  // when the menu sits on a non-neutral surface (the violet registration block).
  const triggerStyle = compact ? {
    width: 32, height: 32, padding: 0,
    borderRadius: 10, border: "1px solid " + (th?.border || "#ddd"),
    background: "transparent", color: th?.textMuted || "#666",
    fontSize: 15, fontWeight: 800, lineHeight: 1, cursor: "pointer", fontFamily: F,
    display: "flex", alignItems: "center", justifyContent: "center",
    ...triggerStyleOverride,
  } : {
    padding: BTN_PAD.sm, borderRadius: 10, border: "1px solid " + (th?.border || "#ddd"),
    background: "transparent", color: th?.textMuted || "#666",
    fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: F,
    display: "flex", alignItems: "center", gap: 4,
    ...triggerStyleOverride,
  };

  // The wrapper is what sits in the caller's flex row, so the "never shrink"
  // rule belongs on it — putting it on the button would leave the wrapper free
  // to collapse and the trigger would still get squeezed.
  return (
    <div ref={ref} style={{ position: "relative", ...(compact ? { flex: "0 0 32px" } : null) }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        style={triggerStyle}
      >
        {label}{!compact && <> <span style={{ fontSize: 12 }}>▾</span></>}
      </button>
      {open && ReactDOM.createPortal(
        <div ref={panelRef} role="menu" style={{
          position: "fixed",
          top: pos ? pos.top : 0,
          left: pos ? pos.left : 0,
          // Hidden for the one frame between mounting and being measured —
          // it has to be in the DOM to have a size, but must not be seen
          // sitting in the top-left corner on the way there.
          visibility: pos ? "visible" : "hidden",
          zIndex: Z_DROPDOWN,
          background: th?.card || "#fff",
          border: "1px solid " + (th?.border || "#ddd"),
          borderRadius: 14,
          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          minWidth: 220,
          // Never wider than the phone it opens on. minWidth would otherwise
          // win on a 320px screen and force the panel off-screen regardless of
          // the clamp above.
          maxWidth: "calc(100vw - 16px)",
          ...(pos?.maxHeight != null
            ? { maxHeight: pos.maxHeight, overflowX: "hidden", overflowY: "auto", overscrollBehavior: "contain" }
            : { overflow: "hidden" }),
          padding: 4,
        }}>
          {resolvedItems.map((it, idx) => {
            if (it.type === "divider") {
              return <div key={`d${idx}`} style={{ height: 0.5, background: th?.border || "#ddd", margin: "4px 0" }} />;
            }
            if (it.type === "section") {
              return (
                <div key={`s${idx}`} style={{
                  fontSize: 12, fontWeight: 800, color: th?.textMuted || "#999",
                  padding: "6px 12px 2px", textTransform: "uppercase", letterSpacing: 0.5,
                }}>
                  {it.label}
                </div>
              );
            }
            return (
              <button key={idx} role="menuitem" onClick={() => handleItemClick(it.onClick)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "8px 12px", border: "none", borderRadius: 8,
                background: "transparent", color: it.danger ? "#A32D2D" : (th?.text || "#333"),
                fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: F,
                textAlign: "left",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = th?.bg || "#f5f5f5"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                {it.icon && <span style={{ flexShrink: 0 }}>{it.icon}</span>}
                <span>{it.label}</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

/* ==========================================================================
 * The card family — CardShell, FactRow, GroupChip, SessionRow, CardRowList.
 *
 * Every competition / challenge / external / series / event card on every
 * surface is drawn from these five, fed by a model built in
 * `src/competition/cardModel.js`. They are DUMB: they read props, they never
 * decide what to show. The one decision they hold is the layout rule the C
 * mockup fixed — one box, no nested frames, a section is a LABEL COLUMN and a
 * hairline, and what is yours gets a left rail rather than a tinted surface.
 *
 * Before this, one live competition card carried four frames inside its own
 * (violet entry, grey structure, grey friends, grey admin), each at
 * `borderRadius: 14`, with nothing to say which one mattered; and the same
 * category pill was hand-copied on three surfaces. See
 * `docs/spec/05-competitions-core.md` → "List view".
 * ========================================================================= */

/** The rail colour per card kind — the type is carried by colour AND by the
 *  kicker text, so the type badge could leave the header. */
export const CARD_RAIL = {
  competition: null,          // null = the gym's accent
  series: "#7c3aed",
  event: "#059669",
  challenge: "#d97706",       // AMBER.accent
  external: "#2563eb",
  sprayWall: "#475569",       // slate; the accent stays the competition's
};

/** Label column width. The Hungarian labels ("Kategóriák", "Jelentkezés")
 *  just fit at 84px on a 375px phone; narrower screens wrap the value under
 *  the label via `minmax(0, 1fr)`. */
/**
 * The grid a LIST of `FactRow`s lives in — spread it on the container, never
 * on the row.
 *
 * ONE label column for the whole list, and it is `max-content`, not a fixed
 * 84px: a Hungarian label is longer than an English one, and at 84px
 * "REGISZTRÁCIÓS DÍJ" wrapped to two lines, which — with `baseline` alignment —
 * put the amount beside the FIRST line and read as one run-on string
 * ("REGISZTRÁCIÓS 5998 HUF"). Sizing the track to the longest label in the
 * list fixes it without shortening any wording, and because the whole list is
 * one grid, every row still lines up with every other row on the same card.
 * 84px stays the FLOOR, so a card of short labels ("Mikor", "Te") keeps the
 * airy column the design asks for.
 */
export const FACT_LIST_GRID = {
  display: "grid",
  gridTemplateColumns: "minmax(84px, max-content) minmax(0, 1fr)",
  columnGap: 12,
  alignItems: "baseline",
};

/**
 * FactRow — one "label · value" definition row, rendered as the TWO CELLS of
 * a `FACT_LIST_GRID` container (not as its own grid — that is what let the
 * columns drift apart row by row). `k` is the uppercase label, `v` the value,
 * `sub` a muted second line under it. `tone` colours the value: "accent" |
 * "danger" | "warn" | "paid" | "partial" | "unpaid" | "muted". `children` replaces `v`
 * for a composite value (chips, session rows, the viewer's own entry).
 *
 * The public /register landing drew this row first (as a right-aligned pair);
 * the card design generalises it, so the landing now imports it from here.
 */
export function FactRow({ k, v, sub, tone, children, divider = true }) {
  const { th, accent } = useAppTheme();
  const reg = getRegPalette(th);
  const tones = {
    accent, danger: occupancyToneColor(th, "full"), warn: AMBER.accent,
    paid: reg.feePaid, partial: reg.feePartial, unpaid: reg.feeUnpaid,
    muted: th.textMuted,
  };
  const color = (tone && tones[tone]) || th.text;
  const cell = { padding: "10px 0" };
  return (
    <>
      {/* The hairline ABOVE the row, drawn by an element that SPANS both
          columns — never by the two cells' own borders. A per-cell border
          stops at the column gutter, so the line broke in two right between
          the label and the value, which is what it looked like: a stripe, a
          gap, a stripe. `1 / -1` makes it one line across the whole card. */}
      {divider && <div style={{ gridColumn: "1 / -1", borderTop: "1px solid " + th.borderLight }} />}
      <div style={{ ...cell, fontSize: 12, fontWeight: 800, color: th.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>{k}</div>
      <div style={{ ...cell, fontSize: 13, minWidth: 0, color: th.text }}>
        {children ?? <span style={{ color, fontWeight: tone && tone !== "muted" ? 700 : 500, ...SELECTABLE }}>{v}</span>}
        {sub && <div style={{ fontSize: 12, color: th.textMuted, marginTop: 2, ...SELECTABLE }}>{sub}</div>}
      </div>
    </>
  );
}

/**
 * GroupChip — a competition category. Collapsed it is the pill every surface
 * used to hand-draw (colour dot · name · age band · fee); expandable ones open
 * into the same pill plus the label-column facts (age, attempts, mode) and the
 * organizer's `description` under them. `model` comes from `groupChipModel`.
 *
 * `onExpand` is OPTIONAL and fires on the first open only — the public
 * landing passes nothing, because public surfaces run no analytics.
 */
export function GroupChip({ model, onExpand, surface }) {
  const { th } = useAppTheme();
  const [open, setOpen] = useState(false);
  if (!model) return null;
  const bg = surface || th.bg;
  const pill = {
    display: "inline-flex", alignItems: "center", gap: 6, maxWidth: "100%",
    fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 999,
    background: bg, border: "1px solid " + th.border, color: th.text, fontFamily: F,
    cursor: model.expandable ? "pointer" : "default", textAlign: "left",
  };
  const content = (
    <>
      {model.color && <ColorDot color={model.color} size={8} surface={bg} />}
      <span style={{ ...SELECTABLE, minWidth: 0 }}>
        {model.name}{model.ageText ? ` ${model.ageText}` : ""}
        {model.feeText && <span style={{ color: th.textMuted, fontWeight: 500 }}> · {model.feeText}</span>}
      </span>
      {model.expandable && (
        <span style={{ color: th.textMuted, display: "inline-flex", marginLeft: 2 }}>
          {open ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
        </span>
      )}
    </>
  );
  if (!model.expandable) return <span style={pill}>{content}</span>;
  const toggle = () => setOpen((o) => { if (!o) onExpand?.(); return !o; });
  if (!open) {
    return (
      <button type="button" aria-expanded={false} aria-label={model.expandLabel} onClick={toggle} style={pill}>
        {content}
      </button>
    );
  }
  return (
    <div style={{ width: "100%", border: "1px solid " + th.border, borderRadius: 12, padding: 10, background: bg }}>
      <button type="button" aria-expanded aria-label={model.expandLabel} onClick={toggle}
        style={{ ...pill, border: "none", padding: 0, background: "transparent" }}>
        {content}
      </button>
      <div style={{ marginTop: 8, borderTop: "1px solid " + th.borderLight }}>
        {model.details.map((d, i) => (
          <div key={d.key} style={{ display: "grid", gridTemplateColumns: "84px minmax(0, 1fr)", gap: 10, padding: "6px 0", fontSize: 12 }}>
            <span style={{ fontWeight: 800, color: th.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>{d.k}</span>
            <span style={{ color: th.text, ...SELECTABLE }}>{d.v}</span>
          </div>
        ))}
        {model.description && (
          <div style={{ fontSize: 13, color: th.text, lineHeight: 1.55, whiteSpace: "pre-wrap", marginTop: 4, ...SELECTABLE }}>
            {model.description}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * SessionRow — one run: name · start · capacity on the left, the occupancy
 * word on the right. `occupancy` is the `getSessionOccupancy` result (or
 * null); this component never decides whether the viewer may see counts —
 * the CALLER resolved `showCounts` before building the item, and the two
 * surfaces that print this row deliberately resolve it differently
 * (`seesRegistrationCounts` on the card, `showsRegistrationCounts` on the
 * public landing). `cardReadScope.test.js` scans for that.
 */
export function SessionRow({ name, when, max, occupancy, last = false }) {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  return (
    <div style={{
      display: "flex", alignItems: "baseline", gap: 8, fontSize: 13, color: th.text,
      padding: "5px 0", borderBottom: last ? "none" : "1px solid " + th.borderLight,
    }}>
      <div style={{ flex: 1, minWidth: 0, ...SELECTABLE }}>
        <b>{name}</b>
        {when && <span style={{ color: th.textMuted, fontSize: 12 }}>{" · "}{when}</span>}
        <SessionCapacity max={max} style={{ fontSize: 12 }} />
      </div>
      {occupancy && (
        <span style={{ whiteSpace: "nowrap", fontWeight: 700, fontSize: 12, color: occupancyToneColor(th, occupancy.tone) }}>
          {t(occupancy.key, occupancy.params)}
        </span>
      )}
    </div>
  );
}

/**
 * CardRowList — draws a model's `rows[]`. Text rows are FactRows; "groups"
 * rows a wrap of GroupChips; "sessions" rows a SessionRow list; "friends"
 * rows the group · session → names grid; a "ladder" row the fee's `FeeLadder`
 * (compact); a "slot" row takes its value from
 * `slots[key]` (the caller's JSX — the viewer's own entry with its menus).
 * A slot with nothing to render is skipped, so a model may list it
 * unconditionally.
 */
export function CardRowList({ rows = [], slots = {}, onExpandGroup, style }) {
  const { th } = useAppTheme();
  const visible = rows.filter((r) => r.kind !== "slot" || slots[r.key]);
  if (!visible.length) return null;
  return (
    <div style={{ marginTop: 12, ...FACT_LIST_GRID, ...style }}>
      {visible.map((r) => {
        if (r.kind === "text") return <FactRow key={r.key} k={r.label} v={r.value} sub={r.sub} tone={r.tone} />;
        if (r.kind === "slot") return <FactRow key={r.key} k={r.label}>{slots[r.key]}</FactRow>;
        // A fee with a live early-bird tier: today's price, the full price struck
        // through, the next cutoff — the compact form of the one price component.
        if (r.kind === "ladder") return <FactRow key={r.key} k={r.label}><FeeLadder texts={r.texts} variant="compact" ariaLabel={r.ariaLabel} /></FactRow>;
        if (r.kind === "groups") {
          return (
            <FactRow key={r.key} k={r.label}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {r.items.map((g) => <GroupChip key={g.id} model={g} onExpand={onExpandGroup} surface={th.card} />)}
              </div>
            </FactRow>
          );
        }
        if (r.kind === "sessions") {
          return (
            <FactRow key={r.key} k={r.label}>
              {r.items.map((s, j) => <SessionRow key={s.id} {...s} last={j === r.items.length - 1} />)}
            </FactRow>
          );
        }
        if (r.kind === "friends") {
          return (
            <FactRow key={r.key} k={r.label}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, max-content) 1fr", columnGap: 12, rowGap: 5, fontSize: 13 }}>
                {r.items.map((row, j) => (
                  <React.Fragment key={j}>
                    <div style={{ fontWeight: 700, color: th.text, ...SELECTABLE }}>
                      {row.groupName}
                      {row.sessionName && <span style={{ color: th.textMuted, fontWeight: 600 }}>{" · "}{row.sessionName}</span>}
                    </div>
                    <div style={{ color: th.text, ...SELECTABLE }}>{row.names}</div>
                  </React.Fragment>
                ))}
              </div>
            </FactRow>
          );
        }
        return null;
      })}
    </div>
  );
}

/* ==========================================================================
 * Rendező elérhetősége — who to call about a competition / series / event.
 *
 * ⚠️ NOT the competition's `organizer` ROLE, and not the event's
 * `organizers[]`: those grant gym members write access. This is contact
 * information about a person who need not have a Sendly account at all — the
 * reason the Hungarian label is "Rendező elérhetősége", never the bare
 * "Szervező" the staff role already owns.
 *
 * Both halves are here because all three entities show and edit the same three
 * fields, on eight display surfaces and three editors.
 * ========================================================================= */

/**
 * OrganizerContactBlock — the read side. `contact` is ALWAYS the output of
 * `resolveOrganizerContact(doc, gym, { fallback })` from `@sendly/shared`,
 * never the raw `organizerContact` field: the gym fallback chain lives in one
 * place, and a surface reading the field directly would quietly lose it.
 *
 * Returns null for a null contact, so no call site needs an `&&` guard — same
 * contract as `CollapsibleText` and `CoverImage`. A field the contact does not
 * carry gets no row at all.
 *
 * `labels` keeps this file i18n-free: `{ title, name, email, phone, gymFallback }`.
 * The `gymFallback` line is drawn only when the block is showing the GYM's
 * contact rather than the organiser's own — without it the visitor would read
 * the gym's phone number as the organiser's personal one.
 */
export function OrganizerContactBlock({ contact, labels = {}, style }) {
  const { th, accent } = useAppTheme();
  if (!contact) return null;
  const link = { color: accent, fontWeight: 700, textDecoration: "none", ...SELECTABLE };
  return (
    <div style={{ marginTop: 14, ...style }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: th.text, marginBottom: contact.source === "gym" ? 4 : 8 }}>
        {labels.title}
      </div>
      {contact.source === "gym" && (
        <div style={{ fontSize: 12, color: th.textMuted, marginBottom: 8 }}>{labels.gymFallback}</div>
      )}
      <div style={FACT_LIST_GRID}>
        {contact.name && <FactRow k={labels.name} v={contact.name} />}
        {contact.email && (
          <FactRow k={labels.email}>
            <a href={`mailto:${contact.email}`} style={link}>{contact.email}</a>
          </FactRow>
        )}
        {contact.phone && (
          <FactRow k={labels.phone}>
            <a href={`tel:${contact.phone}`} style={link}>{contact.phone}</a>
          </FactRow>
        )}
      </div>
    </div>
  );
}

/**
 * OrganizerContactFields — the write side, one shape for all three editors.
 *
 * `value` is `{ name, email, phone }` (partial is fine), `onChange` gets the
 * whole next object. Normalising to `null` happens at SAVE time
 * (`buildOrganizerContact`), not while typing — otherwise clearing the last
 * character would drop the other two fields out from under the form.
 *
 * `required` (competition, series) adds the `*` and turns on the empty-state
 * preview. It does NOT block saving: the gym fallback means a visitor never
 * sees an empty contact, so a blocking gate would only bite when someone edits
 * a competition that has been running for months.
 *
 * `gymFallback` is `resolveOrganizerContact({}, gym, { fallback: true })` —
 * what WOULD be shown if this stays empty, or `null` when the gym itself has
 * no e-mail and no phone. That second case is the one worth shouting about
 * (`WarnNote`): the competition would go public with no way to reach anyone.
 *
 * `labels`: `{ title, name, email, phone, publicHint, fallbackPreview,
 * noGymContact }`. All three editors pass `publicHint`: the competition, the
 * series and — since the public sign-up landing — a published event are all
 * readable without sign-in.
 */
export function OrganizerContactFields({ value, onChange, labels = {}, required = false, gymFallback = null, disabled = false, style }) {
  const { th } = useAppTheme();
  const v = value || {};
  const set = (field) => (e) => onChange?.({ name: v.name || "", email: v.email || "", phone: v.phone || "", [field]: e.target.value });
  const isEmpty = !(v.name || "").trim() && !(v.email || "").trim() && !(v.phone || "").trim();
  const labelStyle = { fontSize: 12, fontWeight: 600, color: th.textMuted, marginBottom: 4 };
  const hint = { fontSize: 12, color: th.textMuted, marginTop: 6 };
  return (
    <div style={style}>
      <div style={{ fontSize: 14, fontWeight: 800, color: th.text, marginBottom: 8 }}>
        {labels.title}{required ? " *" : ""}
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={labelStyle}>{labels.name}</div>
        <TextInput value={v.name || ""} onChange={set("name")} maxLength={ORGANIZER_CONTACT_LIMITS.name} disabled={disabled} />
      </div>
      <div style={{ marginBottom: 8 }}>
        <div style={labelStyle}>{labels.email}</div>
        <TextInput type="email" value={v.email || ""} onChange={set("email")} maxLength={ORGANIZER_CONTACT_LIMITS.email} disabled={disabled} />
      </div>
      <div>
        <div style={labelStyle}>{labels.phone}</div>
        <TextInput type="tel" value={v.phone || ""} onChange={set("phone")} maxLength={ORGANIZER_CONTACT_LIMITS.phone} disabled={disabled} />
      </div>
      {labels.publicHint && <div style={hint}>{labels.publicHint}</div>}
      {required && isEmpty && (gymFallback
        ? (
          <div style={hint}>
            {labels.fallbackPreview}
            <div style={{ color: th.text, marginTop: 2, ...SELECTABLE }}>
              {[gymFallback.name, gymFallback.email, gymFallback.phone].filter(Boolean).join(" · ")}
            </div>
          </div>
        )
        : <WarnNote style={{ marginTop: 8 }}>{labels.noGymContact}</WarnNote>
      )}
    </div>
  );
}

/**
 * CardShell — the one card frame. Header (kicker · title · meta · status ·
 * chevron) is always drawn; the body — cover, intro, children, the action
 * bar, the organizer bar — only when the card is open or not collapsible.
 *
 * `open` / `onToggle` are controlled: the list owns the state, because D2
 * derives the default from data (your entry, the nearest date, a deep link)
 * that arrives after mount. The public landing passes `collapsible={false}`
 * and never toggles anything.
 *
 * No height animation on purpose: `max-height` on unknown content reflows
 * on every frame. The switch is instant.
 */
export function CardShell({
  id, kind = "competition", kicker, title, meta = [], status, dashed = false,
  collapsible = false, open = true, onToggle, toggleLabel,
  cover, intro, children, actions, admin, adminLabel,
  muted = false, highlight = false, style,
}) {
  const { th, accent } = useAppTheme();
  // `DEFAULT_ACCENT` is not decoration here: an undefined accent would render
  // `borderLeft: "4px solid undefined"`, which the browser drops WHOLE — the
  // card silently loses its coloured rail. `GreenBtn` carries the same
  // fallback for the same reason (every button once went browser-grey).
  const rail = CARD_RAIL[kind] || accent || DEFAULT_ACCENT;
  const showBody = !collapsible || open;
  const chevron = open ? IconChevronUp : IconChevronDown;
  return (
    // An `article` named by its title: one competition / event / series card is
    // one unit a screen reader (and a test) can find among its neighbours.
    <Card id={id} role="article" ariaLabel={typeof title === "string" ? title : undefined} style={{
      padding: 14, marginBottom: 12,
      // Three longhands, NOT the `borderLeft` shorthand. A shorthand is
      // all-or-nothing: one `undefined` in the template string ("4px solid
      // undefined") makes the browser drop the whole declaration, and the card
      // silently falls back to the 1px neutral border on every side — a rail
      // that vanishes for a reason nobody can see in the diff. Split up, a bad
      // colour costs the colour, never the 4px.
      borderLeftWidth: 4,
      // A draft keeps its kind's colour and only loses the solid line: the
      // dashes already say "not live yet", and a grey rail took away the one
      // thing that tells a draft series from a draft event at a glance.
      borderLeftStyle: dashed ? "dashed" : "solid",
      borderLeftColor: rail,
      opacity: muted ? 0.8 : 1,
      boxShadow: highlight ? `0 0 0 4px ${accent}44` : undefined,
      transition: "box-shadow 0.3s",
      ...style,
    }}>
      <div
        onClick={collapsible ? onToggle : undefined}
        style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: collapsible ? "pointer" : "default" }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          {kicker && (
            <div style={{ fontSize: 12, fontWeight: 800, color: dashed ? th.textMuted : rail, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
              {kicker}
            </div>
          )}
          <div style={{ fontSize: 16, fontWeight: 800, color: th.text, lineHeight: 1.25, ...SELECTABLE }}>{title}</div>
          {meta.length > 0 && (
            <div style={{ fontSize: 12, color: th.textMuted, marginTop: 4, ...SELECTABLE }}>
              {/* An all-string meta stays one text node; a node item (an accent
                  "N new this week") is joined with the same separator as nodes. */}
              {meta.every((m) => typeof m === "string")
                ? meta.join(" · ")
                : meta.map((m, i) => (
                  <React.Fragment key={i}>{i > 0 ? " · " : null}{m}</React.Fragment>
                ))}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {status && <StatusBadge status={status.status} label={status.label} />}
          {collapsible && (
            <IconBtn icon={chevron} tone="muted" size={18} padding={4} title={toggleLabel}
              onClick={(e) => { e.stopPropagation(); onToggle?.(); }} />
          )}
        </div>
      </div>

      {showBody && (
        <>
          {cover}
          {intro}
          {children}
          {actions && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 14, paddingTop: 12, borderTop: "1px solid " + th.border }}>
              {actions}
            </div>
          )}
          {/* The organizer bar gets its own rule above it, the same one the
              action bar has: the card's foot is two DIFFERENT audiences — what
              anyone may do, then what the organizer may do — and without a
              line the second row read as an overflow of the first. */}
          {admin && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 12, paddingTop: 12, borderTop: "1px solid " + th.border }}>
              {adminLabel && (
                <span style={{ fontSize: 12, fontWeight: 800, color: th.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginRight: "auto", whiteSpace: "nowrap" }}>
                  {adminLabel}
                </span>
              )}
              {admin}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

/** The quiet outlined button of a card's action bar. `tone` = "default" |
 *  "accent" | "violet" (the entry palette) | "go" (activate). */
export function CardGhostBtn({ onClick, children, tone = "default", disabled, title, style }) {
  const { th, accent } = useAppTheme();
  const reg = getRegPalette(th);
  const tones = {
    default: { color: th.text, border: th.border },
    // Same fallback as CardShell's rail: an undefined accent would leave the
    // label with no colour at all.
    accent: { color: accent || DEFAULT_ACCENT, border: th.border },
    violet: { color: reg.link, border: reg.outline },
    go: { color: "#3B6D11", border: "#97C459" },
  };
  const c = tones[tone] || tones.default;
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} title={title} style={{
      padding: BTN_PAD.md, borderRadius: 10, border: "1px solid " + c.border,
      background: "transparent", color: c.color, fontSize: 13, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer", fontFamily: F, opacity: disabled ? 0.4 : 1,
      display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
      ...style,
    }}>{children}</button>
  );
}

/** The violet filled button of the entry flow (Belépés / Nevezés) — the one
 *  non-accent primary, in the entry palette so it reads as "yours". */
export function CardEntryBtn({ onClick, children, disabled, style }) {
  const { th } = useAppTheme();
  const reg = getRegPalette(th);
  return (
    <button type="button" onClick={disabled ? undefined : onClick} disabled={disabled} style={{
      padding: BTN_PAD.md, borderRadius: 10, border: "none",
      background: reg.btnBg, color: reg.btnFg, fontSize: 13, fontWeight: 700,
      cursor: disabled ? "default" : "pointer", fontFamily: F, opacity: disabled ? 0.5 : 1,
      whiteSpace: "nowrap", ...style,
    }}>{children}</button>
  );
}

/**
 * The "© {year} Sendly" footer every public page ends with — the spectator
 * dashboard, the series dashboard and both sign-up landings. It was four
 * hand-copied blocks; one component keeps them the same product.
 *
 * The mark takes the THEME accent: a page that needs its own (the series
 * dashboard's violet) pins it with <ThemeProvider accent=…>, like every other
 * leaf reads its palette.
 */
export function PublicPageFooter() {
  const { th, accent } = useAppTheme();
  return (
    <div style={{ textAlign: "center", padding: "24px 0 16px", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
      <svg width="16" height="16" viewBox="0 0 128 128" fill="none" aria-hidden="true">
        <rect width="128" height="128" rx="28" fill={accent} />
        <path d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="white" strokeWidth="9" strokeLinecap="round" fill="none" />
      </svg>
      <span style={{ fontSize: 12, color: th.textMuted, letterSpacing: 1 }}>
        © {new Date().getFullYear()}{" "}
        <a href="https://sendly.hu" target="_blank" rel="noopener noreferrer" style={{ color: th.textMuted, textDecoration: "none" }}>Sendly</a>
      </span>
    </div>
  );
}

/**
 * The "not found" page of a URL that leads nowhere: an unknown route, a
 * competition or series that does not exist (or is not announced), an event
 * that is not published. The message is the page's one heading, so a screen
 * reader opens on it; one link leads on — to the gym, or to the gym list.
 *
 * One component for the five places that used to draw it by hand (the two
 * sign-up landings, the two public dashboards — which showed a bare,
 * untranslated „404" — and the catch-all route).
 */
export function PublicNotFound({ title, body, linkTo, linkLabel }) {
  const { th, accent } = useAppTheme();
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      gap: 12, fontFamily: F, background: th.bg, padding: 24, textAlign: "center",
    }}>
      <div aria-hidden="true" style={{ fontSize: 48 }}>🏔️</div>
      <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: th.text }}>{title}</h1>
      {body && <p style={{ margin: 0, fontSize: 14, color: th.textMuted, maxWidth: 360, lineHeight: 1.5 }}>{body}</p>}
      <Link to={linkTo} style={{ color: accent, fontWeight: 700, fontSize: 14 }}>{linkLabel}</Link>
    </div>
  );
}

/**
 * A button that keeps the look of the text it stands in — a name in a table
 * row, a card's title — for a surface whose row or card opens a detail view on
 * a click. The row's (card's) own `onClick` serves the mouse; this is the same
 * action for the keyboard and a screen reader, without giving a `<tr>` or a
 * card a button role around other controls. Its name is its text.
 */
export function InlineTextButton({ onClick, title, children, style }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      style={{
        background: "none", border: "none", padding: 0, margin: 0,
        font: "inherit", color: "inherit", textAlign: "inherit", cursor: "pointer",
        ...style,
      }}
    >
      {children}
    </button>
  );
}

/**
 * The green "you are already in" box of the public sign-up landings, in both
 * themes.
 *
 * The light values are the pastel the competition landing has always used.
 * Dark mode cannot reuse them — `#dcfce7` against `#111` is a glowing
 * white-green slab with near-invisible `#15803d` text on it — so it keeps the
 * hue and inverts the relationship the same way `getRegPalette` does for the
 * card: a translucent wash over whatever is behind it, with a light-green
 * foreground (9.3:1 on the blended surface, so the 12px detail line is
 * readable, not just visible).
 */
export function landingEntryPalette(isDark) {
  if (!isDark) return { bg: "#dcfce7", border: "#16a34a", fg: "#15803d" };
  return { bg: "rgba(22,163,74,0.18)", border: "rgba(34,197,94,0.38)", fg: "#86efac" };
}
